import React, { useEffect, useState, useMemo, useImperativeHandle, forwardRef } from "react";
import Button3D from "./Button3D";
import apiClient from '../services/apiClient';
import { IMaskInput } from 'react-imask';
import { FaInfoCircle, FaExclamationTriangle, FaPause, FaPlay, FaTrash, FaArrowRight } from "react-icons/fa";
import styles from "./oauthStyles";
import '../styles/oauth.css';
import dataService from '../services/dataService';
import { useAuth } from '../context/AuthContext';
import { ACCOUNT_TYPE_SEATS, PHONE_ADDON, FEATURE_FLAGS, isConnectCompanyCalendarEnabled } from '../constants';
import Seats from './Seats';
import TeamNotificationTooltip from '../components/TeamNotificationTooltip';

function MemberAvatar({ name, lastName, email, size = 36 }) {
  const initials = name
    ? `${name.charAt(0)}${lastName ? lastName.charAt(0) : ''}`.toUpperCase()
    : (email || '?').charAt(0).toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'linear-gradient(135deg, #1565c0, #42a5f5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: size * 0.38,
      flexShrink: 0, userSelect: 'none',
    }}>
      {initials}
    </div>
  );
}

const MEMBER_TASK_FIELD_LABELS = {
  title: 'Title', due: 'Due Date', action_items: 'Action Items',
  status: 'Status', description: 'Description', notes: 'Notes',
  deleted: 'Removed from List', active: 'Active',
};

function formatMemberTaskFieldValue(val) {
  if (val === null || val === undefined) return <em style={{ color: '#9ca3af', fontStyle: 'italic' }}>empty</em>;
  if (Array.isArray(val)) {
    if (val.length === 0) return <em style={{ color: '#9ca3af', fontStyle: 'italic' }}>empty</em>;
    return (
      <ul style={{ margin: 0, paddingLeft: 14 }}>
        {val.map((item, i) => <li key={i}>{String(item)}</li>)}
      </ul>
    );
  }
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  return String(val);
}

const Auth = forwardRef((props, ref) => {
  const { user, updateUserProfile } = useAuth();
  // The Company/Brokerage Calendar always belongs to the Account Owner's workspace,
  // regardless of who is editing the team. The backend attaches owner_email to every
  // non-owner user — a delegated Admin AND a Lead Agent (role 'member') alike (see
  // auth.py) — so resolving through owner_email covers both. Only an actual Account
  // Owner (no owner_email) falls back to their own email. The acting user's own
  // calendar must never be substituted here.
  const companyCalendarOwnerEmail = user?.owner_email || user?.email;
  // True whenever the acting user is editing on behalf of someone else's workspace
  // (a delegated Admin or a Lead Agent), i.e. the Company/Brokerage Calendar targets
  // the Account Owner's calendar rather than the acting user's own.
  const isAdminDelegate = !!user?.owner_email;
  // True when a Lead Agent with Team Management permission is editing a team where they are the Lead Agent.
  // In this state the Lead Agent slot (idx===0) must be read-only — only the Account Owner can reassign it.
  const isLeadAgentManager = !!(user?.can_manage_teams && user?.role === 'member');
  const isOwnTeamAsLeadAgent =
    isLeadAgentManager &&
    props.isEditMode &&
    (props.members?.[0]?.email || '').toLowerCase() === (user?.email || '').toLowerCase();
  const [currentSeats, setCurrentSeats] = useState(props.seatLimit || null);
  const [availability, setAvailability] = useState({ loading: true, seatsTotal: null, seatsUsed: null, seatsAvailable: null, phoneTotal: null, phoneUsed: null, phoneAvailable: null });
  
  // Add basic component loading log
  console.log('[DEBUG] Auth component loaded/re-rendered with props:', {
    isEditMode: props.isEditMode,
    hasCurrentEditItem: !!props.currentEditItem,
    membersCount: props.members?.length,
    seatLimit: props.seatLimit
  });
  
  // Phone-related states - only if phone feature is enabled
  const [availablePhoneSlots, setAvailablePhoneSlots] = useState([]);
  const [totalPhoneSlots, setTotalPhoneSlots] = useState(0);
  const [registeredPhoneNumbers, setRegisteredPhoneNumbers] = useState({});
  const [phoneRegistrationRequests, setPhoneRegistrationRequests] = useState({});
  const [tooltipVisible, setTooltipVisible] = useState({});
  const [localPhoneInputs, setLocalPhoneInputs] = useState({});
  const [phoneValidation, setPhoneValidation] = useState({});
  
  // Track original member data for change detection in edit mode
  const [originalMembersData, setOriginalMembersData] = useState([]);
  // True only after the snapshot is committed — gates the button on first render
  const [isOriginalDataReady, setIsOriginalDataReady] = useState(false);
  // Validation state for email inputs
  const [emailValidation, setEmailValidation] = useState({});
  // State to track if Company/Team Calendar Type checkbox is checked
  const [showCalendarTypeSelection, setShowCalendarTypeSelection] = useState(false);
  
  // Debug: Monitor showCalendarTypeSelection changes
  useEffect(() => {
    console.log('[DEBUG] showCalendarTypeSelection changed to:', showCalendarTypeSelection);
  }, [showCalendarTypeSelection]);
  // Calendar authorization state
  const [authCompleted, setAuthCompleted] = useState(false);
  const [authText, setAuthText] = useState('');
  
  // Company/Team Calendar connection state
  const [, setCompanyCalendarConnection] = useState(null);

  // User's personal calendar connection status (for disabling company calendar checkbox)
  const [userCalendarStatus, setUserCalendarStatus] = useState(null); // null | 'loading' | 'connected' | 'unconnected'
  const [userCalendarProvider, setUserCalendarProvider] = useState(null);
  useEffect(() => {
    if (!companyCalendarOwnerEmail) return;
    setUserCalendarStatus('loading');
    // Company/Brokerage Calendar status is always checked against the Account Owner's
    // calendar connection (admin_for_owner), never the acting Admin's own calendar.
    apiClient.get(`/api/calendar/auth/status?target_email=${encodeURIComponent(companyCalendarOwnerEmail)}`)
      .then(res => {
        if (res.data.connected) {
          setUserCalendarStatus('connected');
          setUserCalendarProvider(res.data.provider);
        } else {
          setUserCalendarStatus('unconnected');
        }
      })
      .catch(() => setUserCalendarStatus('unconnected'));
  }, [companyCalendarOwnerEmail]);
  
  // Data refresh loading state
  const [, setIsDataRefreshing] = useState(false);
  

  
  // Seats modal state
  const [showSeatsModal, setShowSeatsModal] = useState(false);
  // Track which member cards have their Permissions section expanded (by member index)
  const [expandedPermissions, setExpandedPermissions] = useState(new Set());
  const [confirmRemoveMember, setConfirmRemoveMember] = useState(null); // { member, idx, memberName }
  const [removeMemberStep, setRemoveMemberStep] = useState('checking'); // checking|ready|need_transfer|blocked|executing
  const [removeMemberCheckResult, setRemoveMemberCheckResult] = useState(null);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  const [removeMemberError, setRemoveMemberError] = useState(null);
  const [transferMemberModalOpen, setTransferMemberModalOpen] = useState(false);
  const [transferMemberStep, setTransferMemberStep] = useState('select'); // select|checking|confirm|conflict-warning|conflict-review|final-confirm
  const [transferMemberTargets, setTransferMemberTargets] = useState([]);
  const [transferMemberTargetsLoading, setTransferMemberTargetsLoading] = useState(false);
  const [transferMemberTargetsError, setTransferMemberTargetsError] = useState(null);
  const [selectedMemberTransfer, setSelectedMemberTransfer] = useState(null); // { targetUser, connectionId, teamName }
  const [transferMemberConflicts, setTransferMemberConflicts] = useState([]);
  const [transferMemberConflictResolutions, setTransferMemberConflictResolutions] = useState({});
  const [transferMemberTaskResolutions, setTransferMemberTaskResolutions] = useState({});
  const [isTransferringMember, setIsTransferringMember] = useState(false);
  const [transferMemberError, setTransferMemberError] = useState(null);
  const [transferMemberDirectMode, setTransferMemberDirectMode] = useState(false);
  const [confirmSuspendMember, setConfirmSuspendMember] = useState(null); // { member, idx, memberName }
  const [suspendMemberStep, setSuspendMemberStep] = useState('checking'); // checking|ready|need_transfer|blocked|executing
  const [suspendMemberCheckResult, setSuspendMemberCheckResult] = useState(null);
  const [isSuspendingMember, setIsSuspendingMember] = useState(false);
  const [suspendMemberError, setSuspendMemberError] = useState(null);
  const [suspendMemberAfterTransfer, setSuspendMemberAfterTransfer] = useState(false);
  const [confirmReactivateMember, setConfirmReactivateMember] = useState(null); // { member, idx, memberName }
  const [isReactivatingMember, setIsReactivatingMember] = useState(false);
  const [reactivateMemberError, setReactivateMemberError] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [teamNameError, setTeamNameError] = useState('');

  // Change Lead Agent wizard state
  const [claModalOpen, setClaModalOpen] = useState(false);
  const [claStep, setClaStep] = useState('choose'); // choose|old_action|preview|executing
  const [claMode, setClaMode] = useState(null); // 'existing'|'new'
  const [claSelectedMember, setClaSelectedMember] = useState(null);
  const [claNewUser, setClaNewUser] = useState({ firstName: '', lastName: '', email: '' });
  const [claNewUserErrors, setClaNewUserErrors] = useState({});
  const [claOldAction, setClaOldAction] = useState('keep'); // 'keep'|'remove'
  const [claPreview, setClaPreview] = useState(null);
  const [claPreviewLoading, setClaPreviewLoading] = useState(false);
  const [claError, setClaError] = useState(null);
  const [claExecuting, setClaExecuting] = useState(false);
  const [claTransferRequired, setClaTransferRequired] = useState(false);
  const [claTransferDone, setClaTransferDone] = useState(false);
  const [claTransferPendingReturn, setClaTransferPendingReturn] = useState(false);
  const togglePermissions = (idx) => setExpandedPermissions(prev => {
    const next = new Set(prev); if (next.has(idx)) next.delete(idx); else next.add(idx); return next;
  });
  
  // Load existing company calendar connection on component mount
  useEffect(() => {
    const loadCompanyCalendarConnection = async () => {
      try {
        if (companyCalendarOwnerEmail && props.accountType) {
          const response = await apiClient.get(`/api/company-calendar/${props.accountType}?userEmail=${encodeURIComponent(companyCalendarOwnerEmail)}`);
          if (response.data.success && response.data.connection) {
            setCompanyCalendarConnection(response.data.connection);
            setShowCalendarTypeSelection(true);
            setAuthCompleted(response.data.connection.connected);
            
            // Set the provider in the first member for UI consistency
            if (props.members.length > 0) {
              const updatedMembers = [...props.members];
              updatedMembers[0].provider = response.data.connection.provider;
              props.setMembers(updatedMembers);
            }
          }
        }
      } catch (error) {
        console.log('[DEBUG] No existing company calendar connection found or error loading:', error.message);
      }
    };

    loadCompanyCalendarConnection();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCalendarOwnerEmail, props.accountType]);

  // Also watch for props changes that might indicate external loading
  useEffect(() => {
    // If there's a prop that indicates the parent is loading, we can show our indicator
    if (props.isLoading || props.dataLoading || props.loading) {
      setIsDataRefreshing(true);
    } else {
      setIsDataRefreshing(false);
    }
  }, [props.isLoading, props.dataLoading, props.loading]);
  
  // Initialize local phone inputs with existing phone numbers when members change - only if phone feature is enabled
  useEffect(() => {
    if (!FEATURE_FLAGS.PHONE_FEATURE_ENABLED) return;
    
    console.log('[DEBUG] Initializing localPhoneInputs with members:', props.members);
    const phoneInputs = {};
    const phoneRequests = {};
    const phoneNumbers = {};
    
    props.members.forEach((member, index) => {
      console.log(`[DEBUG] Processing member ${index}:`, member);
      if (member.email && member.phone) {
        console.log(`[DEBUG] Setting phone for ${member.email}: ${member.phone}`);
        phoneInputs[member.email] = member.phone;
        // Also mark phone registration as requested since they already have a phone
        phoneRequests[member.email] = true;
        // Also set the registered phone number for display
        phoneNumbers[member.email] = member.phone;
      } else {
        console.log(`[DEBUG] Member ${index} missing email or phone:`, { 
          email: member.email, 
          phone: member.phone,
          allKeys: Object.keys(member)
        });
      }
    });
    
    console.log('[DEBUG] Final phoneInputs object:', phoneInputs);
    console.log('[DEBUG] Final phoneRequests object:', phoneRequests);
    console.log('[DEBUG] Final phoneNumbers object:', phoneNumbers);
    setLocalPhoneInputs(phoneInputs);
    setPhoneRegistrationRequests(prev => ({ ...prev, ...phoneRequests }));
    setRegisteredPhoneNumbers(prev => ({ ...prev, ...phoneNumbers }));
  }, [props.members]); // Re-run when members prop changes
  
  // Load user's current seat count, phone slots, and account-level availability
  useEffect(() => {
    if (user && props.accountType) {
      loadUserData();
      loadAvailability();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, props.accountType]);

  // Update currentSeats when props.seatLimit changes (e.g., after billing update)
  useEffect(() => {
    if (props.seatLimit) {
      console.log('[DEBUG] props.seatLimit changed, updating currentSeats to:', props.seatLimit);
      setCurrentSeats(props.seatLimit);
    }
  }, [props.seatLimit]);

  // Listen for seat updates from Seats modal
  useEffect(() => {
    const handleSeatsUpdate = (event) => {
      console.log('[DEBUG] ========== SEATS UPDATE EVENT ==========');
      console.log('[DEBUG] seatsUpdated event received:', event.detail);
      console.log('[DEBUG] Current currentSeats value:', currentSeats);
      const { newSeatCount } = event.detail;
      if (newSeatCount) {
        console.log('[DEBUG] Updating currentSeats from', currentSeats, 'to:', newSeatCount);
        setCurrentSeats(newSeatCount);
        console.log('[DEBUG] setCurrentSeats called with:', newSeatCount);
      } else {
        console.warn('[DEBUG] No newSeatCount in event detail');
      }
      console.log('[DEBUG] =========================================');
    };

    console.log('[DEBUG] Adding seatsUpdated event listener');
    window.addEventListener('seatsUpdated', handleSeatsUpdate);

    return () => {
      console.log('[DEBUG] Removing seatsUpdated event listener');
      window.removeEventListener('seatsUpdated', handleSeatsUpdate);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wrapper function for data refresh with loading indicator
  const triggerDataRefresh = async () => {
    console.log('[DEBUG] triggerDataRefresh called, props.onDataUpdate exists:', !!props.onDataUpdate);
    if (!props.onDataUpdate) return;
    
    console.log('[DEBUG] Starting data refresh, showing loading indicator');
    setIsDataRefreshing(true);
    try {
      await props.onDataUpdate();
      console.log('[DEBUG] Data refresh completed');
      
      // Reload billing data to get updated seat count
      console.log('[DEBUG] Reloading billing data to refresh seat count');
      await loadUserData();
      
      // Add a small delay to ensure the loading indicator is visible
      setTimeout(() => {
        console.log('[DEBUG] Hiding loading indicator');
        setIsDataRefreshing(false);
      }, 800);
    } catch (error) {
      console.error('Data refresh failed:', error);
      setIsDataRefreshing(false);
    }
  };

  // Expose loading control methods to parent component
  useImperativeHandle(ref, () => ({
    showTableLoading: () => {
      console.log('[DEBUG] showTableLoading called by parent');
      setIsDataRefreshing(true);
    },
    hideTableLoading: () => {
      console.log('[DEBUG] hideTableLoading called by parent');
      setIsDataRefreshing(false);
    },
    triggerDataRefresh
  }));

  // Clean up any phone registrations for empty email keys
  useEffect(() => {
    // Remove any phone registrations or requests for empty email keys
    setRegisteredPhoneNumbers(prev => {
      const cleaned = { ...prev };
      if (cleaned[''] || cleaned[undefined] || cleaned[null]) {
        delete cleaned[''];
        delete cleaned[undefined];
        delete cleaned[null];
        console.log('[DEBUG] Cleaned up phone registrations for empty email keys');
      }
      return cleaned;
    });
    
    setPhoneRegistrationRequests(prev => {
      const cleaned = { ...prev };
      if (cleaned[''] || cleaned[undefined] || cleaned[null]) {
        delete cleaned[''];
        delete cleaned[undefined];
        delete cleaned[null];
        console.log('[DEBUG] Cleaned up phone registration requests for empty email keys');
      }
      return cleaned;
    });
  }, [props.members]);

  // Track original member data in edit mode for change detection
  useEffect(() => {
    if (props.isEditMode && props.currentEditItem && props.currentEditItem.members) {
      // Snapshot uses the same field mapping as the edit handlers so comparisons are apples-to-apples
      const originalData = props.currentEditItem.members.map((member, memberIndex) => ({
        email: member.email || '',
        originalEmail: member.email || '',
        isNew: false,
        first_name: member.first_name || '',
        last_name: member.last_name || '',
        phone: member.phone || '',
        displayPhone: member.phone || '',
        provider: member.client || 'google',
        role: member.role || '',
        appleId: member.apple_id || '',
        appPassword: member.app_password || '',
        task_due_email_reminder: member.task_due_email_reminder !== false,
        personal_calendar_sync: member.personal_calendar_sync !== false,
        can_delete_task_lists: member.permissions?.can_delete_task_lists ?? member.can_delete_task_lists ?? (member.role === 'team_lead'),
        can_archive_task_lists: member.permissions?.can_archive_task_lists ?? member.can_archive_task_lists ?? (member.role === 'team_lead'),
        can_manage_teams: member.permissions?.can_manage_teams ?? member.can_manage_teams ?? false,
        can_view_unpublished_changes: member.permissions?.can_view_unpublished_changes ?? member.can_view_unpublished_changes ?? (member.role === 'team_lead'),
      }));
      setOriginalMembersData(originalData);
      setIsOriginalDataReady(true);

      // Initialize validation state for existing members
      const initialEmailValidation = {};
      const initialPhoneValidation = {};
      props.currentEditItem.members.forEach(member => {
        if (member.email) {
          const emailValidation = validateEmail(member.email);
          initialEmailValidation[member.email] = emailValidation;

          if (member.phone) {
            const phoneValidation = validatePhone(member.phone);
            initialPhoneValidation[member.email] = phoneValidation;
          }
        }
      });
      setEmailValidation(initialEmailValidation);
      setPhoneValidation(initialPhoneValidation);
    } else {
      // Clear original data when not in edit mode
      setOriginalMembersData([]);
      setIsOriginalDataReady(false);
      setEmailValidation({});
      setPhoneValidation({});
    }
  }, [props.isEditMode, props.currentEditItem]);

  // Initialize / reset team name for team-type connections
  useEffect(() => {
    const isTeamType = ['small_team', 'medium_team', 'large_team'].includes(props.accountType);
    if (!isTeamType) return;
    if (props.isEditMode && props.currentEditItem) {
      setTeamName(props.currentEditItem.team_name || '');
      setTeamNameError('');
      return;
    }
    // Create mode: find next available numbered default name
    setTeamNameError('');
    const firstName = user?.name?.split(' ')[0] || 'User';
    const baseName = `${firstName}'s Team`;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`/api/connections/${props.accountType}`);
        if (cancelled) return;
        const existingNames = new Set(
          (res.data || []).map(c => (c.team_name || '').trim().toLowerCase())
        );
        let n = 1;
        while (existingNames.has(`${baseName.toLowerCase()} ${n}`)) n++;
        setTeamName(`${baseName} ${n}`);
      } catch {
        if (!cancelled) setTeamName(`${baseName} 1`);
      }
    })();
    return () => { cancelled = true; };
  }, [props.isEditMode, props.currentEditItem, props.accountType, user?.name]);

  // Reset checkbox state when component is mounted or reopened
  useEffect(() => {
    console.log('[DEBUG] useEffect triggered with:', {
      isEditMode: props.isEditMode,
      currentEditItem: props.currentEditItem,
      userEmail: user?.email,
      membersCount: props.currentEditItem?.members?.length,
      isCompanyCalendar: props.currentEditItem?.is_company_calendar,
      paidUsers: props.currentEditItem?.paid_users
    });
    
    // Check if we're in edit mode and if there's already a company calendar connection
    if (props.isEditMode && props.currentEditItem && user?.email) {
      // Multiple ways to detect company calendar connection:
      // 1. Direct flag check
      // 2. paid_users field indicating company calendar billing
      // 3. Admin member has provider set
      
      const userEmail = user.email.toLowerCase().trim();
      console.log('[DEBUG] Checking for company calendar. User email:', userEmail);
      console.log('[DEBUG] Current edit item:', props.currentEditItem);
      
      // Check direct company calendar flag
      const hasCompanyCalendarFlag = props.currentEditItem.is_company_calendar === true;
      const isExplicitlyNotCompanyCalendar = props.currentEditItem.is_company_calendar === false;
      
      // If explicitly set to false, respect that setting and don't use heuristics
      if (isExplicitlyNotCompanyCalendar) {
        console.log('[DEBUG] *** EDIT MODE: is_company_calendar explicitly set to FALSE - respecting setting ***');
        setShowCalendarTypeSelection(false);
        setAuthCompleted(false);
        return; // Skip all other heuristic checks
      }
      
      // Check if paid_users is different from members count (indicates company calendar)
      const hasPaidUsersIndicator = props.currentEditItem.paid_users && 
        props.currentEditItem.members && 
        (props.currentEditItem.paid_users !== props.currentEditItem.members.length || props.currentEditItem.paid_users === 1);
      
      // Check if admin member has provider set
      const adminMember = props.currentEditItem.members?.[0];
      const hasAdminProvider = adminMember && (adminMember.provider || adminMember.client);
      
      // Check if any member has provider set (indicates calendar connection)
      const hasAnyProvider = props.currentEditItem.members?.some(member => member.provider || member.client);
      
      // NEW: Check company_calendar field for provider
      const companyCalendar = props.currentEditItem.company_calendar;
      const hasCompanyCalendarProvider = companyCalendar && companyCalendar.provider;
      console.log('[DEBUG] Company calendar field check:', {
        hasCompanyCalendar: !!companyCalendar,
        provider: companyCalendar?.provider,
        email: companyCalendar?.email,
        connected: companyCalendar?.connected
      });
      
      // Check connection description for company calendar indicators
      const hasCompanyDescription = props.currentEditItem.description && 
        props.currentEditItem.description.toLowerCase().includes('company calendar');
      
      // Check if paid_users equals 1 (strong indicator of company calendar)
      const hasPaidUsersOne = props.currentEditItem.paid_users === 1;
      
      // Additional check: if we have fewer members than typical for account type, might be company calendar
      const hasFewerMembers = props.currentEditItem.members && props.currentEditItem.members.length <= 2 && 
        props.currentEditItem.paid_users && props.currentEditItem.paid_users >= props.currentEditItem.members.length;
      
      const hasCompanyCalendar = hasCompanyCalendarFlag || hasPaidUsersIndicator || hasAdminProvider || hasAnyProvider || hasCompanyCalendarProvider || hasCompanyDescription || hasPaidUsersOne || hasFewerMembers;
      
      console.log('[DEBUG] *** EDIT MODE COMPANY CALENDAR DETECTION ***');
      console.log('[DEBUG] Company calendar detection:', {
        hasCompanyCalendarFlag,
        hasPaidUsersIndicator,
        hasAdminProvider,
        hasAnyProvider,
        hasCompanyCalendarProvider,
        hasCompanyDescription,
        hasPaidUsersOne,
        finalResult: hasCompanyCalendar,
        currentItemKeys: Object.keys(props.currentEditItem),
        memberProviders: props.currentEditItem.members?.map(m => ({ email: m.email, provider: m.provider, client: m.client })),
        paidUsers: props.currentEditItem.paid_users,
        membersLength: props.currentEditItem.members?.length,
        companyCalendarData: companyCalendar
      });
      
      if (hasCompanyCalendar) {
        console.log('[DEBUG] *** EDIT MODE: COMPANY CALENDAR DETECTED - SETTING CHECKBOX TRUE ***');
        setShowCalendarTypeSelection(true);
        setAuthCompleted(true); // Show as already connected
        
        // Set the provider from company calendar data if available
        if (hasCompanyCalendarProvider && props.members.length > 0) {
          console.log('[DEBUG] Setting provider from company_calendar field:', companyCalendar.provider);
          const updatedMembers = [...props.members];
          updatedMembers[0].provider = companyCalendar.provider;
          props.setMembers(updatedMembers);
        }
      } else {
        console.log('[DEBUG] *** EDIT MODE: NO COMPANY CALENDAR DETECTED - SETTING CHECKBOX FALSE ***');
        setShowCalendarTypeSelection(false);
        setAuthCompleted(false);
      }
    } else {
      // For new connections or when not in edit mode, check if user has existing company calendar connections
      console.log('[DEBUG] New connection: Checking for existing company calendar connections...');
      
      // Check localStorage for existing company calendar connections
      const hasExistingCompanyCalendar = checkForExistingCompanyCalendar();
      
      if (hasExistingCompanyCalendar) {
        console.log('[DEBUG] Found existing company calendar - showing checkbox as available option');
        setShowCalendarTypeSelection(true); // Show checkbox so user can choose to make this connection company calendar too
      } else {
        console.log('[DEBUG] No existing company calendar found - user can still manually check the option');
      }
      
      setAuthCompleted(false);
      // DO NOT reset showCalendarTypeSelection here - let user control it via checkbox
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isEditMode, props.currentEditItem, user?.email, props.currentEditItem?.members]); // React to changes in edit mode, item, user, or members

  // Separate effect to check for company calendar after component has settled
  useEffect(() => {
    // Small delay to ensure localStorage and other data is fully loaded
    const checkTimer = setTimeout(() => {
      if (!props.isEditMode && !showCalendarTypeSelection) {
        console.log('[DEBUG] Delayed check for existing company calendar connections...');
        const hasExisting = checkForExistingCompanyCalendar();
        if (hasExisting) {
          console.log('[DEBUG] DELAYED CHECK: Found existing company calendar - enabling checkbox');
          setShowCalendarTypeSelection(true);
        }
      }
    }, 500); // 500ms delay to ensure data is loaded

    return () => clearTimeout(checkTimer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.accountType, user?.email]); // Run when account type or user changes

  const loadUserData = async () => {
    try {
      const userId = user?.id || user?.userId || user?.user_id;
      console.log('[DEBUG] Loading phone slots for userId:', userId);
      console.log('[DEBUG] Full user object:', user);
      console.log('[DEBUG] Available user ID fields:', {
        id: user?.id,
        userId: user?.userId, 
        user_id: user?.user_id
      });
      if (!userId) return;
      
      // Load billing data for seats
      console.log(`[DEBUG] Loading billing data for userId: ${userId}, accountType: ${props.accountType}`);
      const billingResponse = await apiClient.get(`/api/billing/${userId}`);
      
      if (billingResponse.data.success && billingResponse.data.data) {
        const seats = billingResponse.data.data.currentPlan?.seatCount || ACCOUNT_TYPE_SEATS[props.accountType] || 2;
        console.log(`[DEBUG] Billing data found - seatCount: ${billingResponse.data.data.currentPlan?.seatCount}, fallback: ${ACCOUNT_TYPE_SEATS[props.accountType]}, final seats: ${seats}`);
        setCurrentSeats(seats);
      } else {
        const fallbackSeats = ACCOUNT_TYPE_SEATS[props.accountType] || 2;
        console.log(`[DEBUG] No billing data - using fallback seats: ${fallbackSeats} for accountType: ${props.accountType}`);
        setCurrentSeats(fallbackSeats);
      }
      
      // Load phone slots data
      await loadPhoneData(userId);
    } catch (err) {
      console.error('Failed to load user data:', err);
      const fallbackSeats = ACCOUNT_TYPE_SEATS[props.accountType] || 2;
      console.log(`[DEBUG] Error case - using fallback seats: ${fallbackSeats} for accountType: ${props.accountType}`);
      setCurrentSeats(fallbackSeats);
      setAvailablePhoneSlots([]);
      const planType = user?.account_type || user?.accountType || props.accountType || '';
      const normType = planType.toLowerCase().replace(/[\s]+/g, '_');
      const fallbackBaseSlots = PHONE_ADDON.BASE_SLOTS_PER_ACCOUNT[normType] ?? PHONE_ADDON.BASE_SLOTS_PER_ACCOUNT[planType] ?? 0;
      setTotalPhoneSlots(fallbackBaseSlots);
    }
  };

  const loadAvailability = async () => {
    setAvailability(prev => ({ ...prev, loading: true }));
    try {
      const res = await apiClient.get('/api/connections/availability');
      if (res.data.success) {
        setAvailability({
          loading: false,
          seatsTotal: res.data.seats_total,
          seatsUsed: res.data.seats_used,
          seatsAvailable: res.data.seats_available,
          phoneTotal: res.data.phone_slots_total,
          phoneUsed: res.data.phone_slots_used,
          phoneAvailable: res.data.phone_slots_available,
        });
      } else {
        setAvailability(prev => ({ ...prev, loading: false }));
      }
    } catch (e) {
      console.error('[availability] fetch error:', e);
      setAvailability(prev => ({ ...prev, loading: false }));
    }
  };

  const loadPhoneData = async (userId = null) => {
    // Use the account's plan type (from user object), not the connection type (props.accountType)
    const planType = user?.account_type || user?.accountType || props.accountType || '';
    const normalizedType = planType.toLowerCase().replace(/[\s]+/g, '_');
    const baseSlots = PHONE_ADDON.BASE_SLOTS_PER_ACCOUNT[normalizedType] ?? PHONE_ADDON.BASE_SLOTS_PER_ACCOUNT[planType] ?? 0;

    try {
      const actualUserId = userId || user?.id || user?.userId || user?.user_id;
      if (!actualUserId) {
        setTotalPhoneSlots(baseSlots);
        return;
      }

      console.log('[DEBUG] Loading phone data from billing for userId:', actualUserId);

      const billingResponse = await apiClient.get(`/api/billing/${actualUserId}`);

      if (billingResponse.data.success && billingResponse.data.data) {
        const purchasedPhoneSlots = billingResponse.data.data.phoneSlots || 0;
        const effectiveSlots = Math.max(purchasedPhoneSlots, baseSlots);
        setTotalPhoneSlots(effectiveSlots);
        console.log('[DEBUG] Set totalPhoneSlots: purchased=', purchasedPhoneSlots, 'base=', baseSlots, 'effective=', effectiveSlots);
        await loadExistingPhoneRegistrations(actualUserId);
      } else {
        console.log('[DEBUG] No billing data found, using plan base slots:', baseSlots);
        setTotalPhoneSlots(baseSlots);
      }
    } catch (phoneError) {
      console.error('Error loading phone data from billing:', phoneError);
      setTotalPhoneSlots(baseSlots);
    }
  };

  const loadExistingPhoneRegistrations = async (userId) => {
    try {
      // Load existing phone registrations directly from member phone fields
      // Only fetch connections relevant to the user's account type
      const existingRegistrations = {};
      
      // Helper function to extract phone numbers from connections
      // Backend enriches member.phone from Users.profile.phone at read time
      const extractPhoneNumbers = (data, connectionType) => {
        const list = data.connections || data.data || [];
        if (data.success && Array.isArray(list)) {
          list.forEach(connection => {
            if (connection.members && Array.isArray(connection.members)) {
              connection.members.forEach(member => {
                if (member.email && member.phone && member.phone !== "0000000000") {
                  existingRegistrations[member.email] = member.phone;
                  console.log(`[DEBUG] Found existing ${connectionType} phone registration:`, member.email, '→', member.phone);
                }
              });
            }
          });
        }
      };

      // Determine which connections to load based on account type
      const accountType = props.accountType || 'Solo';
      console.log(`[DEBUG] Loading connections for account type: ${accountType}`);
      
      // Always try to load solo connections (most accounts can have these)
      try {
        const soloResponse = await apiClient.get('/api/connections/solo');
        if (soloResponse.data) {
          extractPhoneNumbers(soloResponse.data, 'solo');
        }
      } catch (soloError) {
        console.log('[DEBUG] No solo connections found or error:', soloError.message);
      }

      // Load trial connections for trial accounts
      if (accountType.toLowerCase() === 'trial') {
        try {
          const trialResponse = await apiClient.get('/api/connections/trial');
          if (trialResponse.data) {
            extractPhoneNumbers(trialResponse.data, 'trial');
          }
        } catch (trialError) {
          console.log('[DEBUG] No trial connections found or error:', trialError.message);
        }
      }

      // Load team connections only for team account types
      if (accountType.toLowerCase().includes('team')) {
        try {
          const teamResponse = await apiClient.get('/api/connections/small_team');
          if (teamResponse.data) {
            extractPhoneNumbers(teamResponse.data, 'small_team');
          }
        } catch (teamError) {
          console.log('[DEBUG] No team connections found or error:', teamError.message);
        }
      }

      // Load solo_assistant connections for solo_assistant accounts
      if (accountType.toLowerCase().includes('assistant') || accountType.toLowerCase() === 'solo_assistant') {
        try {
          const soloAgentResponse = await apiClient.get('/api/connections/solo_assistant');
          if (soloAgentResponse.data) {
            extractPhoneNumbers(soloAgentResponse.data, 'solo_assistant');
          }
        } catch (assistantError) {
          console.log('[DEBUG] No solo_assistant connections found or error:', assistantError.message);
        }
      }
      
      // Merge into existing state — never overwrite phones already tracked in-memory
      // (prevents loadExistingPhoneRegistrations finishing last and clearing correct state set by checkPhoneRegistrations)
      setRegisteredPhoneNumbers(prev => ({...prev, ...existingRegistrations}));
      console.log('[DEBUG] Merged registeredPhoneNumbers from connections:', existingRegistrations);
    } catch (error) {
      console.error('Error loading existing phone registrations:', error);
    }
  };

  // Expose loadPhoneData function to parent component
  useImperativeHandle(ref, () => ({
    refreshPhoneData: () => loadPhoneData(),
    loadPhoneData: loadPhoneData
  }));

  // Manual function to check phone registrations (called when needed)
  const checkPhoneRegistrations = () => {
    // Simple approach: check if members have phone data saved in their member objects
    if (props.members.length > 0) {
      console.log('[DEBUG] checkPhoneRegistrations - checking member phone registrations...');
      console.log('[DEBUG] checkPhoneRegistrations - current phoneRegistrationRequests:', phoneRegistrationRequests);
      
      const existingRegistrations = {};
      
      props.members.forEach(member => {
        // Check if this member has a phone number in the simplified phone field
        if (member.phone) {
          const phoneNumber = member.phone;
          console.log(`[DEBUG] checkPhoneRegistrations - Member ${member.email} has phone: ${phoneNumber}`);
          // Only add to existingRegistrations if it's a complete 10-digit number and not the default
          if (phoneNumber && phoneNumber.toString().replace(/\D/g, '').length === 10 && phoneNumber !== "0000000000") {
            existingRegistrations[member.email] = phoneNumber;
          }
        }
      });
      
      console.log('[DEBUG] checkPhoneRegistrations - Found registered members:', existingRegistrations);
      console.log('[DEBUG] checkPhoneRegistrations - Before setting registeredPhoneNumbers, phoneRegistrationRequests was:', phoneRegistrationRequests);
      
      // Only update if there are actual complete registrations, don't clear existing ones
      if (Object.keys(existingRegistrations).length > 0) {
        setRegisteredPhoneNumbers(prev => {
          console.log('[DEBUG] checkPhoneRegistrations - Setting registeredPhoneNumbers from:', prev, 'to:', { ...prev, ...existingRegistrations });
          return { ...prev, ...existingRegistrations };
        });
      }
      
      // POTENTIAL FIX: Don't modify phoneRegistrationRequests here
      // Let the user maintain manual checkbox state
      console.log('[DEBUG] checkPhoneRegistrations - NOT modifying phoneRegistrationRequests to preserve user checkbox state');
    }
  };

  // Check phone registrations when phone slots are loaded
  useEffect(() => {
    console.log('[DEBUG] useEffect[availablephoneSlots] - Phone slots length:', availablePhoneSlots.length);
    if (availablePhoneSlots.length > 0) {
      console.log('[DEBUG] useEffect[availablephoneSlots] - Calling checkPhoneRegistrations...');
      checkPhoneRegistrations();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePhoneSlots.length]); // Only depend on the length to avoid infinite loops

  // Also check when members are available or change
  useEffect(() => {
    console.log('[DEBUG] useEffect[members] - Members length:', props.members.length);
    // DISABLED: This was causing interference with individual phone input
    // Only check initial load, not on every member change
    if (props.members.length > 0 && Object.keys(registeredPhoneNumbers).length === 0) {
      console.log('[DEBUG] useEffect[members] - Initial load, checking phone registrations...');
      checkPhoneRegistrations();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.members.length]); // Only check when member count changes, not phone data
  
  // Helper function to check if a member has changes compared to original data
  const hasMemberChanges = (memberIndex) => {
    if (!props.isEditMode || !originalMembersData || originalMembersData.length === 0) {
      return false; // Not in edit mode or no original data to compare
    }
    
    if (memberIndex >= originalMembersData.length || memberIndex >= props.members.length) {
      return true; // New member or index out of bounds means there are changes
    }
    
    const originalMember = originalMembersData[memberIndex];
    const currentMember = props.members[memberIndex];
    
    // Compare relevant fields
    const originalEmail = originalMember.email || '';
    const currentEmail = currentMember.email || '';
    const originalFirstName = (originalMember.first_name || '').trim();
    const currentFirstName = (currentMember.first_name || '').trim();
    const originalLastName = (originalMember.last_name || '').trim();
    const currentLastName = (currentMember.last_name || '').trim();
    const originalPhone = originalMember.phone || '';
    const currentPhone = currentMember.phone || '';
    const originalProvider = originalMember.provider || 'google';
    const currentProvider = currentMember.provider || 'google';
    const originalAppleId = originalMember.appleId || '';
    const currentAppleId = currentMember.appleId || '';
    const originalAppPassword = originalMember.appPassword || '';
    const currentAppPassword = currentMember.appPassword || '';
    const originalCalendarSync = originalMember.personal_calendar_sync !== false;
    const currentCalendarSync = currentMember.personal_calendar_sync !== false;
    const originalTaskReminder = originalMember.task_due_email_reminder !== false;
    const currentTaskReminder = currentMember.task_due_email_reminder !== false;
    const originalCanDelete = originalMember.can_delete_task_lists ?? originalMember.role === 'team_lead';
    const currentCanDelete = currentMember.permissions?.can_delete_task_lists ?? currentMember.role === 'team_lead';
    const originalCanArchive = originalMember.can_archive_task_lists ?? originalMember.role === 'team_lead';
    const currentCanArchive = currentMember.permissions?.can_archive_task_lists ?? currentMember.role === 'team_lead';
    const originalCanManageTeams = originalMember.can_manage_teams === true;
    const currentCanManageTeams  = currentMember.permissions?.can_manage_teams === true;
    const originalCanViewUnpublished = originalMember.can_view_unpublished_changes ?? originalMember.role === 'team_lead';
    const currentCanViewUnpublished  = currentMember.permissions?.can_view_unpublished_changes ?? currentMember.role === 'team_lead';

    // Also check local phone input state for this member
    const localPhone = localPhoneInputs[currentEmail] || '';
    const phoneChanged = originalPhone !== currentPhone || originalPhone !== localPhone;

    // Check if phone registration checkbox state has changed
    const originalHadPhone = originalPhone !== '';
    const currentPhoneRequested = phoneRegistrationRequests[currentEmail] || false;
    const phoneRegistrationChanged = originalHadPhone !== currentPhoneRequested;

    // IMPORTANT: Provider comparison for company calendar (memberIndex === 0)
    const providerChanged = originalProvider !== currentProvider;
    if (memberIndex === 0 && providerChanged) {
      console.log(`[DEBUG] *** COMPANY CALENDAR PROVIDER CHANGED for member ${memberIndex} ***`);
      console.log(`[DEBUG] Original provider: ${originalProvider} → Current provider: ${currentProvider}`);
    }

    const hasChanges = (
      originalEmail !== currentEmail ||
      originalFirstName !== currentFirstName ||
      originalLastName !== currentLastName ||
      phoneChanged ||
      phoneRegistrationChanged ||
      originalProvider !== currentProvider ||
      originalAppleId !== currentAppleId ||
      originalAppPassword !== currentAppPassword ||
      originalCalendarSync !== currentCalendarSync ||
      originalTaskReminder !== currentTaskReminder ||
      originalCanDelete !== currentCanDelete ||
      originalCanArchive !== currentCanArchive ||
      originalCanManageTeams !== currentCanManageTeams ||
      originalCanViewUnpublished !== currentCanViewUnpublished
    );

    console.log(`[DEBUG] hasMemberChanges for member ${memberIndex}:`, {
      originalEmail, currentEmail,
      originalPhone, currentPhone, localPhone,
      originalHadPhone, currentPhoneRequested, phoneRegistrationChanged,
      originalProvider, currentProvider,
      providerChanged,
      originalAppleId, currentAppleId,
      originalAppPassword, currentAppPassword,
      originalCalendarSync, currentCalendarSync,
      originalTaskReminder, currentTaskReminder,
      originalCanDelete, currentCanDelete,
      hasChanges,
      isCompanyCalendarMember: memberIndex === 0
    });
    
    return hasChanges;
  };
  
  // Email validation helper
  const validateEmail = (email) => {
    if (!email || email.trim() === '') {
      return { isValid: false, message: 'Email is required' };
    }
    
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Please enter a valid email address' };
    }
    
    return { isValid: true, message: '' };
  };
  
  // Phone validation helper
  const validatePhone = (phone) => {
    if (!phone || phone.trim() === '') {
      return { isValid: true, message: '' }; // Phone is optional
    }
    
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    
    if (digitsOnly.length < 10) {
      return { isValid: false, message: 'Phone number must be at least 10 digits' };
    }
    
    if (digitsOnly.length > 15) {
      return { isValid: false, message: 'Phone number is too long' };
    }
    
    return { isValid: true, message: '' };
  };
  
  // Update email validation state
  const updateEmailValidation = (memberEmail, email) => {
    const validation = validateEmail(email);
    setEmailValidation(prev => ({
      ...prev,
      [memberEmail]: validation
    }));
    return validation.isValid;
  };
  
  // Update phone validation state
  const updatePhoneValidation = (memberEmail, phone) => {
    const validation = validatePhone(phone);
    setPhoneValidation(prev => ({
      ...prev,
      [memberEmail]: validation
    }));
    return validation.isValid;
  };

  // Helper function to check for existing company calendar connections
  const checkForExistingCompanyCalendar = () => {
    try {
      const accountTypes = ['brokerspacex_small_teams', 'brokerspacex_medium_teams', 'brokerspacex_large_teams', 'brokerspacex_soloAgents'];
      
      for (const storageKey of accountTypes) {
        const storedData = localStorage.getItem(storageKey);
        if (storedData && storedData !== 'undefined') {
          try {
            const parsedData = JSON.parse(storedData);
            if (Array.isArray(parsedData)) {
              // Check each connection for company calendar indicators
              const hasCompanyCalendar = parsedData.some(connection => {
                if (!connection || !connection.members) return false;
                
                // Multiple ways to detect company calendar:
                const hasFlag = connection.is_company_calendar === true;
                const hasCompanyDescription = connection.description && 
                  connection.description.toLowerCase().includes('company calendar');
                const hasProvider = connection.members.some(member => member.provider || member.client);
                const hasPaidUsersIndicator = connection.paid_users && 
                  connection.members && 
                  (connection.paid_users > connection.members.length || connection.paid_users === 1);
                
                return hasFlag || hasCompanyDescription || hasProvider || hasPaidUsersIndicator;
              });
              
              if (hasCompanyCalendar) {
                console.log('[DEBUG] Found existing company calendar connections in', storageKey);
                return true;
              }
            }
          } catch (parseError) {
            console.log(`[DEBUG] Error parsing ${storageKey}:`, parseError);
          }
        }
      }
      
      console.log('[DEBUG] No existing company calendar connections found');
      return false;
    } catch (error) {
      console.log('[DEBUG] Error checking for existing company calendar:', error);
      return false;
    }
  };
  
  // Check if there are any changes across all members
  const hasAnyChanges = () => {
    if (!props.isEditMode) {
      console.log('[DEBUG] hasAnyChanges: Not in edit mode, returning false');
      return false; // In add mode, individual save buttons handle this
    }
    
    // Check if the number of members has changed (member removed or added)
    const memberCountChanged = originalMembersData.length !== props.members.length;
    console.log('[DEBUG] hasAnyChanges: Member count check:', {
      originalCount: originalMembersData.length,
      currentCount: props.members.length,
      memberCountChanged
    });
    
    if (memberCountChanged) {
      return true;
    }
    
    // Check for member data changes
    const hasMemberDataChanges = props.members.some((_, index) => hasMemberChanges(index));
    
    // Check if company calendar checkbox state has changed from original
    const originallyWasCompanyCalendar = props.currentEditItem?.is_company_calendar === true;
    const companyCalendarChanged = originallyWasCompanyCalendar !== showCalendarTypeSelection;
    
    console.log('[DEBUG] hasAnyChanges: Final result:', {
      hasMemberDataChanges,
      memberCountChanged,
      originallyWasCompanyCalendar,
      currentShowCalendarTypeSelection: showCalendarTypeSelection,
      companyCalendarChanged,
      totalMembers: props.members.length,
      overallHasChanges: hasMemberDataChanges || companyCalendarChanged
    });
    
    const isTeamType = ['small_team', 'medium_team', 'large_team'].includes(props.accountType);
    const savedTeamName = (props.currentEditItem?.team_name || '').trim();
    const teamNameChanged = isTeamType && teamName.trim() !== savedTeamName;

    return hasMemberDataChanges || companyCalendarChanged || teamNameChanged;
  };
  
  // Returns true if the given email appears more than once across all members (case/space insensitive)
  const isDuplicateEmail = (email) => {
    if (!email || !email.trim()) return false;
    const norm = email.trim().toLowerCase();
    return props.members.filter(m => (m.email || '').trim().toLowerCase() === norm).length > 1;
  };

  // Check if all inputs are valid across all members
  const areAllInputsValid = () => {
    // Must have at least one member
    if (!props.members || props.members.length === 0) {
      return false;
    }

    // Fail immediately if any duplicate emails exist
    if (props.members.some(m => isDuplicateEmail(m.email))) {
      return false;
    }

    // For team types, validate team name
    const isTeamType = ['small_team', 'medium_team', 'large_team'].includes(props.accountType);
    if (isTeamType) {
      // Team name is required when creating, or when editing a connection that already has one
      const savedTeamName = (props.currentEditItem?.team_name || '').trim();
      const teamNameRequired = !props.isEditMode || savedTeamName !== '';
      if (teamNameRequired && !teamName.trim()) return false;
      if (teamNameError) return false;
    }

    return props.members.every((member, index) => {
      // In edit mode, skip full field validation for members that haven't been changed —
      // they were already valid when the connection was saved.
      if (props.isEditMode && !hasMemberChanges(index)) return true;

      const emailValid = emailValidation[member.email]?.isValid !== false;
      const phoneValid = phoneValidation[member.email]?.isValid !== false;
      const hasEmail = member.email && member.email.trim() !== '';
      const hasFirstName = !!(member.first_name && member.first_name.trim());
      const hasLastName = !!(member.last_name && member.last_name.trim());

      // Existing saved members in edit mode: only re-validate email+phone.
      // Legacy connections may lack names, and the user may only be changing permissions.
      if (props.isEditMode && !member.isNew) {
        return hasEmail && emailValid && phoneValid;
      }

      // Add mode or new unsaved card in edit mode: all required fields must be present.
      return hasEmail && emailValid && phoneValid && hasFirstName && hasLastName;
    });
  };



  // Helper function to clear calendar auth messages when user interacts with other elements
  const clearCalendarAuthMessage = () => {
    console.log('[DEBUG] clearCalendarAuthMessage called, authCompleted:', authCompleted, 'authText:', authText);
    if (authText) {
      props.setMessage(null);
      setAuthText('');
    }
    // Don't reset authCompleted - let it persist after successful connection
    // It should only be reset when provider changes or in other specific scenarios
  };
  
  // Save all members at once
  const saveAllMembers = async () => {
    try {
      console.log('[DEBUG] *** SAVE ALL MEMBERS CALLED ***');
      console.log('[DEBUG] showCalendarTypeSelection at saveAllMembers start:', showCalendarTypeSelection);
      console.log('[DEBUG] authCompleted at saveAllMembers start:', authCompleted);
      console.log('[DEBUG] members:', props.members.map(m => ({ email: m.email, provider: m.provider })));
      
      console.log('[DEBUG] Saving all members...');

      // Validate first/last name for every member before doing anything
      for (let i = 0; i < props.members.length; i++) {
        const m = props.members[i];
        const label = i === 0 ? (props.accountType === 'solo_assistant' ? 'Agent' : 'Lead Agent') : `Member ${i + 1}`;
        if (!m.first_name?.trim()) {
          props.setMessage(`Please enter a first name for ${label}.`);
          return;
        }
        if (!m.last_name?.trim()) {
          props.setMessage(`Please enter a last name for ${label}.`);
          return;
        }
      }

      props.setLoading(true);
      props.setMessage(null);

      // Handle account invites for newly added members and cleanup for removed members
      const handleMemberLifecycle = async () => {
        console.log('[DEBUG] handleMemberLifecycle ENTRY: isEditMode=', props.isEditMode,
          'currentEditItem=', props.currentEditItem,
          'currentEditItem.members=', props.currentEditItem?.members?.map(m => m.email),
          'props.members=', props.members.map(m => m.email));

        const currentEmails = new Set(props.members.map(m => m.email?.toLowerCase()).filter(Boolean));
        // Use props.currentEditItem.members as the source of truth for original members.
        // originalMembersData (React state) can be stale due to async setState timing;
        // the prop is always the saved connection state before the user's current edits.
        const savedMembers = props.isEditMode ? (props.currentEditItem?.members || []) : [];
        const originalEmails = new Set(savedMembers.map(m => (m.email || '').toLowerCase()).filter(Boolean));
        const ownerEmail = user?.email || '';

        console.log('[DEBUG] handleMemberLifecycle: savedMembers=', savedMembers.map(m => m.email),
          'savedMembers.length=', savedMembers.length,
          'currentEmails=', [...currentEmails],
          'ownerEmail=', ownerEmail);

        // Call invite-member for every non-owner member.
        // For newly added members: creates the stub account and sends the invite email.
        // For existing owner-managed members: updates name/last_name only (no email sent).
        // For other existing accounts: backend returns 409 which is silently ignored.
        const allNonOwnerMembers = props.members.filter((m) =>
          m.email &&
          m.email.toLowerCase() !== ownerEmail.toLowerCase()
        );
        const newlyAdded = allNonOwnerMembers.filter((m) =>
          !originalEmails.has(m.email.toLowerCase())
        );
        console.log('[DEBUG] handleMemberLifecycle: allNonOwnerMembers=', allNonOwnerMembers.map(m => m.email), 'newlyAdded=', newlyAdded.map(m => m.email), 'savedMembers=', savedMembers.map(m => m.email));
        for (const member of allNonOwnerMembers) {
          const memberIdx = props.members.indexOf(member);
          const teamRole = memberIdx === 0 ? 'team_lead' : 'team_member';
          try {
            const res = await apiClient.post('/api/authentication/login/invite-member', {
              email: member.email,
              firstName: member.first_name,
              lastName: member.last_name,
              phone: localPhoneInputs[member.email] || member.phone || '',
              invitedBy: ownerEmail,
              companyName: user?.company || '',
              teamRole,
            });
            console.log('[DEBUG] invite-member response for', member.email, '(teamRole:', teamRole, ')', res.data);
          } catch (err) {
            // 409 = existing account that is not owner-managed (active/paid) — safe to ignore
            if (err.response?.status !== 409) {
              console.warn('[DEBUG] invite-member failed for:', member.email, err);
            }
          }
        }

        // Clean up removed members (edit mode only — savedMembers is populated)
        console.log('[DEBUG] handleMemberLifecycle: about to check savedMembers.length=', savedMembers.length, '— entering remove block?', savedMembers.length > 0);
        if (savedMembers.length > 0) {
          const removedMembers = savedMembers.filter(m => m.email && !currentEmails.has(m.email.toLowerCase()));
          console.log('[DEBUG] handleMemberLifecycle: removedMembers=', removedMembers.map(m => m.email), 'removedMembers.length=', removedMembers.length);
          for (const removedMember of removedMembers) {
            try {
              console.log('[DEBUG] Calling remove-member API for:', removedMember.email, 'ownerEmail:', ownerEmail);
              const removeResp = await apiClient.post('/api/authentication/remove-member', {
                memberEmail: removedMember.email,
                ownerEmail
              });
              console.log('[DEBUG] Remove member API response:', removedMember.email, removeResp.status, JSON.stringify(removeResp.data));
            } catch (err) {
              console.warn('[DEBUG] Remove member API error:', removedMember.email, err.response?.status, JSON.stringify(err.response?.data), err.message);
            }
          }
        }
      };

      // Prepare all members with enhanced data
      const enhancedMembers = props.members.map(member => {
        const phoneNumber = localPhoneInputs[member.email] || member.phone || "";
        
        return {
          ...member,
          phone: phoneNumber,
          displayPhone: phoneNumber
        };
      });

      // Check if this is a team account type
      const isTeamAccount = props.accountType && props.accountType.toLowerCase().includes('team');
      console.log('[DEBUG] saveAllMembers - isTeamAccount:', isTeamAccount, 'accountType:', props.accountType);

      // Validate team name for team account types
      if (isTeamAccount) {
        const savedTeamName = (props.currentEditItem?.team_name || '').trim();
        const teamNameRequired = !props.isEditMode || savedTeamName !== '';
        if (teamNameRequired && !teamName.trim()) {
          setTeamNameError('Team name is required.');
          if (props.setLoading) props.setLoading(false);
          return;
        }
      }
      
      if (isTeamAccount && !props.isEditMode) {
        // For new team connections, use the individual member save logic with team rules
        console.log('[DEBUG] Team account detected - saving members individually with team logic');
        
        // First, save the admin (index 0) which creates the connection with all members
        if (enhancedMembers.length > 0) {
          console.log('[DEBUG] Processing admin member - will create connection with all members');
          await saveMemberData(0); // Admin saves connection
        }
        
        // Then, send invites for other members in parallel (no need to wait)
        if (enhancedMembers.length > 1) {
          console.log('[DEBUG] Processing additional members - sending invitations in parallel');
          const invitePromises = [];
          for (let i = 1; i < enhancedMembers.length; i++) {
            console.log(`[DEBUG] Queueing invitation for member ${i + 1}/${enhancedMembers.length}:`, enhancedMembers[i].email);
            invitePromises.push(saveMemberData(i, true)); // Force invite mode for other members
          }
          // Start all invites in parallel, don't wait for them to complete
          Promise.all(invitePromises).catch(err => {
            console.error('[DEBUG] Some member invitations failed:', err);
          });
        }
        
        props.setMessage('✅ Team connection created successfully!');
        await handleMemberLifecycle();

        // Set loading to false for immediate feedback
        props.setLoading(false);

        // Trigger data refresh and refresh availability counts
        await triggerDataRefresh();
        loadAvailability();

        // Close the modal after successful save
        setTimeout(() => {
          if (props.onClose) {
            props.onClose();
          }
        }, 800);
        
        return;
      }
      
      // Transform form data to connection format
      console.log('[DEBUG] About to transform connection format with showCalendarTypeSelection:', showCalendarTypeSelection);
      const connectionData = dataService.transformToConnectionFormat(
        props.accountType,
        enhancedMembers,
        user,
        showCalendarTypeSelection,
        teamName
      );
      
      console.log('[DEBUG] Saving all members with connection data:', connectionData);
      
      // Save the entire connection
      if (props.isEditMode && props.currentEditItem?.connection_id) {
        // Update existing connection - use connection_id as the primary identifier
        const connectionId = props.currentEditItem.connection_id || props.currentEditItem.id;
        
        // DON'T override the members array from transformToConnectionFormat
        // It already has the correct format with client field mapped from provider
        //
        // Preserve the connection's actual ownership rather than the freshly-built
        // owner/user_id from transformToConnectionFormat (which reflects the acting
        // user, not necessarily the connection's owner — e.g. for a delegated Admin).
        // Editing a connection (including adding a member) must never move it to a
        // different workspace; ownership changes only happen via the dedicated
        // Transfer flow. The backend also enforces this independently.
        const updatedConnectionData = {
          ...connectionData,
          connection_id: connectionId,
          owner: props.currentEditItem.owner,
          user_id: props.currentEditItem.user_id
          // REMOVED: members: enhancedMembers - this was overriding the properly formatted members
        };
        
        console.log('[DEBUG] Saving updated connection with provider changes:', {
          connectionId,
          firstMemberProvider: connectionData.members[0]?.client,
          firstMemberEmail: connectionData.members[0]?.email
        });
        
        const result = await dataService.saveConnection(
          props.accountType,
          updatedConnectionData,
          true, // isEdit
          connectionId
        );
        
        if (result && result.success) {
          props.setMessage('✅ All changes saved successfully!');
          await handleMemberLifecycle();

          // Company calendar data is now included directly in the connection data
          // via transformToConnectionFormat, so no separate API call needed
          console.log('[DEBUG] Connection saved with company_calendar object:',
            updatedConnectionData.company_calendar ? 'YES' : 'NO');

          // Trigger data refresh and refresh availability counts
          await triggerDataRefresh();
          loadAvailability();
          
          console.log('[DEBUG] All members saved successfully to JSON file:', result);
          
          // Close the modal after successful save
          setTimeout(() => {
            if (props.onClose) {
              props.onClose();
            }
          }, 1000);
        } else {
          const errMsg = result.message || 'Failed to save changes. Please try again.';
          props.setMessage(errMsg);
          if (errMsg.toLowerCase().includes('team name') || errMsg.toLowerCase().includes('team with this name')) {
            setTeamNameError(errMsg);
          }
          console.error('[DEBUG] Failed to save all members:', result);
        }
      } else {
        // Create new connection
        const result = await dataService.saveConnection(
          props.accountType,
          connectionData,
          false, // isEdit
          null
        );
        
        if (result && result.success) {
          props.setMessage('✅ Connection created successfully!');
          await handleMemberLifecycle();
          loadAvailability();

          // If this is a company calendar connection, save additional calendar data
          if (showCalendarTypeSelection) {
            console.log('[DEBUG] Saving company calendar data after connection created...');
            try {
              const companyCalendarData = {
                provider: props.members[0]?.provider || 'google',
                email: companyCalendarOwnerEmail || props.members[0]?.email,
                name: 'Company Calendar',
                calendar_id: 'primary',
                // Note: These would normally come from OAuth flow
                // For now using placeholder data since we're marking existing connection as company calendar
                access_token: 'placeholder_token',
                refresh_token: 'placeholder_refresh_token',
                expires_at: new Date(Date.now() + 3600000).toISOString(), // 1 hour from now
                scopes: ['calendar.events'],
                calendar_settings: {},
                isCompanyCalendar: true
              };
              
              const companyCalendarResult = await apiClient.post('/api/company-calendar/save', {
                accountType: props.accountType,
                connectionData: companyCalendarData,
                userEmail: companyCalendarOwnerEmail
              });
              
              if (companyCalendarResult.data.success) {
                console.log('[DEBUG] Company calendar data saved successfully');
              } else {
                console.warn('[DEBUG] Failed to save company calendar data:', companyCalendarResult.data.message);
              }
            } catch (companyCalendarError) {
              console.error('[ERROR] Failed to save company calendar data:', companyCalendarError);
              // Don't fail the main operation if company calendar save fails
            }
          }
          
          // Trigger data refresh immediately
          await triggerDataRefresh();
          
          console.log('[DEBUG] New connection created successfully:', result);
          
          // Close the modal after successful save
          setTimeout(() => {
            if (props.onClose) {
              props.onClose();
            }
          }, 800);
        } else {
          const errMsg = result.message || 'Failed to create connection. Please try again.';
          props.setMessage(errMsg);
          if (errMsg.toLowerCase().includes('team name') || errMsg.toLowerCase().includes('team with this name')) {
            setTeamNameError(errMsg);
          }
          console.error('[DEBUG] Failed to create connection:', result);
        }
      }
    } catch (error) {
      console.error('[ERROR] Failed to save all members:', error);
      props.setMessage('Failed to save changes. Please try again.');
    } finally {
      props.setLoading(false);
    }
  };
  
  // Get effective seat limit (use current purchased seats or fallback to base)
  // Use useMemo to ensure it updates when currentSeats changes
  const effectiveSeatLimit = useMemo(() => {
    const limit = currentSeats || ACCOUNT_TYPE_SEATS[props.accountType] || 2;
    console.log('[DEBUG] effectiveSeatLimit calculated:', limit, '(currentSeats:', currentSeats, ', accountType:', props.accountType, ')');
    return limit;
  }, [currentSeats, props.accountType]);
  
  const openSuspendMemberModal = async (member, idx) => {
    const memberName = [member.first_name, member.last_name].filter(Boolean).join(' ') || member.email || `Member ${idx + 1}`;
    setConfirmSuspendMember({ member, idx, memberName });
    setSuspendMemberStep('checking');
    setSuspendMemberCheckResult(null);
    setSuspendMemberError(null);
    try {
      const res = await apiClient.post('/api/users/suspend/check', { member_email: member.email });
      const data = res.data || {};
      setSuspendMemberCheckResult(data);
      if (!data.can_suspend) {
        setSuspendMemberStep('blocked');
      } else if (data.requires_transfer) {
        setSuspendMemberStep('need_transfer');
      } else {
        setSuspendMemberStep('ready');
      }
    } catch (err) {
      setSuspendMemberError(err?.response?.data?.message || 'Failed to check suspend status.');
      setSuspendMemberStep('blocked');
    }
  };

  const handleSuspendMemberExecute = async () => {
    if (!confirmSuspendMember) return;
    setIsSuspendingMember(true);
    setSuspendMemberError(null);
    setSuspendMemberStep('executing');
    try {
      await apiClient.post('/api/users/suspend', { member_email: confirmSuspendMember.member.email });
      const updated = [...props.members];
      updated[confirmSuspendMember.idx] = {
        ...updated[confirmSuspendMember.idx],
        suspended: true,
        status: 'suspended',
        suspended_by: user?.email || '',
        suspended_at: new Date().toISOString(),
      };
      props.setMembers(updated);
      setConfirmSuspendMember(null);
    } catch (err) {
      setSuspendMemberError(err?.response?.data?.message || 'Failed to suspend member.');
      setSuspendMemberStep('ready');
    } finally {
      setIsSuspendingMember(false);
    }
  };

  const openReactivateMemberModal = (member, idx) => {
    const memberName = [member.first_name, member.last_name].filter(Boolean).join(' ') || member.email || `Member ${idx + 1}`;
    setConfirmReactivateMember({ member, idx, memberName });
    setReactivateMemberError(null);
  };

  const handleReactivateMemberExecute = async () => {
    if (!confirmReactivateMember) return;
    setIsReactivatingMember(true);
    setReactivateMemberError(null);
    try {
      await apiClient.post('/api/users/reactivate', { member_email: confirmReactivateMember.member.email });
      const updated = [...props.members];
      updated[confirmReactivateMember.idx] = {
        ...updated[confirmReactivateMember.idx],
        suspended: false,
        status: 'active',
        suspended_by: null,
        suspended_at: null,
      };
      props.setMembers(updated);
      setConfirmReactivateMember(null);
    } catch (err) {
      setReactivateMemberError(err?.response?.data?.message || 'Failed to reactivate member.');
    } finally {
      setIsReactivatingMember(false);
    }
  };

  const openRemoveMemberModal = async (member, idx) => {
    // New unsaved local card — remove from UI only, no API call
    if (member.isNew || !member.originalEmail) {
      const updated = [...props.members];
      updated.splice(idx, 1);
      props.setMembers(updated);
      return;
    }

    // Existing saved member — use originalEmail so clearing the visible input doesn't break the flow
    const emailForRemove = member.originalEmail || member.email;
    const memberName = [member.first_name, member.last_name].filter(Boolean).join(' ') || emailForRemove || `Member ${idx + 1}`;
    setConfirmRemoveMember({ member: { ...member, email: emailForRemove }, idx, memberName });
    setRemoveMemberStep('checking');
    setRemoveMemberCheckResult(null);
    setRemoveMemberError(null);
    try {
      const res = await apiClient.post('/api/users/remove/check', { member_email: emailForRemove });
      const data = res.data || {};
      setRemoveMemberCheckResult(data);
      const scope = data.delete_scope;
      if (scope === 'blocked_requires_new_lead_agent') {
        setRemoveMemberStep('blocked');
      } else if (scope === 'transfer_required') {
        setRemoveMemberStep('need_transfer');
      } else {
        setRemoveMemberStep('ready');
      }
    } catch (err) {
      setRemoveMemberError(err?.response?.data?.message || 'Failed to check member status.');
      setRemoveMemberStep('blocked');
    }
  };

  const handleRemoveMemberExecute = async () => {
    if (!confirmRemoveMember) return;
    setIsRemovingMember(true);
    setRemoveMemberError(null);
    setRemoveMemberStep('executing');
    try {
      await apiClient.post('/api/users/remove/execute', { member_email: confirmRemoveMember.member.email });
      const updated = [...props.members];
      updated.splice(confirmRemoveMember.idx, 1);
      props.setMembers(updated);
      setConfirmRemoveMember(null);
    } catch (err) {
      setRemoveMemberError(err?.response?.data?.message || 'Failed to remove member.');
      setRemoveMemberStep('ready');
    } finally {
      setIsRemovingMember(false);
    }
  };

  const openTransferMemberModal = async (memberArg) => {
    const effectiveMember = memberArg || confirmRemoveMember;
    if (!effectiveMember) return;
    if (memberArg) setConfirmRemoveMember(memberArg);
    setTransferMemberModalOpen(true);
    setTransferMemberStep('select');
    setSelectedMemberTransfer(null);
    setTransferMemberConflicts([]);
    setTransferMemberConflictResolutions({});
    setTransferMemberTaskResolutions({});
    setTransferMemberError(null);
    setTransferMemberTargets([]);
    setTransferMemberTargetsLoading(true);
    setTransferMemberTargetsError(null);
    try {
      const res = await apiClient.get('/api/users/transfer-targets', {
        params: { originalEmail: effectiveMember.member.email, ownerEmail: user?.email },
      });
      if (res.data.success) {
        setTransferMemberTargets(res.data.teams || []);
      } else {
        setTransferMemberTargetsError(res.data.message || 'Failed to load eligible users');
      }
    } catch (err) {
      setTransferMemberTargetsError(err?.response?.data?.message || 'Failed to load eligible users');
    } finally {
      setTransferMemberTargetsLoading(false);
    }
  };

  const openDirectTransferMemberModal = async (member, idx) => {
    const sourceEmail = member.originalEmail || member.email;
    if (!sourceEmail) return;
    const memberName = [member.first_name, member.last_name].filter(Boolean).join(' ') || sourceEmail || `Member ${idx + 1}`;
    setConfirmRemoveMember({ member: { ...member, email: sourceEmail, name: member.first_name }, idx, memberName });
    setTransferMemberDirectMode(true);
    setTransferMemberModalOpen(true);
    setTransferMemberStep('select');
    setSelectedMemberTransfer(null);
    setTransferMemberConflicts([]);
    setTransferMemberConflictResolutions({});
    setTransferMemberTaskResolutions({});
    setTransferMemberError(null);
    setTransferMemberTargets([]);
    setTransferMemberTargetsLoading(true);
    setTransferMemberTargetsError(null);
    try {
      const res = await apiClient.get('/api/users/transfer-targets', {
        params: { originalEmail: sourceEmail, ownerEmail: user?.email },
      });
      if (res.data.success) {
        setTransferMemberTargets(res.data.teams || []);
      } else {
        setTransferMemberTargetsError(res.data.message || 'Failed to load eligible users');
      }
    } catch (err) {
      setTransferMemberTargetsError(err?.response?.data?.message || 'Failed to load eligible users');
    } finally {
      setTransferMemberTargetsLoading(false);
    }
  };

  // ── Change Lead Agent handlers ───────────────────────────────────────────────

  const openChangeLeadAgentModal = () => {
    setClaModalOpen(true);
    setClaStep('choose');
    setClaMode(null);
    setClaSelectedMember(null);
    setClaNewUser({ firstName: '', lastName: '', email: '' });
    setClaNewUserErrors({});
    setClaOldAction('keep');
    setClaPreview(null);
    setClaPreviewLoading(false);
    setClaError(null);
    setClaExecuting(false);
    setClaTransferRequired(false);
    setClaTransferDone(false);
    setClaTransferPendingReturn(false);
  };

  const closeChangeLeadAgentModal = () => {
    if (claExecuting) return;
    setClaModalOpen(false);
  };

  const handleClaContinueFromChoose = () => {
    if (claMode === 'existing' && !claSelectedMember) {
      setClaError('Please select a team member.');
      return;
    }
    if (claMode === 'new') {
      const errors = {};
      if (!claNewUser.firstName.trim()) errors.firstName = 'First name is required';
      if (!claNewUser.lastName.trim()) errors.lastName = 'Last name is required';
      if (!claNewUser.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(claNewUser.email)) {
        errors.email = 'Please enter a valid email address';
      } else {
        const emailLower = claNewUser.email.toLowerCase();
        if (emailLower === (props.members[0]?.email || '').toLowerCase()) {
          errors.email = 'This is already the Lead Agent email';
        } else if (props.members.some(m => m.email.toLowerCase() === emailLower)) {
          errors.email = 'This email already belongs to a team member';
        }
      }
      if (Object.keys(errors).length > 0) { setClaNewUserErrors(errors); return; }
    }
    setClaNewUserErrors({});
    setClaError(null);
    setClaOldAction(claMode === 'new' ? 'remove' : 'keep');
    setClaStep('old_action');
  };

  const handleClaContinueFromOldAction = async () => {
    setClaPreviewLoading(true);
    setClaError(null);
    setClaPreview(null);
    setClaTransferRequired(false);
    setClaTransferDone(false);
    try {
      const oldLead = props.members[0];
      const oldLeadName = [oldLead.first_name, oldLead.last_name].filter(Boolean).join(' ') || oldLead.email;

      // Best-effort task count — gracefully skip if endpoint unavailable (cloud API)
      let activeTaskListCount = 0;
      try {
        const taskRes = await apiClient.post('/api/tasks/members', { emails: [oldLead.email] });
        if (taskRes.data?.success) activeTaskListCount = taskRes.data.count || 0;
      } catch { /* not available in all environments */ }

      const preview = {
        success: true,
        can_change: true,
        requires_transfer: activeTaskListCount > 0,
        active_task_list_count: activeTaskListCount,
        seat_impact: {
          current_active_seats: props.members.filter(m => !m.suspended).length,
          will_increase_seat_count: claMode === 'new',
        },
        new_lead_user_status: claMode === 'existing' ? 'existing_member' : 'new_user',
        old_lead_email: oldLead.email,
        old_lead_name: oldLeadName,
        warnings: [],
      };
      setClaPreview(preview);
      setClaTransferRequired(preview.requires_transfer);
      setClaStep('preview');
    } catch (err) {
      setClaError('Failed to load preview. Please try again.');
    } finally {
      setClaPreviewLoading(false);
    }
  };

  const openClaTransferModal = async () => {
    const oldLead = props.members[0];
    const sourceEmail = oldLead.originalEmail || oldLead.email;
    const memberName = [oldLead.first_name, oldLead.last_name].filter(Boolean).join(' ') || sourceEmail;
    const newLeadEmail = claMode === 'existing' ? claSelectedMember.email : claNewUser.email;
    const newLeadName = claMode === 'existing'
      ? ([claSelectedMember.first_name, claSelectedMember.last_name].filter(Boolean).join(' ') || claSelectedMember.email)
      : ([claNewUser.firstName, claNewUser.lastName].filter(Boolean).join(' ') || claNewUser.email);
    const connectionId = props.currentEditItem?.connection_id || props.currentEditItem?.id;
    const teamName = props.currentEditItem?.team_name || props.accountType;

    // Build target directly from known CLA data — no recipient selection needed
    const targetSelection = {
      targetUser: { email: newLeadEmail, name: newLeadName, role_label: 'New Lead Agent' },
      connectionId,
      teamName,
    };

    setClaTransferPendingReturn(true);
    setConfirmRemoveMember({ member: { ...oldLead, email: sourceEmail, name: oldLead.first_name }, idx: 0, memberName });
    setTransferMemberDirectMode(true);
    setSelectedMemberTransfer(targetSelection);
    setTransferMemberConflicts([]);
    setTransferMemberConflictResolutions({});
    setTransferMemberTaskResolutions({});
    setTransferMemberError(null);
    setTransferMemberModalOpen(true);

    // For new-invite CLA, the invited user has no existing records — no conflicts possible.
    // Skip the backend membership check and go straight to confirm.
    if (claMode === 'new') {
      setTransferMemberStep('confirm');
      return;
    }

    // Skip 'select' step — run conflict check immediately
    await runTransferConflictCheck(sourceEmail, newLeadEmail, connectionId);
  };

  const handleClaExecute = async () => {
    if (claExecuting) return;
    setClaExecuting(true);
    setClaError(null);
    setClaStep('executing');
    try {
      const connectionId = props.currentEditItem?.connection_id || props.currentEditItem?.id;
      const oldLead = props.members[0];

      const applyLeadPerms = (member) => ({
        ...member,
        role: 'team_lead',
        team_role: 'team_lead',
        is_connection_owner: true,
        permissions: {
          ...(member.permissions || {}),
          can_edit_connection: true,
          can_manage_members: true,
          has_control: true,
          can_manage_teams: true,
          can_delete_task_lists: true,
          can_archive_task_lists: true,
          can_view_unpublished_changes: true,
        },
      });

      const applyMemberPerms = (member) => ({
        ...member,
        role: 'team_member',
        team_role: 'team_member',
        is_connection_owner: false,
        permissions: {
          ...(member.permissions || {}),
          can_edit_connection: false,
          can_manage_members: false,
          has_control: false,
          can_manage_teams: false,
          can_delete_task_lists: false,
          can_archive_task_lists: false,
          can_view_unpublished_changes: false,
        },
      });

      let newMembers;
      if (claMode === 'existing') {
        const newLead = applyLeadPerms(claSelectedMember);
        const oldLeadDemoted = applyMemberPerms(oldLead);
        const rest = props.members.filter(m => m.email !== claSelectedMember.email && m.email !== oldLead.email);
        newMembers = claOldAction === 'keep'
          ? [newLead, oldLeadDemoted, ...rest]
          : [newLead, ...rest];
      } else {
        const newLead = applyLeadPerms({
          email: claNewUser.email,
          first_name: claNewUser.firstName,
          last_name: claNewUser.lastName,
          role: 'team_lead',
          status: 'invited',
          added_by: user?.email,
          added_date: new Date().toISOString(),
          billing_status: 'pending',
          permissions: {},
        });
        const oldLeadDemoted = applyMemberPerms(oldLead);
        const rest = props.members.slice(1);
        newMembers = claOldAction === 'keep'
          ? [newLead, oldLeadDemoted, ...rest]
          : [newLead, ...rest];
      }

      // Strip DynamoDB-internal fields that cause UpdateItem path conflicts
      const { status_updated_at, created_at, updated_at, status, ...cleanBase } = props.currentEditItem || {};
      const updatedConnectionData = {
        ...cleanBase,
        connection_id: connectionId,
        members: newMembers,
        current_members: newMembers.length,
      };

      const result = await dataService.saveConnection(
        props.accountType,
        updatedConnectionData,
        true,
        connectionId
      );

      if (result && result.success) {
        props.setMembers(newMembers);
        setClaModalOpen(false);

        // If the currently logged-in user is one of the affected members, update their
        // local auth state immediately so the navbar refreshes without requiring re-login.
        // (For other affected users, can_manage_teams is derived from Connections at login time.)
        const newLead = newMembers[0];
        const currentEmail = user?.email?.toLowerCase();
        if (currentEmail === oldLead.email?.toLowerCase()) {
          updateUserProfile({ ...user, can_manage_teams: false });
        } else if (currentEmail === newLead.email?.toLowerCase()) {
          updateUserProfile({ ...user, can_manage_teams: true });
        }

        // If old lead was removed, clean up their Users record if they are now orphaned
        if (claOldAction === 'remove') {
          const oldLeadEmail = oldLead.originalEmail || oldLead.email;
          try {
            await apiClient.post('/api/users/remove/orphan', { member_email: oldLeadEmail });
          } catch (_) {
            // Non-fatal — orphan cleanup failure does not block the CLA success path
          }
        }

        await triggerDataRefresh();
      } else {
        setClaError(result?.message || 'Failed to change Lead Agent');
        setClaStep('preview');
      }
    } catch (err) {
      setClaError(err?.response?.data?.message || err?.message || 'Failed to change Lead Agent');
      setClaStep('preview');
    } finally {
      setClaExecuting(false);
    }
  };

  const runTransferConflictCheck = async (sourceEmail, targetEmail, connectionId) => {
    setTransferMemberStep('checking');
    setTransferMemberError(null);
    try {
      const res = await apiClient.post('/api/users/transfer-check', {
        originalEmail: sourceEmail,
        targetEmail,
        connectionId,
        ownerEmail: user?.email,
      });
      if (res.data.success) {
        if (!res.data.hasTransferableRecords) {
          setTransferMemberStep('no-records');
        } else if (res.data.hasConflicts) {
          const defaults = {};
          (res.data.conflicts || []).forEach(c => { defaults[c.conflictKey] = 'keep_target'; });
          setTransferMemberConflictResolutions(defaults);
          setTransferMemberConflicts(res.data.conflicts || []);
          setTransferMemberStep('conflict-warning');
        } else {
          setTransferMemberStep('confirm');
        }
      } else {
        setTransferMemberError(res.data.message || 'Conflict check failed');
        setTransferMemberStep('select');
      }
    } catch (err) {
      setTransferMemberError(err?.response?.data?.message || 'Conflict check failed');
      setTransferMemberStep('select');
    }
  };

  const handleTransferMemberContinue = async () => {
    if (!selectedMemberTransfer || !confirmRemoveMember) return;
    await runTransferConflictCheck(
      confirmRemoveMember.member.email,
      selectedMemberTransfer.targetUser.email,
      selectedMemberTransfer.connectionId,
    );
  };

  const handleTransferMemberExecute = async () => {
    if (isTransferringMember || !confirmRemoveMember || !selectedMemberTransfer) return;
    setIsTransferringMember(true);
    setTransferMemberError(null);
    const memberToRemove = confirmRemoveMember;
    try {
      const resolutionsList = Object.entries(transferMemberConflictResolutions).map(
        ([conflictKey, choice]) => ({ conflictKey, choice, taskResolutions: transferMemberTaskResolutions[conflictKey] || {} })
      );
      const res = await apiClient.post('/api/users/transfer-execute', {
        originalEmail: memberToRemove.member.email,
        targetEmail: selectedMemberTransfer.targetUser.email,
        connectionId: selectedMemberTransfer.connectionId,
        ownerEmail: user?.email,
        resolutions: resolutionsList,
      });
      if (res.data.success) {
        if (suspendMemberAfterTransfer) {
          try {
            await apiClient.post('/api/users/suspend', { member_email: memberToRemove.member.email });
            const updated = [...props.members];
            updated[memberToRemove.idx] = {
              ...updated[memberToRemove.idx],
              suspended: true,
              status: 'suspended',
              suspended_by: user?.email || '',
              suspended_at: new Date().toISOString(),
            };
            props.setMembers(updated);
          } catch (_) { /* non-fatal — transfer succeeded, suspend will need retry */ }
          setTransferMemberModalOpen(false);
          setConfirmRemoveMember(null);
          setConfirmSuspendMember(null);
          setSuspendMemberAfterTransfer(false);
        } else if (transferMemberDirectMode) {
          setTransferMemberModalOpen(false);
          setConfirmRemoveMember(null);
          setTransferMemberDirectMode(false);
          if (claTransferPendingReturn) {
            setClaTransferDone(true);
            setClaTransferPendingReturn(false);
          }
        } else {
          try {
            await apiClient.post('/api/users/remove/execute', { member_email: memberToRemove.member.email });
          } catch (_) { /* non-fatal — transfer succeeded, remove will be retried */ }
          const updated = [...props.members];
          updated.splice(memberToRemove.idx, 1);
          props.setMembers(updated);
          setTransferMemberModalOpen(false);
          setConfirmRemoveMember(null);
        }
      } else {
        setTransferMemberError(res.data.message || 'Transfer failed');
      }
    } catch (err) {
      setTransferMemberError(err?.response?.data?.message || 'Transfer failed');
    } finally {
      setIsTransferringMember(false);
    }
  };

  // For claMode==='new': the invited user isn't a team member yet, so transfer-execute's normal
  // membership check would fail. Pass skipTargetMemberCheck so the backend skips it.
  // The CLA save (which adds the user as invited member) runs afterward via handleClaExecute.
  const handleClaNewUserTransferExecute = async () => {
    if (isTransferringMember || !confirmRemoveMember || !selectedMemberTransfer) return;
    setIsTransferringMember(true);
    setTransferMemberError(null);
    try {
      const connectionId = props.currentEditItem?.connection_id || props.currentEditItem?.id;
      const res = await apiClient.post('/api/users/transfer-execute', {
        originalEmail: confirmRemoveMember.member.email,
        targetEmail: selectedMemberTransfer.targetUser.email,
        connectionId,
        ownerEmail: user?.email,
        resolutions: [],
        skipTargetMemberCheck: true,
      });

      if (res.data.success) {
        setTransferMemberModalOpen(false);
        setConfirmRemoveMember(null);
        setTransferMemberDirectMode(false);
        setClaTransferPendingReturn(false);
        setClaTransferDone(true);
      } else {
        setTransferMemberError(res.data.message || 'Transfer failed');
      }
    } catch (err) {
      setTransferMemberError(err?.response?.data?.message || 'Transfer failed');
    } finally {
      setIsTransferringMember(false);
    }
  };

  // Calculate total used seats across all account types
  const getTotalUsedSeats = () => {
    try {
      const accountTypes = ['solo', 'small_team', 'medium_team', 'large_team', 'solo_assistant', 'trial'];
      let totalUsed = 0;
      
      console.log('[DEBUG] Calculating total used seats...');
      console.log('[DEBUG] All localStorage keys:', Object.keys(localStorage));
      
      accountTypes.forEach(accountType => {
        let storageKey = 'brokerspacex_';
        if (accountType === 'solo_assistant') {
          storageKey += 'soloAgents';
        } else if (accountType === 'small_team') {
          storageKey += 'small_teams';
        } else if (accountType === 'medium_team') {
          storageKey += 'medium_teams';
        } else if (accountType === 'large_team') {
          storageKey += 'large_teams';
        } else if (accountType === 'solo') {
          storageKey += 'solos';
        } else if (accountType === 'trial') {
          storageKey += 'trials';
        }
        
        const stored = localStorage.getItem(storageKey);
        console.log(`[DEBUG] Checking ${storageKey}:`, stored ? 'Found data' : 'No data');
        
        if (stored) {
          try {
            const data = JSON.parse(stored);
            console.log(`[DEBUG] ${storageKey} parsed data:`, data);
            
            if (Array.isArray(data)) {
              let accountTypeUsed = 0;
              
              // Filter to only active connections and remove duplicates by keeping the most recent
              const activeConnections = data.filter(connection => connection.status === 'active');
              console.log(`[DEBUG] ${storageKey} active connections:`, activeConnections.length);
              
              // For now, let's only count the most recent connection to avoid duplicates
              // This is a temporary fix - the real solution is to prevent duplicate creation
              const mostRecentConnection = activeConnections.length > 0 ? 
                activeConnections.sort((a, b) => {
                  const aTime = parseInt(a.id.split('_')[1]) || 0;
                  const bTime = parseInt(b.id.split('_')[1]) || 0;
                  return bTime - aTime; // Sort descending (most recent first)
                })[0] : null;
              
              if (mostRecentConnection && mostRecentConnection.members && Array.isArray(mostRecentConnection.members)) {
                // Check if this is a company calendar connection
                const isCompanyCalendar = mostRecentConnection.is_company_calendar || 
                  (mostRecentConnection.description && mostRecentConnection.description.toLowerCase().includes('company calendar')) ||
                  (mostRecentConnection.members?.some(member => member.provider || member.client));
                
                // If we detect company calendar and not in edit mode, enable the checkbox for new connections
                if (isCompanyCalendar && !props.isEditMode && !showCalendarTypeSelection) {
                  console.log('[DEBUG] SEAT CALCULATION: Company calendar detected - enabling checkbox for new connections');
                  setShowCalendarTypeSelection(true);
                }
                
                let seatsUsed;
                if (isCompanyCalendar) {
                  // For company calendar connections:
                  // 1. If paid_users > members count, it includes company calendar seat - use paid_users
                  // 2. If paid_users <= members count, calculate as members + 1 for company calendar
                  // 3. Special case: if members count seems low for the account setup, use minimum expected
                  
                  if (mostRecentConnection.paid_users && mostRecentConnection.paid_users > mostRecentConnection.members.length) {
                    seatsUsed = mostRecentConnection.paid_users;
                    console.log(`[DEBUG] Using paid_users for company calendar: ${seatsUsed} seats`);
                  } else {
                    // Calculate based on context: for small_team with company calendar, minimum is usually 3
                    // (1 admin + 1 member + 1 company calendar)
                    const minSeatsForTeamWithCompanyCalendar = Math.max(mostRecentConnection.members.length + 1, 3);
                    seatsUsed = minSeatsForTeamWithCompanyCalendar;
                    console.log(`[DEBUG] Company calendar calculated: min(${mostRecentConnection.members.length} members + 1 company calendar, 3) = ${seatsUsed} seats`);
                  }
                } else {
                  // For regular connections: use paid_users if available, otherwise members.length
                  seatsUsed = mostRecentConnection.paid_users || mostRecentConnection.members.length;
                }
                
                accountTypeUsed += seatsUsed;
                totalUsed += seatsUsed;
                console.log(`[DEBUG] Using most recent connection: ${mostRecentConnection.id}`);
                console.log(`[DEBUG] - Account type: ${accountType}`);
                console.log(`[DEBUG] - members: ${mostRecentConnection.members.length}`);
                console.log(`[DEBUG] - paid_users: ${mostRecentConnection.paid_users}`);
                console.log(`[DEBUG] - seats used: ${seatsUsed}`);
                console.log(`[DEBUG] - is_company_calendar: ${mostRecentConnection.is_company_calendar}`);
                console.log(`[DEBUG] - totalUsed so far: ${totalUsed}`);
                console.log(`[DEBUG] - detected as company calendar: ${isCompanyCalendar}`);
                console.log(`[DEBUG] - member details:`, mostRecentConnection.members);
              }
              
              console.log(`[DEBUG] ${accountType} (${storageKey}): ${accountTypeUsed} seats used (from ${activeConnections.length} active connections)`);
            }
          } catch (parseError) {
            console.error(`[DEBUG] Error parsing ${storageKey}:`, parseError);
          }
        }
      });
      
      console.log(`[DEBUG] Total used seats across all account types: ${totalUsed}`);
      return totalUsed;
    } catch (error) {
      console.error('Error calculating total used seats:', error);
      return 0;
    }
  };
  
  // Get available seats for display - use prop from parent if available
  const getAvailableSeatsForDisplay = () => {
    // ALWAYS use our calculation to ensure accuracy
    const totalUsed = getTotalUsedSeats();
    let currentModalMembers = props.members ? props.members.filter(m => !m.suspended).length : 0;
    
    // If we're creating a NEW company calendar connection (not editing), it only uses 1 seat regardless of member count
    // Don't apply this logic in edit mode with existing members
    const isNewCompanyCalendarConnection = showCalendarTypeSelection && 
                                          props.members && 
                                          props.members[0] && 
                                          props.members[0].provider &&
                                          !props.isEditMode;
    
    if (isNewCompanyCalendarConnection) {
      currentModalMembers = 1;
      console.log(`[DEBUG] Display: New company calendar connection detected, using 1 seat instead of ${props.members.length} members`);
    }
    
    const totalPurchased = currentSeats || ACCOUNT_TYPE_SEATS[props.accountType] || 2;
    
    console.log(`[DEBUG] Seat calculation: currentSeats=${currentSeats}, accountType=${props.accountType}, ACCOUNT_TYPE_SEATS[accountType]=${ACCOUNT_TYPE_SEATS[props.accountType]}, totalPurchased=${totalPurchased}`);
    
    // For display purposes when not actively editing, show total available without edit mode adjustments
    // Only apply edit mode logic if we're actually modifying the connection
    let adjustedTotalUsed = totalUsed;
    console.log(`[DEBUG] Before edit mode adjustment: totalUsed: ${totalUsed}, isEditMode: ${props.isEditMode}`);
    
    // In edit mode, calculate available seats based on current modal state
    if (props.isEditMode && originalMembersData && originalMembersData.length > 0) {
      console.log(`[DEBUG] ========== EDIT MODE SEAT CALCULATION ==========`);
      console.log(`[DEBUG] - totalPurchased: ${totalPurchased} (currentSeats=${currentSeats}, fallback=${ACCOUNT_TYPE_SEATS[props.accountType]})`);
      console.log(`[DEBUG] - totalUsed: ${totalUsed} (ALL connections total)`);
      console.log(`[DEBUG] - originalMembersData.length: ${originalMembersData.length} (original members in THIS connection)`);
      console.log(`[DEBUG] - currentModalMembers: ${currentModalMembers} (current members in modal)`);
      console.log(`[DEBUG] - props.members:`, props.members);
      console.log(`[DEBUG] - originalMembersData:`, originalMembersData);
      
      // Calculate seats used in OTHER connections (excluding this one being edited)
      const usedElsewhere = Math.max(0, totalUsed - originalMembersData.length);
      console.log(`[DEBUG] - usedElsewhere = totalUsed(${totalUsed}) - originalMembers(${originalMembersData.length}) = ${usedElsewhere}`);
      
      // Available = total - used elsewhere - current members in this modal
      // This represents the "budget" remaining: what's left after other connections and this modal
      const remainingBudget = Math.max(0, totalPurchased - usedElsewhere - currentModalMembers);
      
      // For display, show how many MORE can be ADDED (not the absolute remaining)
      // If modal has 1 member and remainingBudget is 4, it means "4 more can be added" (total 5)
      const available = remainingBudget;
      console.log(`[DEBUG] - remainingBudget (how many more can be added) = ${remainingBudget}`);
      console.log(`[DEBUG] - available = ${available}`);
      console.log(`[DEBUG] ================================================`);
      return available;
    }
    
    // Calculate available seats for new connection or non-edit scenarios
    // Formula: total purchased - already used - current modal members
    const calculated = Math.max(0, totalPurchased - adjustedTotalUsed - currentModalMembers);
    console.log(`[DEBUG] FINAL Display available seats: total: ${totalPurchased}, totalUsed: ${totalUsed}, adjustedTotalUsed: ${adjustedTotalUsed}, currentModalMembers: ${currentModalMembers}, calculated: ${calculated}`);
    console.log(`[DEBUG] Props availableSeats value: ${props.availableSeats}, showCalendarTypeSelection: ${showCalendarTypeSelection}, isEditMode: ${props.isEditMode}`);
    
    // Always use our calculated value for accurate real-time updates
    // Don't rely on props.availableSeats as it may not update when members are added/removed
    console.log(`[DEBUG] Using calculated value: ${calculated}`);
    return calculated;
  };
  
  // Helper function to render the "Need more?" text
  const renderNeedMoreText = () => {
    console.log('[DEBUG] renderNeedMoreText called with accountType:', props.accountType);
    if (props.accountType === 'trial' || props.accountType === 'Trial') {
      return (
        <div style={{ fontSize: 11, color: '#0d47a1', marginTop: 2, whiteSpace: 'nowrap' }}>
          Need more? Visit{' '}
          <span 
            style={{ 
              textDecoration: 'underline', 
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
            onClick={() => {
              if (props.onBillingClick) {
                console.log('[DEBUG] Navigating to billing for trial user');
                props.onBillingClick();
              }
            }}
          >
            Upgrade Plan
          </span>
        </div>
      );
    } else {
      return (
        <div style={{ fontSize: 11, color: '#0d47a1', marginTop: 2, whiteSpace: 'nowrap' }}>
          Need more? Visit{' '}
          <span 
            style={{ 
              textDecoration: 'underline', 
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('[DEBUG] Clicking Manage Seats for non-trial user');
              handleOpenSeatsModal();
            }}
          >
            Manage Seats
          </span>
        </div>
      );
    }
  };

  // Phone slot slot registration helpers
  const isPhoneRegistrationRequested = (memberEmail) => {
    const isRequested = phoneRegistrationRequests[memberEmail] || false;
    console.log(`[DEBUG] isPhoneRegistrationRequested(${memberEmail}):`, isRequested, 'phoneRegistrationRequests:', phoneRegistrationRequests);
    return isRequested;
  };
  
  const togglePhoneRegistrationRequest = (memberEmail) => {
    clearCalendarAuthMessage(); // Clear message when toggling phone registration
    console.log(`[DEBUG] togglePhoneRegistrationRequest(${memberEmail}) - current state:`, phoneRegistrationRequests[memberEmail]);
    
    setPhoneRegistrationRequests(prev => {
      const newState = {
        ...prev,
        [memberEmail]: !prev[memberEmail]
      };
      console.log(`[DEBUG] Setting phoneRegistrationRequests to:`, newState);
      return newState;
    });
    
    // Clear registered phone number if unchecking
    if (phoneRegistrationRequests[memberEmail]) {
      console.log(`[DEBUG] Unchecking - clearing registered phone for ${memberEmail}`);
      unregisterPhoneNumber(memberEmail);
      
      // Clear local phone input state
      setLocalPhoneInputs(prev => {
        const updated = { ...prev };
        delete updated[memberEmail];
        return updated;
      });
    }
  };
  
  const isPhoneSlotRegistered = (memberEmail) => {
    return registeredPhoneNumbers[memberEmail] && registeredPhoneNumbers[memberEmail].trim() !== '';
  };
  
  const getRegisteredPhoneNumber = (memberEmail) => {
    const phone = registeredPhoneNumbers[memberEmail] || '';
    // Filter out default/invalid phone numbers
    if (phone === "0000000000" || !phone || phone.trim() === '') {
      return '';
    }
    return phone;
  };
  
  const registerPhoneNumber = (memberEmail, phoneNumber) => {
    setRegisteredPhoneNumbers(prev => ({
      ...prev,
      [memberEmail]: phoneNumber
    }));
  };
  
  const unregisterPhoneNumber = (memberEmail) => {
    setRegisteredPhoneNumbers(prev => {
      const updated = { ...prev };
      delete updated[memberEmail];
      return updated;
    });
  };
  
  const getUsedPhoneSlots = () => {
    return Object.values(registeredPhoneNumbers).filter(phone => 
      phone && phone.trim() !== '' && phone !== "0000000000"
    ).length;
  };
  
  const getAvailablePhoneSlots = () => {
    const used = getUsedPhoneSlots();
    const available = Math.max(0, totalPhoneSlots - used);
    console.log('[DEBUG] Phone slots calculation:', {
      totalPhoneSlots: totalPhoneSlots,
      usedSlots: used,
      availableSlots: available,
      registeredPhoneNumbers
    });
    console.log('[DEBUG] getAvailablePhoneSlots result:', available, 'totalPhoneSlots:', totalPhoneSlots, 'used:', used);
    return available;
  };

  // Returns the number of net-new seats added in the current editing session
  // (not yet saved to backend, so not yet counted by the availability API).
  const getDraftSeatAdjustment = () => {
    if (!props.members) return 0;
    const seen = new Set();
    let count = 0;
    props.members.forEach(member => {
      const email = (member.email || '').trim().toLowerCase();
      if (!email || seen.has(email)) return;
      seen.add(email);
      if ((member.role || '').toLowerCase() === 'admin') return;
      // Edit mode: only isNew members are unsaved; Add mode: all members are unsaved
      if (props.isEditMode && !member.isNew) return;
      count++;
    });
    return count;
  };

  // Returns the net change in phone-slot consumption for in-session edits.
  // Pass excludeEmail to ignore one member (used by the per-card visibility guard
  // so a member typing their own phone doesn't cause their own input to disappear).
  const getDraftPhoneAdjustment = (excludeEmail = null) => {
    if (!props.members) return 0;
    const seen = new Set();
    const excludeNorm = (excludeEmail || '').trim().toLowerCase();
    let delta = 0;
    props.members.forEach(member => {
      const email = (member.email || '').trim().toLowerCase();
      if (!email || seen.has(email)) return;
      seen.add(email);
      if (excludeNorm && email === excludeNorm) return;
      const backendPhone = (member.phone || '').replace(/\D/g, '').slice(-10);
      const hadPhoneBefore = backendPhone.length === 10;
      const draftRaw = (localPhoneInputs[email] || '').replace(/\D/g, '').slice(-10);
      const hasPhoneNow = draftRaw.length === 10;
      if (!hadPhoneBefore && hasPhoneNow) delta++;
      else if (hadPhoneBefore && !hasPhoneNow && localPhoneInputs[email] !== undefined) delta--;
    });
    return delta;
  };

  // Seats modal handlers
  const handleOpenSeatsModal = () => {
    setShowSeatsModal(true);
  };
  
  const handleCloseSeatsModal = () => {
    setShowSeatsModal(false);
  };
  
  const handleSeatsUpdate = (newSeatCount, updatedBilling) => {
    setCurrentSeats(newSeatCount);
    // Optionally trigger any other updates needed when seats change
    if (props.onSeatsUpdate) {
      props.onSeatsUpdate(newSeatCount, updatedBilling);
    }
  };
  
  // Save individual member data or send invite
  async function saveMemberData(memberIndex, forceInvite = false) {
    console.log(`[DEBUG] *** saveMemberData CALLED ***`);
    console.log(`[DEBUG] - memberIndex: ${memberIndex}`);
    console.log(`[DEBUG] - forceInvite: ${forceInvite}`);
    console.log(`[DEBUG] - props.members.length: ${props.members.length}`);
    console.log(`[DEBUG] - showCalendarTypeSelection: ${showCalendarTypeSelection}`);
    
    const member = props.members[memberIndex];
    console.log(`[DEBUG] - member:`, member);
    
    // Check if this is the first member (Admin/Agent) or subsequent members
    const isAdminAgent = memberIndex === 0;
    
    // For team connections, only the admin should create the connection with all members
    // Other members should just send invitations
    const isTeamConnection = ['small_team', 'medium_team', 'large_team'].includes(props.accountType);
    
    // Determine if this should be treated as a connection save vs invite send
    // In EDIT mode: Send Invite always sends invite (never saves connection)
    // In ADD mode: 
    //   - For teams: ONLY the admin (index 0) saves connection with all members, all others send invites
    //   - For non-teams: Admin/Agent with provider = connection save, others = invite send
    // If forceInvite is true, always send invite regardless of other logic
    const shouldSaveConnection = !forceInvite && !props.isEditMode && member.provider && (
      isTeamConnection ? memberIndex === 0 : isAdminAgent
    );
    
    // Debug logging to track what's happening
    console.log(`[TEAM DEBUG] Member ${memberIndex}:`, {
      email: member.email,
      isTeamConnection,
      isEditMode: props.isEditMode,
      hasProvider: !!member.provider,
      provider: member.provider,
      memberIndex,
      isAdminAgent,
      forceInvite,
      shouldSaveConnection
    });
    
    console.log(`[DEBUG] *** SAVE DECISION FOR MEMBER ${memberIndex} ***`);
    console.log(`[DEBUG] - shouldSaveConnection: ${shouldSaveConnection}`);
    console.log(`[DEBUG] - Will this member trigger save? ${shouldSaveConnection ? 'YES' : 'NO'}`);
    
    if (!shouldSaveConnection) {
      console.log(`[DEBUG] - SKIPPING SAVE because:`);
      console.log(`[DEBUG]   - forceInvite: ${forceInvite}`);
      console.log(`[DEBUG]   - isEditMode: ${props.isEditMode}`);
      console.log(`[DEBUG]   - member.provider: ${member.provider}`);
      console.log(`[DEBUG]   - isTeamConnection: ${isTeamConnection}`);
      console.log(`[DEBUG]   - memberIndex === 0: ${memberIndex === 0}`);
      console.log(`[DEBUG]   - isAdminAgent: ${isAdminAgent}`);
    }
    
    if (shouldSaveConnection) {
      // For Admin/Agent with provider: Save connection
      if (!member.email) {
        props.setMessage(`Please enter an email address for the Admin/Agent.`);
        return;
      }
      if (!member.first_name?.trim()) {
        props.setMessage(`Please enter a first name for the ${memberIndex === 0 ? 'Lead Agent' : 'Member'}.`);
        return;
      }
      if (!member.last_name?.trim()) {
        props.setMessage(`Please enter a last name for the ${memberIndex === 0 ? 'Lead Agent' : 'Member'}.`);
        return;
      }

      // Validate iCloud specific fields
      if (member.provider === 'icloud' && (!member.appleId || !member.appPassword)) {
        props.setMessage('Please fill in Apple ID and App-Specific Password for iCloud Admin/Agent.');
        return;
      }
      if (member.provider === 'icloud' && (!member.appleId || !member.appPassword)) {
        props.setMessage('Please fill in Apple ID and App-Specific Password for iCloud Admin/Agent.');
        return;
      }
      
      // Continue with existing save logic for Admin/Agent with provider
      props.setLoading(true);
      props.setMessage(null);
      
      try {
        // Resolve phone: prefer localPhoneInputs (what user typed), fall back to member.phone
        const resolvePhone = (m) => localPhoneInputs[m.email] || m.phone || "";

        const phoneNumber = resolvePhone(member);

        // Create enhanced member data with simplified phone storage
        const enhancedMember = {
          ...member,
          phone: phoneNumber,
          displayPhone: phoneNumber
        };

        // For team connections, include ALL members when the admin saves
        // For non-team connections, use only the individual member
        const membersToSave = isTeamConnection ?
          props.members.map((m) => ({
            ...m,
            phone: resolvePhone(m),
            displayPhone: resolvePhone(m)
          })) :
          [enhancedMember];
        
        // Transform form data to connection format
        // Pass company calendar flag to ensure proper seat counting
        // Company calendar flag should be true if the checkbox is checked, regardless of auth status
        const isCompanyCalendarConnection = showCalendarTypeSelection;
        console.log('[DEBUG] *** FINAL SAVE DEBUG ***');
        console.log('[DEBUG] At save time - showCalendarTypeSelection:', showCalendarTypeSelection);
        console.log('[DEBUG] At save time - isCompanyCalendarConnection:', isCompanyCalendarConnection);
        console.log('[DEBUG] At save time - authCompleted:', authCompleted);
        console.log('[DEBUG] At save time - member.provider:', member.provider);
        console.log('[DEBUG] At save time - checkbox element checked:', document.getElementById('calendar-type-checkbox')?.checked);
        const connectionData = dataService.transformToConnectionFormat(
          props.accountType,
          membersToSave,
          user,
          isCompanyCalendarConnection,
          teamName
        );
        console.log('[DEBUG] Connection data created:', {
          paid_users: connectionData.paid_users,
          is_company_calendar: connectionData.is_company_calendar,
          description: connectionData.description
        });
        
        // Continue with existing save logic...
        if (props.isEditMode && props.currentEditItem?.connection_id) {
          // Get the current connection data and update just this member - use connection_id
          const connectionId = props.currentEditItem.connection_id || props.currentEditItem.id;
          const updatedConnectionData = {
            ...connectionData,
            connection_id: connectionId,
            // Preserve actual ownership rather than the acting-user-derived owner/user_id
            // from transformToConnectionFormat — see the analogous fix in the "Save all
            // members" path above for why.
            owner: props.currentEditItem.owner,
            user_id: props.currentEditItem.user_id,
            members: props.members.map(m => m.email === member.email ? enhancedMember : m) // Update specific member
          };
          
          const result = await dataService.saveConnection(
            props.accountType,
            updatedConnectionData,
            true, // isEdit
            connectionId
          );
          
          if (result && result.success) {
            // Update the member with success state
            const updatedMembers = [...props.members];
            updatedMembers[memberIndex] = {
              ...updatedMembers[memberIndex],
              successMessage: `✅ Calendar connected successfully!`
            };
            props.setMembers(updatedMembers);
            
            // Clear success message after 3 seconds
            setTimeout(() => {
              const clearedMembers = [...props.members];
              if (clearedMembers[memberIndex]) {
                delete clearedMembers[memberIndex].successMessage;
                props.setMembers(clearedMembers);
              }
            }, 3000);
            
            // For Admin/Agent with provider: Don't close modal for Connect Calendar action
            // Modal should stay open to allow further interactions
          } else {
            props.setMessage(result.message || 'Failed to save Admin/Agent data. Please try again.');
          }
        } else {
          // For new connections, save the individual member as a new connection
          const result = await dataService.saveConnection(
            props.accountType,
            connectionData,
            false, // isEdit
            null
          );

          if (result && result.success) {
            // Update the member with success state
            const updatedMembers = [...props.members];
            updatedMembers[memberIndex] = {
              ...updatedMembers[memberIndex],
              successMessage: `✅ Calendar connected successfully!`
            };
            props.setMembers(updatedMembers);

            // Clear success message after 3 seconds
            setTimeout(() => {
              const clearedMembers = [...props.members];
              if (clearedMembers[memberIndex]) {
                delete clearedMembers[memberIndex].successMessage;
                props.setMembers(clearedMembers);
              }
            }, 3000);

            console.log('[DEBUG] Admin/Agent data saved successfully:', result);
          } else {
            props.setMessage(result.message || 'Failed to save Admin/Agent data. Please try again.');
          }
        }
        
      } catch (err) {
        props.setMessage('An error occurred while saving Admin/Agent data. Please try again.');
        console.error('Admin/Agent save error:', err);
      }
      
      props.setLoading(false);
      
    } else {
      // For Members: account invite is already sent via handleMemberLifecycle —
      // skip /send-member-invite to avoid a duplicate "Calendar Invitation from" email.
      if (forceInvite) return;

      // Legacy path (no provider set, non-team flow): send calendar connection invite
      if (!member.email) {
        props.setMessage('Please enter email for member.');
        return;
      }
      if (!member.first_name?.trim()) {
        props.setMessage('Please enter a first name for the member.');
        return;
      }
      if (!member.last_name?.trim()) {
        props.setMessage('Please enter a last name for the member.');
        return;
      }

      props.setMessage(null);

      try {
        const inviteData = {
          memberEmail: member.email,
          inviteeName: member.first_name || '',
          memberPhone: member.phone || '',
          adminEmail: props.members[0]?.email,
          companyName: user?.company || '',
          accountType: props.accountType,
          invitedBy: user?.email || props.members[0]?.email
        };

        const response = await apiClient.post('/send-member-invite', inviteData);

        if (response.data.success) {
          const updatedMembers = [...props.members];
          updatedMembers[memberIndex] = {
            ...updatedMembers[memberIndex],
            successMessage: `✅ Invite sent to ${member.email}! They will receive setup instructions.`,
            validationError: null
          };
          props.setMembers(updatedMembers);

          setTimeout(() => {
            const clearedMembers = [...props.members];
            if (clearedMembers[memberIndex]) {
              delete clearedMembers[memberIndex].successMessage;
              props.setMembers(clearedMembers);
            }
          }, 5000);
        } else {
          throw new Error(response.data.message || 'Failed to send invite');
        }

      } catch (err) {
        const updatedMembers = [...props.members];
        updatedMembers[memberIndex] = {
          ...updatedMembers[memberIndex],
          validationError: 'Failed to send invite. Please try again.',
          successMessage: null
        };
        props.setMembers(updatedMembers);
        console.error('Member invite error:', err);
      }
    }
  }

  // Selection screen
  if (!props.accountType) {
    return (
      <>
        <div style={{ background: '#fff', borderRadius: 4, padding: '16px' }}>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '18px', margin: 0 }}>
              {props.isEditMode ? 'Edit Connections' : 'Add Connections'} {props.accountType ? `(${props.accountType.charAt(0).toUpperCase() + props.accountType.slice(1)})` : ''}
            </h2>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginBottom: 20 }}>
            <Button3D
              style={{ minWidth: 120, fontSize: 16, padding: '10px 20px', borderRadius: 8, fontWeight: 600 }}
              onClick={() => props.setAccountType('solo')}
            >
              Solo
            </Button3D>
            <Button3D
              style={{ minWidth: 120, fontSize: 16, padding: '10px 20px', borderRadius: 8, fontWeight: 600 }}
              onClick={() => props.setAccountType('small_team')}
            >
              Team
            </Button3D>
          </div>
        </div>
      </>
    );
  }

  // Success screen
  if (props.showSuccess) {
    return (
      <>
        <div style={{ background: '#fff', borderRadius: 4, padding: '16px' }}>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '18px', margin: 0 }}>Registration Complete</h2>
          </div>
          {props.message && <div style={styles.message}>{props.message}</div>}
          {props.authText && <textarea readOnly style={styles.output} value={props.authText} />}
          {props.authCode && <p style={styles.success}>✅ Authorization Code Received!</p>}
          <Button3D
            onClick={props.onReset}
            style={{ width: 200, maxWidth: '90%', margin: '20px auto 0', display: 'block', padding: '8px 16px', fontSize: '14px' }}
          >
            Back to Selection
          </Button3D>
        </div>
      </>
    );
  }

// Main registration form - Updated for wider cards
  return (
    <>
      <style>
        {`
          .member-card-container:hover {
            transform: translateY(-1px);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.9) !important;
          }
          
          .calendar-connection-section:hover {
            transform: translateY(-0.5px);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.9) !important;
          }
          
          .enhanced-button:hover:not(:disabled) {
            transform: translateY(-3px) scale(1.05);
            box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25), 0 8px 16px rgba(0, 0, 0, 0.15), inset 0 2px 0 rgba(255, 255, 255, 0.4) !important;
            filter: brightness(1.1) saturate(1.1);
          }
          
          .enhanced-button:active:not(:disabled) {
            transform: translateY(1px) scale(0.95);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3), inset 0 3px 6px rgba(0, 0, 0, 0.15) !important;
            filter: brightness(0.95);
          }
          
          /* 3D Button Effects for Member Cards */
          .card-action-button {
            transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            position: relative;
            overflow: hidden;
          }
          
          .card-action-button:hover:not(:disabled) {
            transform: translateY(-4px) scale(1.08);
            box-shadow: 0 16px 40px rgba(0, 0, 0, 0.2), 0 8px 16px rgba(0, 0, 0, 0.15), inset 0 2px 0 rgba(255, 255, 255, 0.5) !important;
            filter: brightness(1.15) saturate(1.2);
          }
          
          .card-action-button:active:not(:disabled) {
            transform: translateY(1px) scale(0.95);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25), inset 0 3px 6px rgba(0, 0, 0, 0.15) !important;
            filter: brightness(0.9);
          }
          
          /* Remove Button Specific Effects */
          .remove-button:hover:not(:disabled) {
            transform: translateY(-5px) scale(1.12);
            box-shadow: 0 20px 50px rgba(239, 68, 68, 0.5), 0 10px 20px rgba(239, 68, 68, 0.3), inset 0 2px 0 rgba(255, 255, 255, 0.5) !important;
            filter: brightness(1.2) saturate(1.3);
          }
          
          .remove-button:active:not(:disabled) {
            transform: translateY(1px) scale(0.9);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4), inset 0 3px 6px rgba(0, 0, 0, 0.2) !important;
            filter: brightness(0.85);
          }
          
          /* Add Member Button Effects */
          .add-member-button:hover:not(:disabled) {
            transform: translateY(-4px) scale(1.1);
            box-shadow: 0 18px 45px rgba(34, 197, 94, 0.4), 0 10px 20px rgba(34, 197, 94, 0.25), inset 0 2px 0 rgba(255, 255, 255, 0.5) !important;
            filter: brightness(1.18) saturate(1.25);
          }
          
          .add-member-button:active:not(:disabled) {
            transform: translateY(1px) scale(0.92);
            box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4), inset 0 3px 6px rgba(0, 0, 0, 0.15) !important;
            filter: brightness(0.88);
          }
          
          /* Phone Registration Checkbox Effects */
          .phone-checkbox-container {
            transition: all 0.2s ease;
          }
          
          .phone-checkbox-container:hover {
            transform: translateY(-0.5px);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          }
          
          /* Simple Jump Effects for Send Invite and Remove Buttons */
          .jump-button {
            transition: all 0.2s ease-in-out !important;
          }
          
          .jump-button:hover:not(:disabled) {
            transform: translateY(-2px) !important;
            box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15) !important;
          }
          
          .jump-button:active:not(:disabled) {
            transform: translateY(0px) !important;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
          }
        `}
      </style>
      <div style={{ background: '#fff', borderRadius: 4 }}>
        {/* Title and close button moved from CalendarConnection.js */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '12px 16px',
          background: '#282c34da',
          color: 'white',
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h5 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>{props.title}</h5>
            {(() => {
              // TEMPORARY: Show for all users for testing
              // To restrict to team accounts, check user?.accountType?.toLowerCase() against team types
              const showTooltip = true;

              return showTooltip ? (
                <TeamNotificationTooltip placement="bottom">
                  <FaInfoCircle style={{ color: '#17a2b8', cursor: 'pointer', fontSize: '14px' }} />
                </TeamNotificationTooltip>
              ) : null;
            })()}
          </div>
          <button type="button" className="btn-close" style={{ filter: 'invert(1)' }} onClick={props.onClose}></button>
        </div>
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          
          {/* Company/Team Calendar Connection Section - only show if feature is enabled and not a Lead Agent managing their own team */}
          {isConnectCompanyCalendarEnabled() && !isOwnTeamAsLeadAgent && (
          <div 
            className="calendar-connection-section"
            style={{
              background: 'linear-gradient(145deg, #ffffff, #f8fafc)',
              border: '2px solid #e2e8f0',
              borderRadius: '16px',
              padding: '16px',
              marginBottom: '12px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease'
            }}>
            {/* Subtle highlight line at top */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #3b82f6)',
              borderRadius: '16px 16px 0 0'
            }}></div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', position: 'relative', zIndex: 1 }}>
              {/* Checkbox for Company/Team Calendar Type */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="calendar-type-checkbox"
                  checked={showCalendarTypeSelection}
                  disabled={userCalendarStatus !== 'connected'}
                  onChange={(e) => {
                    setShowCalendarTypeSelection(e.target.checked);
                    if (!e.target.checked && props.members.length > 0) {
                      const updatedMembers = [...props.members];
                      updatedMembers[0].provider = '';
                      props.setMembers(updatedMembers);
                      setAuthCompleted(false);
                    }
                  }}
                  style={{
                    marginRight: '8px',
                    width: '16px',
                    height: '16px',
                    cursor: userCalendarStatus === 'connected' ? 'pointer' : 'not-allowed',
                    opacity: userCalendarStatus === 'connected' ? 1 : 0.5,
                  }}
                />
                <label
                  htmlFor="calendar-type-checkbox"
                  style={{
                    fontWeight: '500',
                    cursor: userCalendarStatus === 'connected' ? 'pointer' : 'not-allowed',
                    userSelect: 'none',
                    fontSize: '15px',
                    color: userCalendarStatus !== 'connected'
                      ? '#9ca3af'
                      : showCalendarTypeSelection ? '#2563eb' : '#374151',
                  }}
                >
                  Connect Company/Brokerage Calendar
                </label>
              </div>
              
              {/* Status hint below checkbox */}
              {userCalendarStatus === 'loading' && (
                <p style={{ margin: '0 0 0 24px', fontSize: '12px', color: '#9ca3af' }}>
                  {isAdminDelegate ? "Checking the Account Owner's calendar status…" : 'Checking your calendar status…'}
                </p>
              )}
              {userCalendarStatus === 'unconnected' && (
                <p style={{ margin: '0 0 0 24px', fontSize: '12px', color: '#f59e0b' }}>
                  ⚠ {isAdminDelegate
                    ? 'The Account Owner must connect their calendar first to enable this option.'
                    : 'Connect company calendar first to enable this option.'}
                </p>
              )}
              {userCalendarStatus === 'connected' && showCalendarTypeSelection && (
                <p style={{ margin: '0 0 0 24px', fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ color: '#10b981' }}>✓</span>
                  Will use {isAdminDelegate ? "the Account Owner's" : 'your'}{' '}
                  <strong>
                    {userCalendarProvider === 'google' ? 'Google'
                      : userCalendarProvider === 'outlook' ? 'Outlook'
                      : 'iCloud'} Calendar
                  </strong>
                  &nbsp;connection.
                </p>
              )}
              
            </div>
          </div>
          )}
          
          {/* Team Name field — shown only for team account types */}
          {['small_team', 'medium_team', 'large_team'].includes(props.accountType) && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label style={{
                  flexShrink: 0,
                  minWidth: '90px',
                  fontWeight: '600',
                  fontSize: '13px',
                  color: '#374151',
                }}>
                  Team Name
                </label>
                <input
                  type="text"
                  value={teamName}
                  readOnly={isOwnTeamAsLeadAgent}
                  disabled={isOwnTeamAsLeadAgent}
                  onChange={e => {
                    setTeamName(e.target.value);
                    if (teamNameError) setTeamNameError('');
                  }}
                  onBlur={() => {
                    const savedTeamName = (props.currentEditItem?.team_name || '').trim();
                    const teamNameRequired = !props.isEditMode || savedTeamName !== '';
                    if (teamNameRequired && !teamName.trim()) setTeamNameError('Team name is required.');
                  }}
                  maxLength={80}
                  placeholder="e.g. Halifax Closing Team"
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${teamNameError ? '#ef4444' : '#d1d5db'}`,
                    fontSize: '14px',
                    outline: 'none',
                    background: isOwnTeamAsLeadAgent ? '#f1f5f9' : '#ffffff',
                    color: isOwnTeamAsLeadAgent ? '#64748b' : '#111827',
                    cursor: isOwnTeamAsLeadAgent ? 'not-allowed' : 'text',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              {teamNameError && (
                <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginLeft: '102px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <FaExclamationTriangle style={{ fontSize: '11px' }} />
                  {teamNameError}
                </div>
              )}
            </div>
          )}

          {props.members.map((member, idx) => (
            <div
              className="member-card-container"
              style={{
                background: member.suspended
                  ? 'linear-gradient(145deg, #fdf6e3, #fef3c7)'
                  : 'linear-gradient(145deg, #ffffff, #f9fafb)',
                border: member.suspended ? '2px solid #fcd34d' : '2px solid #e5e7eb',
                borderRadius: '16px',
                padding: '16px 20px',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.06), 0 3px 6px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                width: '100%',
                opacity: member.suspended ? 0.82 : 1,
              }}
              key={idx}
            >
              {/* Member type indicator */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: idx === 0 
                  ? 'linear-gradient(90deg, #f59e0b, #f97316)' // Admin - orange gradient
                  : 'linear-gradient(90deg, #3b82f6, #6366f1)', // Member - blue gradient
                borderRadius: '16px 16px 0 0'
              }}></div>
              
              {/* Subtle inner glow */}
              <div style={{
                position: 'absolute',
                top: '3px',
                left: '2px',
                right: '2px',
                bottom: '2px',
                background: 'linear-gradient(145deg, rgba(255,255,255,0.4), rgba(255,255,255,0.1))',
                borderRadius: '14px',
                pointerEvents: 'none',
                zIndex: 0
              }}></div>
              
              <div style={{ position: 'relative', zIndex: 1 }}>
              {/* Add member title if multiple members */}
              {(props.accountType === "small_team" || props.accountType === "medium_team" || props.accountType === "large_team") && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '-16px -20px 8px -20px', padding: '6px 16px', background: idx === 0 ? '#fef3c7' : (member.suspended ? '#fef9ec' : '#eff6ff'), borderBottom: `1px solid ${idx === 0 ? '#fcd34d' : (member.suspended ? '#fcd34d' : '#bfdbfe')}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TeamNotificationTooltip
                      placement="bottom"
                      role={idx === 0 ? 'admin' : 'member'}
                    >
                      <span className="role-badge" style={{
                        color: idx === 0 ? '#92400e' : '#1d4ed8',
                        fontSize: '14px',
                        fontWeight: '700',
                        cursor: 'help',
                      }}>
                        {idx === 0 ? 'Lead Agent' : 'Member'}
                      </span>
                    </TeamNotificationTooltip>
                    {member.suspended && (
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#92400e', background: '#fde68a', borderRadius: '6px', padding: '2px 7px', border: '1px solid #fbbf24', letterSpacing: '0.3px' }}>
                        Suspended
                      </span>
                    )}
                    <Button3D
                      onClick={() => togglePermissions(idx)}
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: 'linear-gradient(145deg, #3b82f6, #2563eb)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3), 0 2px 4px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.2)',
                        transition: 'all 0.2s ease',
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      }}
                    >
                      {expandedPermissions.has(idx) ? 'Permissions ▴' : 'Permissions ▾'}
                    </Button3D>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {!member.isNew && !member.suspended && (
                      <button
                        style={{ background: 'linear-gradient(135deg, #faf5ff, #ede9fe)', border: '1px solid #c4b5fd', borderRadius: 6, padding: '5px 12px', fontSize: 13, color: '#6d28d9', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.2s ease-in-out', boxShadow: '0 2px 4px rgba(124,58,237,0.15)', transform: 'translateY(0)', fontWeight: 500 }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #7c3aed, #6d28d9)'; e.currentTarget.style.borderColor = '#6d28d9'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = '0 4px 8px rgba(124,58,237,0.35)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #faf5ff, #ede9fe)'; e.currentTarget.style.borderColor = '#c4b5fd'; e.currentTarget.style.color = '#6d28d9'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(124,58,237,0.15)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        onMouseDown={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(124,58,237,0.2)'; e.currentTarget.style.transform = 'translateY(1px)'; }}
                        onMouseUp={e => { e.currentTarget.style.boxShadow = '0 4px 8px rgba(124,58,237,0.35)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onClick={() => openDirectTransferMemberModal(member, idx)}
                      >
                        <FaArrowRight style={{ fontSize: 11 }} /> Transfer
                      </button>
                    )}
                    {idx === 0 && !isLeadAgentManager && !member.isNew && props.isEditMode && (
                      <button
                        onClick={openChangeLeadAgentModal}
                        style={{ background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)', border: '1px solid #6ee7b7', borderRadius: 6, padding: '5px 12px', fontSize: 13, color: '#065f46', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.2s ease-in-out', boxShadow: '0 2px 4px rgba(16,185,129,0.15)', transform: 'translateY(0)', fontWeight: 500 }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #059669, #047857)'; e.currentTarget.style.borderColor = '#047857'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = '0 4px 8px rgba(16,185,129,0.35)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #ecfdf5, #d1fae5)'; e.currentTarget.style.borderColor = '#6ee7b7'; e.currentTarget.style.color = '#065f46'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(16,185,129,0.15)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        onMouseDown={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(16,185,129,0.2)'; e.currentTarget.style.transform = 'translateY(1px)'; }}
                        onMouseUp={e => { e.currentTarget.style.boxShadow = '0 4px 8px rgba(16,185,129,0.35)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      >
                        Change Lead Agent
                      </button>
                    )}
                    {idx > 0 && (
                      <>
                        {!member.isNew && (member.suspended ? (
                          <Button3D
                            style={{
                              padding: '6px 12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              background: 'linear-gradient(145deg, #22c55e, #16a34a)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '10px',
                              boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3), 0 2px 4px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.2)',
                              transition: 'all 0.2s ease',
                              textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                              display: 'flex', alignItems: 'center', gap: '5px',
                            }}
                            onClick={() => openReactivateMemberModal(member, idx)}
                          >
                            <FaPlay style={{ fontSize: '10px' }} /> Reactivate
                          </Button3D>
                        ) : (
                          <button
                            onClick={() => openSuspendMemberModal(member, idx)}
                            style={{
                              background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
                              border: '1px solid #fcd34d',
                              borderRadius: 6, padding: '5px 12px', fontSize: 13,
                              color: '#92400e', cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              transition: 'all 0.2s ease-in-out',
                              boxShadow: '0 2px 4px rgba(217,119,6,0.15)',
                              transform: 'translateY(0)', fontWeight: 500,
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #d97706, #b45309)';
                              e.currentTarget.style.borderColor = '#b45309';
                              e.currentTarget.style.color = '#fff';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(217,119,6,0.35)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #fffbeb, #fef3c7)';
                              e.currentTarget.style.borderColor = '#fcd34d';
                              e.currentTarget.style.color = '#92400e';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(217,119,6,0.15)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                            onMouseDown={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(217,119,6,0.2)'; e.currentTarget.style.transform = 'translateY(1px)'; }}
                            onMouseUp={e => { e.currentTarget.style.boxShadow = '0 4px 8px rgba(217,119,6,0.35)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                          >
                            <FaPause style={{ fontSize: 10 }} />
                            Suspend
                          </button>
                        ))}
                        <button
                          style={{ background: 'linear-gradient(135deg, #fee2e2, #fecaca)', border: '1px solid #fca5a5', borderRadius: 6, padding: '5px 12px', fontSize: 13, color: '#b91c1c', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.2s ease-in-out', boxShadow: '0 2px 4px rgba(239,68,68,0.2)', transform: 'translateY(0)', fontWeight: 500 }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444, #dc2626)'; e.currentTarget.style.borderColor = '#dc2626'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = '0 4px 8px rgba(239,68,68,0.4)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #fee2e2, #fecaca)'; e.currentTarget.style.borderColor = '#fca5a5'; e.currentTarget.style.color = '#b91c1c'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(239,68,68,0.2)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                          onMouseDown={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(239,68,68,0.3)'; e.currentTarget.style.transform = 'translateY(1px)'; }}
                          onMouseUp={e => { e.currentTarget.style.boxShadow = '0 4px 8px rgba(239,68,68,0.4)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                          onClick={() => openRemoveMemberModal(member, idx)}
                        >
                          <FaTrash style={{ fontSize: 11 }} /> Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Add member title for Solo */}
              {(props.accountType === "solo" || props.accountType === "Solo" || props.accountType === "trial" || props.accountType === "Trial") && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '-16px -20px 8px -20px', padding: '6px 16px', background: idx === 0 ? '#f5f3ff' : (member.suspended ? '#fef9ec' : '#eff6ff'), borderBottom: `1px solid ${idx === 0 ? '#c4b5fd' : (member.suspended ? '#fcd34d' : '#bfdbfe')}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TeamNotificationTooltip
                      placement="bottom"
                      role={idx === 0 ? 'agent' : 'member'}
                    >
                      <span className="role-badge" style={{
                        color: idx === 0 ? '#6d28d9' : '#1d4ed8',
                        fontSize: '12px',
                        fontWeight: '700',
                        cursor: 'help',
                      }}>
                        {idx === 0 ? 'Lead Agent' : 'Member'}
                      </span>
                    </TeamNotificationTooltip>
                    {member.suspended && (
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#92400e', background: '#fde68a', borderRadius: '6px', padding: '2px 7px', border: '1px solid #fbbf24', letterSpacing: '0.3px' }}>
                        Suspended
                      </span>
                    )}
                    <Button3D
                      onClick={() => togglePermissions(idx)}
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: 'linear-gradient(145deg, #3b82f6, #2563eb)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3), 0 2px 4px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.2)',
                        transition: 'all 0.2s ease',
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      }}
                    >
                      {expandedPermissions.has(idx) ? 'Permissions ▴' : 'Permissions ▾'}
                    </Button3D>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {!member.isNew && !member.suspended && (
                    <button
                      style={{ background: 'linear-gradient(135deg, #faf5ff, #ede9fe)', border: '1px solid #c4b5fd', borderRadius: 6, padding: '5px 12px', fontSize: 13, color: '#6d28d9', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.2s ease-in-out', boxShadow: '0 2px 4px rgba(124,58,237,0.15)', transform: 'translateY(0)', fontWeight: 500 }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #7c3aed, #6d28d9)'; e.currentTarget.style.borderColor = '#6d28d9'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = '0 4px 8px rgba(124,58,237,0.35)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #faf5ff, #ede9fe)'; e.currentTarget.style.borderColor = '#c4b5fd'; e.currentTarget.style.color = '#6d28d9'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(124,58,237,0.15)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                      onMouseDown={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(124,58,237,0.2)'; e.currentTarget.style.transform = 'translateY(1px)'; }}
                      onMouseUp={e => { e.currentTarget.style.boxShadow = '0 4px 8px rgba(124,58,237,0.35)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onClick={() => openDirectTransferMemberModal(member, idx)}
                    >
                      <FaArrowRight style={{ fontSize: 11 }} /> Transfer
                    </button>
                  )}
                  {idx > 0 && (
                    <>
                      {!member.isNew && (member.suspended ? (
                        <Button3D
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: 'linear-gradient(145deg, #22c55e, #16a34a)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3), 0 2px 4px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.2)',
                            transition: 'all 0.2s ease',
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                            display: 'flex', alignItems: 'center', gap: '5px',
                          }}
                          onClick={() => openReactivateMemberModal(member, idx)}
                        >
                          <FaPlay style={{ fontSize: '10px' }} /> Reactivate
                        </Button3D>
                      ) : (
                        <button
                          onClick={() => openSuspendMemberModal(member, idx)}
                          style={{
                            background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
                            border: '1px solid #fcd34d',
                            borderRadius: 6, padding: '5px 12px', fontSize: 13,
                            color: '#92400e', cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            transition: 'all 0.2s ease-in-out',
                            boxShadow: '0 2px 4px rgba(217,119,6,0.15)',
                            transform: 'translateY(0)', fontWeight: 500,
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #d97706, #b45309)';
                            e.currentTarget.style.borderColor = '#b45309';
                            e.currentTarget.style.color = '#fff';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(217,119,6,0.35)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #fffbeb, #fef3c7)';
                            e.currentTarget.style.borderColor = '#fcd34d';
                            e.currentTarget.style.color = '#92400e';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(217,119,6,0.15)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                          onMouseDown={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(217,119,6,0.2)'; e.currentTarget.style.transform = 'translateY(1px)'; }}
                          onMouseUp={e => { e.currentTarget.style.boxShadow = '0 4px 8px rgba(217,119,6,0.35)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        >
                          <FaPause style={{ fontSize: 10 }} />
                          Suspend
                        </button>
                      ))}
                      <button
                        style={{ background: 'linear-gradient(135deg, #fee2e2, #fecaca)', border: '1px solid #fca5a5', borderRadius: 6, padding: '5px 12px', fontSize: 13, color: '#b91c1c', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.2s ease-in-out', boxShadow: '0 2px 4px rgba(239,68,68,0.2)', transform: 'translateY(0)', fontWeight: 500 }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444, #dc2626)'; e.currentTarget.style.borderColor = '#dc2626'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = '0 4px 8px rgba(239,68,68,0.4)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #fee2e2, #fecaca)'; e.currentTarget.style.borderColor = '#fca5a5'; e.currentTarget.style.color = '#b91c1c'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(239,68,68,0.2)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        onMouseDown={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(239,68,68,0.3)'; e.currentTarget.style.transform = 'translateY(1px)'; }}
                        onMouseUp={e => { e.currentTarget.style.boxShadow = '0 4px 8px rgba(239,68,68,0.4)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onClick={() => openRemoveMemberModal(member, idx)}
                      >
                        <FaTrash style={{ fontSize: 11 }} /> Remove
                      </button>
                    </>
                  )}
                  </div>
                </div>
              )}

              {/* Add member title for Solo + Assistant */}
              {props.accountType === "solo_assistant" && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '-16px -20px 8px -20px', padding: '6px 16px', background: idx === 0 ? '#f5f3ff' : (member.suspended ? '#fef9ec' : '#ecfeff'), borderBottom: `1px solid ${idx === 0 ? '#c4b5fd' : (member.suspended ? '#fcd34d' : '#a5f3fc')}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TeamNotificationTooltip
                      placement="bottom"
                      role={idx === 0 ? 'agent' : 'assistant'}
                    >
                      <span className="role-badge" style={{
                        color: idx === 0 ? '#6d28d9' : '#0e7490',
                        fontSize: '14px',
                        fontWeight: '700',
                        cursor: 'help',
                      }}>
                        {idx === 0 ? 'Agent' : 'Assistant'}
                      </span>
                    </TeamNotificationTooltip>
                    {member.suspended && (
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#92400e', background: '#fde68a', borderRadius: '6px', padding: '2px 7px', border: '1px solid #fbbf24', letterSpacing: '0.3px' }}>
                        Suspended
                      </span>
                    )}
                    <Button3D
                      onClick={() => togglePermissions(idx)}
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: 'linear-gradient(145deg, #3b82f6, #2563eb)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3), 0 2px 4px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.2)',
                        transition: 'all 0.2s ease',
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      }}
                    >
                      {expandedPermissions.has(idx) ? 'Permissions ▴' : 'Permissions ▾'}
                    </Button3D>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {!member.isNew && !member.suspended && (
                      <button
                        style={{ background: 'linear-gradient(135deg, #faf5ff, #ede9fe)', border: '1px solid #c4b5fd', borderRadius: 6, padding: '5px 12px', fontSize: 13, color: '#6d28d9', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.2s ease-in-out', boxShadow: '0 2px 4px rgba(124,58,237,0.15)', transform: 'translateY(0)', fontWeight: 500 }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #7c3aed, #6d28d9)'; e.currentTarget.style.borderColor = '#6d28d9'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = '0 4px 8px rgba(124,58,237,0.35)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #faf5ff, #ede9fe)'; e.currentTarget.style.borderColor = '#c4b5fd'; e.currentTarget.style.color = '#6d28d9'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(124,58,237,0.15)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        onMouseDown={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(124,58,237,0.2)'; e.currentTarget.style.transform = 'translateY(1px)'; }}
                        onMouseUp={e => { e.currentTarget.style.boxShadow = '0 4px 8px rgba(124,58,237,0.35)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onClick={() => openDirectTransferMemberModal(member, idx)}
                      >
                        <FaArrowRight style={{ fontSize: 11 }} /> Transfer
                      </button>
                    )}
                    {idx > 0 && (
                      <>
                        {!member.isNew && (member.suspended ? (
                          <Button3D
                            style={{
                              padding: '6px 12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              background: 'linear-gradient(145deg, #22c55e, #16a34a)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '10px',
                              boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3), 0 2px 4px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.2)',
                              transition: 'all 0.2s ease',
                              textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                              display: 'flex', alignItems: 'center', gap: '5px',
                            }}
                            onClick={() => openReactivateMemberModal(member, idx)}
                          >
                            <FaPlay style={{ fontSize: '10px' }} /> Reactivate
                          </Button3D>
                        ) : (
                          <button
                            onClick={() => openSuspendMemberModal(member, idx)}
                            style={{
                              background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
                              border: '1px solid #fcd34d',
                              borderRadius: 6, padding: '5px 12px', fontSize: 13,
                              color: '#92400e', cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              transition: 'all 0.2s ease-in-out',
                              boxShadow: '0 2px 4px rgba(217,119,6,0.15)',
                              transform: 'translateY(0)', fontWeight: 500,
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #d97706, #b45309)';
                              e.currentTarget.style.borderColor = '#b45309';
                              e.currentTarget.style.color = '#fff';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(217,119,6,0.35)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #fffbeb, #fef3c7)';
                              e.currentTarget.style.borderColor = '#fcd34d';
                              e.currentTarget.style.color = '#92400e';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(217,119,6,0.15)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                            onMouseDown={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(217,119,6,0.2)'; e.currentTarget.style.transform = 'translateY(1px)'; }}
                            onMouseUp={e => { e.currentTarget.style.boxShadow = '0 4px 8px rgba(217,119,6,0.35)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                          >
                            <FaPause style={{ fontSize: 10 }} />
                            Suspend
                          </button>
                        ))}
                        <button
                          style={{ background: 'linear-gradient(135deg, #fee2e2, #fecaca)', border: '1px solid #fca5a5', borderRadius: 6, padding: '5px 12px', fontSize: 13, color: '#b91c1c', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.2s ease-in-out', boxShadow: '0 2px 4px rgba(239,68,68,0.2)', transform: 'translateY(0)', fontWeight: 500 }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444, #dc2626)'; e.currentTarget.style.borderColor = '#dc2626'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = '0 4px 8px rgba(239,68,68,0.4)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #fee2e2, #fecaca)'; e.currentTarget.style.borderColor = '#fca5a5'; e.currentTarget.style.color = '#b91c1c'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(239,68,68,0.2)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                          onMouseDown={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(239,68,68,0.3)'; e.currentTarget.style.transform = 'translateY(1px)'; }}
                          onMouseUp={e => { e.currentTarget.style.boxShadow = '0 4px 8px rgba(239,68,68,0.4)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                          onClick={() => openRemoveMemberModal(member, idx)}
                        >
                          <FaTrash style={{ fontSize: 11 }} /> Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Form fields — disabled overlay for suspended members */}
              <div style={{ position: 'relative' }}>
              {member.suspended && (
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(254, 249, 236, 0.45)',
                  zIndex: 5,
                  borderRadius: '4px',
                  cursor: 'not-allowed',
                }} />
              )}
              {/* First name + Last name — same grid layout as signup */}
              {(() => {
                const hasEmail = !!member.email?.trim();
                const missingFirst = hasEmail && !member.first_name?.trim();
                const missingLast  = hasEmail && !member.last_name?.trim();
                return (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                      <input
                        type="text"
                        placeholder="First name"
                        value={member.first_name || ''}
                        onChange={e => {
                          const updated = [...props.members];
                          updated[idx] = { ...updated[idx], first_name: e.target.value };
                          props.setMembers(updated);
                        }}
                        style={{
                          ...styles.input,
                          marginBottom: 0,
                          padding: '6px 10px',
                          fontSize: '16px',
                          borderColor: missingFirst ? '#ef4444' : styles.input.borderColor
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Last name"
                        value={member.last_name || ''}
                        onChange={e => {
                          const updated = [...props.members];
                          updated[idx] = { ...updated[idx], last_name: e.target.value };
                          props.setMembers(updated);
                        }}
                        style={{
                          ...styles.input,
                          marginBottom: 0,
                          padding: '6px 10px',
                          fontSize: '16px',
                          borderColor: missingLast ? '#ef4444' : styles.input.borderColor
                        }}
                      />
                    </div>
                  </>
                );
              })()}

              {/* Email — full width, placeholder only */}
              <div style={{ marginBottom: '8px' }}>
                {(isOwnTeamAsLeadAgent && idx === 0) || (props.isEditMode && !member.isNew) ? (
                  <input
                    type="email"
                    value={member.email}
                    readOnly
                    disabled
                    style={{
                      ...styles.input,
                      marginTop: 0,
                      marginBottom: 0,
                      padding: '6px 10px',
                      fontSize: '16px',
                      background: '#f1f5f9',
                      color: '#64748b',
                      cursor: 'not-allowed',
                      borderColor: '#e2e8f0',
                      width: '100%',
                    }}
                  />
                ) : (
                  <input
                    type="email"
                    placeholder="Email address"
                    value={member.email}
                    onFocus={clearCalendarAuthMessage}
                    onChange={e => {
                      clearCalendarAuthMessage();
                      const email = e.target.value;
                      const updated = [...props.members];
                      const oldEmail = updated[idx].email;
                      updated[idx].email = email;
                      props.setMembers(updated);

                      if (oldEmail !== email) {
                        const oldPhoneRequest = phoneRegistrationRequests[oldEmail];
                        const oldLocalPhoneInput = localPhoneInputs[oldEmail];
                        const oldRegisteredPhone = registeredPhoneNumbers[oldEmail];

                        if (oldPhoneRequest) {
                          setPhoneRegistrationRequests(prev => {
                            const newState = { ...prev };
                            delete newState[oldEmail];
                            if (email) newState[email] = oldPhoneRequest;
                            return newState;
                          });
                        }
                        if (oldLocalPhoneInput) {
                          setLocalPhoneInputs(prev => {
                            const newState = { ...prev };
                            delete newState[oldEmail];
                            if (email) newState[email] = oldLocalPhoneInput;
                            return newState;
                          });
                        }
                        if (oldRegisteredPhone) {
                          setRegisteredPhoneNumbers(prev => {
                            const newState = { ...prev };
                            delete newState[oldEmail];
                            if (email) newState[email] = oldRegisteredPhone;
                            return newState;
                          });
                        }
                        setEmailValidation(prev => {
                          const newState = { ...prev };
                          delete newState[oldEmail];
                          return newState;
                        });
                        setPhoneValidation(prev => {
                          const newState = { ...prev };
                          delete newState[oldEmail];
                          if (email && oldLocalPhoneInput) {
                            newState[email] = validatePhone(oldLocalPhoneInput);
                          }
                          return newState;
                        });
                      }

                      if (email) updateEmailValidation(email, email);
                    }}
                    onBlur={() => {
                      if (!member.email) return;
                      updateEmailValidation(member.email, member.email);
                    }}
                    style={{
                      ...styles.input,
                      marginTop: 0,
                      marginBottom: 0,
                      padding: '6px 10px',
                      fontSize: '16px',
                      borderColor: (emailValidation[member.email]?.isValid === false || isDuplicateEmail(member.email)) ? '#ef4444' : styles.input.borderColor
                    }}
                  />
                )}
                {!isOwnTeamAsLeadAgent && !(props.isEditMode && !member.isNew) && isDuplicateEmail(member.email) && (
                  <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FaExclamationTriangle />
                    This person is already added to this team.
                  </div>
                )}
                {!isOwnTeamAsLeadAgent && !(props.isEditMode && !member.isNew) && !isDuplicateEmail(member.email) && emailValidation[member.email]?.isValid === false && (
                  <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FaExclamationTriangle />
                    {emailValidation[member.email]?.message}
                  </div>
                )}
              </div>
              {/* Phone registration — compact inline */}
              {FEATURE_FLAGS.PHONE_FEATURE_ENABLED && (() => {
                const registeredPhone = getRegisteredPhoneNumber(member.email);
                const isRegistered = isPhoneSlotRegistered(member.email) && registeredPhone && registeredPhone.length >= 10;
                // Use account-level availability minus other members' draft changes.
                // Exclude this member so typing their own phone doesn't hide their own input.
                const phoneAvailCount = availability.phoneAvailable !== null
                  ? Math.max(0, availability.phoneAvailable - getDraftPhoneAdjustment(member.email))
                  : getAvailablePhoneSlots();
                const planPhoneTotal = availability.phoneTotal ?? totalPhoneSlots;
                // Hide when no plan slots, or all slots are used and this member has no phone yet
                if (planPhoneTotal === 0 || (!isRegistered && phoneAvailCount === 0)) return null;
                const phoneValue = localPhoneInputs[member.email] ?? (isRegistered ? registeredPhone.replace(/\D/g, '').slice(-10) : '');

                return (
                  <div style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px', minHeight: '30px' }}>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '15px', color: isRegistered ? '#15803d' : '#6b7280', whiteSpace: 'nowrap' }}>📞</span>
                      <IMaskInput
                        id={`phone-${idx}`}
                        mask="(000) 000-0000"
                        unmask={true}
                        placeholder="(000) 000-0000"
                        value={phoneValue}
                        onFocus={clearCalendarAuthMessage}
                        onAccept={raw => {
                          clearCalendarAuthMessage();
                          setLocalPhoneInputs(prev => ({ ...prev, [member.email]: raw }));
                          updatePhoneValidation(member.email, raw);
                          if (raw && raw.length === 10) {
                            const formattedNumber = `+1 ${raw.substring(0,3)}-${raw.substring(3,6)}-${raw.substring(6)}`;
                            registerPhoneNumber(member.email, formattedNumber);
                          } else if (isPhoneSlotRegistered(member.email)) {
                            unregisterPhoneNumber(member.email);
                          }
                        }}
                        onBlur={() => updatePhoneValidation(member.email, phoneValue)}
                        style={{
                          padding: '6px 10px', fontSize: '14px', width: '140px',
                          border: `1px solid ${phoneValidation[member.email]?.isValid === false ? '#ef4444' : isRegistered ? '#6ee7b7' : '#d1d5db'}`,
                          borderRadius: '6px', background: '#fff', outline: 'none', boxSizing: 'border-box',
                          color: isRegistered ? '#15803d' : '#111827', fontWeight: isRegistered ? '600' : 'normal'
                        }}
                      />
                      {phoneValidation[member.email]?.isValid === false && (
                        <span style={{ fontSize: '11px', color: '#ef4444' }}>{phoneValidation[member.email]?.message}</span>
                      )}
                    </div>
                  </div>
                );
              })()}
                  
              {/* Provider selection for each member */}
              <div style={{ marginTop: '0' }}>
              </div>
              
              {/* Display member-specific validation messages or success messages */}
              {/* Member feature permissions — collapsed by default, toggled via Permissions button */}
              {expandedPermissions.has(idx) && (() => {
                const lockPerms = isOwnTeamAsLeadAgent && idx === 0;
                return (
                <div style={{
                  marginTop: '12px',
                  padding: '10px 14px',
                  background: lockPerms ? '#f8fafc' : '#f8fafc',
                  borderRadius: '8px',
                  border: `1px solid ${lockPerms ? '#e2e8f0' : '#e2e8f0'}`,
                  opacity: lockPerms ? 0.7 : 1,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Member Permissions
                    </div>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: lockPerms ? 'default' : 'pointer', marginBottom: '6px' }}>
                    <input
                      type="checkbox"
                      checked={member.task_due_email_reminder !== false}
                      disabled={lockPerms}
                      onChange={lockPerms ? undefined : e => {
                        const updated = [...props.members];
                        updated[idx] = { ...updated[idx], task_due_email_reminder: e.target.checked };
                        props.setMembers(updated);
                      }}
                      style={{ cursor: lockPerms ? 'not-allowed' : 'pointer', accentColor: '#3b82f6' }}
                    />
                    <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>Due Task Reminder Emails</span>
                    <TeamNotificationTooltip placement="top" role="taskReminder" wrapperStyle={{ lineHeight: 1 }}>
                      <span style={{ color: '#94a3b8', fontSize: '12px', cursor: 'help' }}>ℹ</span>
                    </TeamNotificationTooltip>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: lockPerms ? 'default' : 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={member.personal_calendar_sync !== false}
                      disabled={lockPerms}
                      onChange={lockPerms ? undefined : e => {
                        const updated = [...props.members];
                        updated[idx] = { ...updated[idx], personal_calendar_sync: e.target.checked };
                        props.setMembers(updated);
                      }}
                      style={{ cursor: lockPerms ? 'not-allowed' : 'pointer', accentColor: '#3b82f6' }}
                    />
                    <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>Allow Personal Calendar Connection</span>
                    <TeamNotificationTooltip placement="top" role="calendarSync" wrapperStyle={{ lineHeight: 1 }}>
                      <span style={{ color: '#94a3b8', fontSize: '12px', cursor: 'help' }}>ℹ</span>
                    </TeamNotificationTooltip>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: lockPerms ? 'default' : 'pointer', marginTop: '6px' }}>
                    <input
                      type="checkbox"
                      checked={idx === 0 ? (member.permissions?.can_delete_task_lists ?? member.can_delete_task_lists) !== false : (member.permissions?.can_delete_task_lists ?? member.can_delete_task_lists) === true}
                      disabled={lockPerms}
                      onChange={lockPerms ? undefined : e => {
                        const updated = [...props.members];
                        updated[idx] = { ...updated[idx], permissions: { ...updated[idx].permissions, can_delete_task_lists: e.target.checked } };
                        props.setMembers(updated);
                      }}
                      style={{ cursor: lockPerms ? 'not-allowed' : 'pointer', accentColor: '#3b82f6' }}
                    />
                    <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>Can Delete Task Lists</span>
                    <TeamNotificationTooltip placement="top" role="deleteTaskLists" wrapperStyle={{ lineHeight: 1 }}>
                      <span style={{ color: '#94a3b8', fontSize: '12px', cursor: 'help' }}>ℹ</span>
                    </TeamNotificationTooltip>
                  </label>
                  {idx === 0 && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: lockPerms ? 'default' : 'pointer', marginTop: '6px' }}>
                      <input
                        type="checkbox"
                        checked={(member.permissions?.can_archive_task_lists ?? member.can_archive_task_lists) !== false}
                        disabled={lockPerms}
                        onChange={lockPerms ? undefined : e => {
                          const updated = [...props.members];
                          updated[idx] = { ...updated[idx], permissions: { ...updated[idx].permissions, can_archive_task_lists: e.target.checked } };
                          props.setMembers(updated);
                        }}
                        style={{ cursor: lockPerms ? 'not-allowed' : 'pointer', accentColor: '#3b82f6' }}
                      />
                      <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>Can Archive Task Lists</span>
                      <TeamNotificationTooltip placement="top" role="archiveTaskLists" wrapperStyle={{ lineHeight: 1 }}>
                        <span style={{ color: '#94a3b8', fontSize: '12px', cursor: 'help' }}>ℹ</span>
                      </TeamNotificationTooltip>
                    </label>
                  )}
                  {idx === 0 && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: lockPerms ? 'default' : 'pointer', marginTop: '6px' }}>
                      <input
                        type="checkbox"
                        checked={(member.permissions?.can_manage_teams ?? member.can_manage_teams) === true}
                        disabled={lockPerms}
                        onChange={lockPerms ? undefined : e => {
                          const updated = [...props.members];
                          updated[idx] = { ...updated[idx], permissions: { ...updated[idx].permissions, can_manage_teams: e.target.checked } };
                          props.setMembers(updated);
                        }}
                        style={{ cursor: lockPerms ? 'not-allowed' : 'pointer', accentColor: '#7c3aed' }}
                      />
                      <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>Team Management</span>
                      <TeamNotificationTooltip placement="top" role="manageTeams" wrapperStyle={{ lineHeight: 1 }}>
                        <span style={{ color: '#94a3b8', fontSize: '12px', cursor: 'help' }}>ℹ</span>
                      </TeamNotificationTooltip>
                    </label>
                  )}
                  {idx === 0 && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: lockPerms ? 'default' : 'pointer', marginTop: '6px' }}>
                      <input
                        type="checkbox"
                        checked={member.permissions?.can_view_unpublished_changes !== false}
                        disabled={lockPerms}
                        onChange={lockPerms ? undefined : e => {
                          const updated = [...props.members];
                          updated[idx] = { ...updated[idx], permissions: { ...updated[idx].permissions, can_view_unpublished_changes: e.target.checked } };
                          props.setMembers(updated);
                        }}
                        style={{ cursor: lockPerms ? 'not-allowed' : 'pointer', accentColor: '#f97316' }}
                      />
                      <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>View Unpublished Changes</span>
                      <TeamNotificationTooltip placement="top" role="viewUnpublished" wrapperStyle={{ lineHeight: 1 }}>
                        <span style={{ color: '#94a3b8', fontSize: '12px', cursor: 'help' }}>ℹ</span>
                      </TeamNotificationTooltip>
                    </label>
                  )}
                </div>
                );
              })()}

              {member.validationError && (
                <div style={{
                  color: '#f44336',
                  fontSize: '14px',
                  margin: '10px 0',
                  padding: '8px',
                  backgroundColor: '#ffebee',
                  borderRadius: '4px'
                }}>
                  {member.validationError}
                </div>
              )}
              
              {member.successMessage && (
                <div style={{ 
                  color: '#4CAF50', 
                  fontSize: '14px', 
                  margin: '10px 0', 
                  padding: '8px', 
                  backgroundColor: '#E8F5E9',
                  borderRadius: '4px'
                }}>
                  {member.successMessage}
                  <div style={{ fontSize: '12px', marginTop: '5px' }}>
                    Just email your documents to{' '}
                    <a href="mailto:agent@smartclose.app">
                      agent@smartclose.app
                    </a>
                    , and we'll create tasks in your calendar.
                  </div>
                </div>
              )}
              
              {/* Authorization buttons removed for individual/solo members */}
              </div>{/* end suspended overlay wrapper */}
              </div>
            </div>
          ))}
        </div>

        {/* Add Member section for Solo accounts */}
        {(props.accountType === "solo" || props.accountType === "Solo" || props.accountType === "trial" || props.accountType === "Trial") && (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-end', height: '100%' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Button3D
                className="card-action-button add-member-button"
                style={{ 
                  width: 100,
                  fontSize: 12,
                  padding: '6px 12px',
                  marginTop: 0,
                  whiteSpace: 'nowrap',
                  opacity: props.members.length >= effectiveSeatLimit ? 0.5 : 1,
                  cursor: props.members.length >= effectiveSeatLimit ? 'not-allowed' : 'pointer'
                }}
                onClick={() => {
                  if (props.members.length < effectiveSeatLimit) {
                    props.setMembers([
                      ...props.members, 
                      {
                        email: "",
                        originalEmail: null,
                        isNew: true,
                        phone: "",
                        displayPhone: "",
                        provider: "",
                        appleId: "",
                        appPassword: "",
                        task_due_email_reminder: true,
                        personal_calendar_sync: true,
                        permissions: { can_delete_task_lists: false, can_manage_teams: false, can_archive_task_lists: false, can_view_unpublished_changes: false, can_edit_connection: false, can_manage_members: false, has_control: false },
                      }
                    ])
                  }
                }}
                disabled={props.members.length >= effectiveSeatLimit}
              >
                Add Member
              </Button3D>
            </div>
            {props.members.length >= effectiveSeatLimit && (
              <div style={{ 
                fontSize: 12, 
                color: '#666', 
                marginTop: 5, 
                textAlign: 'right',
                maxWidth: 160,
                whiteSpace: 'nowrap'
              }}>
                Maximum {effectiveSeatLimit} members reached
                {renderNeedMoreText()}
              </div>
            )}
            {(() => {
              if (availability.loading) {
                return (
                  <div style={{ marginTop: 5, color: '#9ca3af', fontSize: '11px', textAlign: 'right' }}>
                    Checking availability...
                  </div>
                );
              }
              const dSeats = availability.seatsAvailable !== null ? Math.max(0, availability.seatsAvailable - getDraftSeatAdjustment()) : null;
              const dPhone = availability.phoneAvailable !== null ? Math.max(0, availability.phoneAvailable - getDraftPhoneAdjustment()) : null;
              if ((dSeats ?? 0) <= 0 && (!FEATURE_FLAGS.PHONE_FEATURE_ENABLED || (dPhone ?? 0) <= 0)) return null;
              return (
                <div style={{ marginTop: 5, textAlign: 'right' }}>
                  {(dSeats ?? 0) > 0 && (
                    <div style={{ color: '#2E7D32', fontSize: '11px', fontWeight: '500' }}>
                      {dSeats} seat{dSeats === 1 ? '' : 's'} available
                    </div>
                  )}
                  {FEATURE_FLAGS.PHONE_FEATURE_ENABLED && (dPhone ?? 0) > 0 && (
                    <div style={{ color: '#2E7D32', fontSize: '11px', fontWeight: '500', marginTop: '2px' }}>
                      {dPhone} phone slot{dPhone === 1 ? '' : 's'} available
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

  {(props.accountType === "small_team" || props.accountType === "medium_team" || props.accountType === "large_team") && (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-end', height: '100%' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Button3D
                className="card-action-button add-member-button"
                style={{ 
                  width: 110, 
                  fontSize: 13, 
                  padding: '8px 16px',
                  marginTop: 0,
                  whiteSpace: 'nowrap',
                  opacity: props.members.length >= effectiveSeatLimit ? 0.5 : 1,
                  cursor: props.members.length >= effectiveSeatLimit ? 'not-allowed' : 'pointer'
                }}
                onClick={() => {
                  if (props.members.length < effectiveSeatLimit) {
                    props.setMembers([
                      ...props.members, 
                      {
                        email: "",
                        originalEmail: null,
                        isNew: true,
                        phone: "",
                        displayPhone: "",
                        provider: "",
                        appleId: "",
                        appPassword: "",
                        task_due_email_reminder: true,
                        personal_calendar_sync: true,
                        permissions: { can_delete_task_lists: false, can_manage_teams: false, can_archive_task_lists: false, can_view_unpublished_changes: false, can_edit_connection: false, can_manage_members: false, has_control: false },
                      }
                    ])
                  }
                }}
                disabled={props.members.length >= effectiveSeatLimit}
              >
                Add Member
              </Button3D>
            </div>
            {props.members.length >= effectiveSeatLimit && (
              <div style={{ 
                fontSize: 12, 
                color: '#666', 
                marginTop: 5, 
                textAlign: 'right',
                maxWidth: 160,
                whiteSpace: 'nowrap'
              }}>
                Maximum {effectiveSeatLimit} members reached
                {renderNeedMoreText()}
              </div>
            )}
            {(() => {
              if (availability.loading) {
                return (
                  <div style={{ marginTop: 5, color: '#9ca3af', fontSize: '11px', textAlign: 'right' }}>
                    Checking availability...
                  </div>
                );
              }
              const dSeats = availability.seatsAvailable !== null ? Math.max(0, availability.seatsAvailable - getDraftSeatAdjustment()) : null;
              const dPhone = availability.phoneAvailable !== null ? Math.max(0, availability.phoneAvailable - getDraftPhoneAdjustment()) : null;
              if ((dSeats ?? 0) <= 0 && (!FEATURE_FLAGS.PHONE_FEATURE_ENABLED || (dPhone ?? 0) <= 0)) return null;
              return (
                <div style={{ marginTop: 5, textAlign: 'right' }}>
                  {(dSeats ?? 0) > 0 && (
                    <div style={{ color: '#2E7D32', fontSize: '11px', fontWeight: '500' }}>
                      {dSeats} seat{dSeats === 1 ? '' : 's'} available
                    </div>
                  )}
                  {FEATURE_FLAGS.PHONE_FEATURE_ENABLED && (dPhone ?? 0) > 0 && (
                    <div style={{ color: '#2E7D32', fontSize: '11px', fontWeight: '500', marginTop: '2px' }}>
                      {dPhone} phone slot{dPhone === 1 ? '' : 's'} available
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {props.accountType === "solo_assistant" && (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-end', height: '100%' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Button3D
                className="card-action-button add-member-button"
                style={{ 
                  width: 110, 
                  fontSize: 13, 
                  padding: '8px 16px',
                  marginTop: 0,
                  whiteSpace: 'nowrap',
                  opacity: props.members.length >= effectiveSeatLimit ? 0.5 : 1,
                  cursor: props.members.length >= effectiveSeatLimit ? 'not-allowed' : 'pointer'
                }}
                onClick={() => {
                  if (props.members.length < effectiveSeatLimit) {
                    props.setMembers([
                      ...props.members, 
                      {
                        email: "",
                        originalEmail: null,
                        isNew: true,
                        phone: "",
                        displayPhone: "",
                        provider: "",
                        appleId: "",
                        appPassword: "",
                        task_due_email_reminder: true,
                        personal_calendar_sync: true,
                        permissions: { can_delete_task_lists: false, can_manage_teams: false, can_archive_task_lists: false, can_view_unpublished_changes: false, can_edit_connection: false, can_manage_members: false, has_control: false },
                      }
                    ])
                  }
                }}
                disabled={props.members.length >= effectiveSeatLimit}
              >
                Add Member
              </Button3D>
            </div>
            {props.members.length >= effectiveSeatLimit && (
              <div style={{ 
                fontSize: 12, 
                color: '#666', 
                marginTop: 5, 
                textAlign: 'right',
                maxWidth: 160,
                whiteSpace: 'nowrap'
              }}>
                Maximum {effectiveSeatLimit} members reached
                {renderNeedMoreText()}
              </div>
            )}
            {(() => {
              if (availability.loading) {
                return (
                  <div style={{ marginTop: 5, color: '#9ca3af', fontSize: '11px', textAlign: 'right' }}>
                    Checking availability...
                  </div>
                );
              }
              const dSeats = availability.seatsAvailable !== null ? Math.max(0, availability.seatsAvailable - getDraftSeatAdjustment()) : null;
              const dPhone = availability.phoneAvailable !== null ? Math.max(0, availability.phoneAvailable - getDraftPhoneAdjustment()) : null;
              if ((dSeats ?? 0) <= 0 && (!FEATURE_FLAGS.PHONE_FEATURE_ENABLED || (dPhone ?? 0) <= 0)) return null;
              return (
                <div style={{ marginTop: 5, textAlign: 'right' }}>
                  {(dSeats ?? 0) > 0 && (
                    <div style={{ color: '#2E7D32', fontSize: '11px', fontWeight: '500' }}>
                      {dSeats} seat{dSeats === 1 ? '' : 's'} available
                    </div>
                  )}
                  {FEATURE_FLAGS.PHONE_FEATURE_ENABLED && (dPhone ?? 0) > 0 && (
                    <div style={{ color: '#2E7D32', fontSize: '11px', fontWeight: '500', marginTop: '2px' }}>
                      {dPhone} phone slot{dPhone === 1 ? '' : 's'} available
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
        {props.authText && (
          <textarea readOnly style={styles.output} value={props.authText} />
        )}
        {props.message && !showCalendarTypeSelection && (
          <div style={styles.message}>{props.message}</div>
        )}
        {props.authCode && <p style={styles.success}>✅ Authorization Code Received!</p>}
        
        {/* Footer with Save All and Cancel buttons */}
        {(props.isEditMode || props.accountType) && (
          <>
            <style>
              {`
                .hover-cancel-btn:hover {
                  background: linear-gradient(90deg, #4b5563 0%, #6b7280 40%, #4b5563 100%) !important;
                  transform: translateY(-1px) !important;
                  box-shadow: 0 4px 8px rgba(0,0,0,0.15) !important;
                }
                .hover-save-btn:hover:not(:disabled) {
                  background: linear-gradient(90deg, #4b5563 0%, #6b7280 40%, #4b5563 100%) !important;
                  transform: translateY(-1px) !important;
                  box-shadow: 0 4px 8px rgba(0,0,0,0.15) !important;
                }
                .hover-cancel-btn, .hover-save-btn {
                  transition: all 0.2s ease-in-out !important;
                }
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              marginTop: '16px',
              paddingTop: '12px',
              borderTop: '1px solid #e2e8f0'
            }}>
              <Button3D
                className="hover-cancel-btn"
                style={{
                  padding: '8px 20px',
                  fontSize: '14px',
                  background: 'linear-gradient(90deg, #6b7280 0%, #9ca3af 40%, #6b7280 100%)',
                  color: 'white',
                  minWidth: '90px',
                  border: 'none',
                  borderRadius: '6px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease-in-out'
                }}
                onClick={props.onCancel || props.onClose}
              >
                Cancel
              </Button3D>
              <TeamNotificationTooltip placement="top" role="saveAction" wrapperStyle={{ display: 'inline-block' }}>
              <Button3D
                className="hover-save-btn"
                style={{
                  padding: '8px 20px',
                  fontSize: '14px',
                  opacity: (() => {
                    if (props.isEditMode) {
                      return (hasAnyChanges() && areAllInputsValid()) ? 1 : 0.5;
                    } else {
                      return areAllInputsValid() ? 1 : 0.5;
                    }
                  })(),
                  cursor: (() => {
                    if (props.isEditMode) {
                      return (hasAnyChanges() && areAllInputsValid()) ? 'pointer' : 'not-allowed';
                    } else {
                      return areAllInputsValid() ? 'pointer' : 'not-allowed';
                    }
                  })(),
                  background: (() => {
                    const isEnabled = props.isEditMode ?
                      (hasAnyChanges() && areAllInputsValid()) :
                      areAllInputsValid();
                    return isEnabled
                      ? 'linear-gradient(90deg, #6b7280 0%, #9ca3af 40%, #6b7280 100%)'
                      : 'linear-gradient(90deg, #9ca3af 0%, #d1d5db 40%, #9ca3af 100%)';
                  })(),
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease-in-out'
                }}
                onClick={() => {
                  console.log('[DEBUG] *** MODAL SAVE BUTTON CLICKED ***');
                  console.log('[DEBUG] About to call saveAllMembers...');
                  saveAllMembers();
                }}
                onMouseDown={() => {
                  console.log('[DEBUG] *** SAVE BUTTON MOUSE DOWN ***');
                }}
                onMouseUp={() => {
                  console.log('[DEBUG] *** SAVE BUTTON MOUSE UP ***');
                }}
                disabled={(() => {
                  if (props.isEditMode) {
                    if (!isOriginalDataReady) return true;
                    return !(hasAnyChanges() && areAllInputsValid());
                  }
                  return !areAllInputsValid();
                })()}
              >
                {props.loading ? (
                  <>
                    <span style={{
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginRight: '8px'
                    }}></span>
                    Saving...
                  </>
                ) : 'Save & Send Invites'}
              </Button3D>
              </TeamNotificationTooltip>
            </div>
          </>
        )}
      </div>
      
      {/* Seats Modal */}
      <Seats
        show={showSeatsModal}
        onClose={handleCloseSeatsModal}
        onSeatsUpdate={handleSeatsUpdate}
      />

      {/* Remove member modal */}
      {confirmRemoveMember && !transferMemberModalOpen && (
        <>
          <div className="modal-backdrop show" style={{ display: 'block' }} />
          <div className="modal show" style={{ display: 'block' }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '440px', width: '90%' }}>
              <div className="modal-content">
                <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
                  <h5 className="modal-title" style={{ color: 'white', fontWeight: 600 }}>
                    {removeMemberStep === 'need_transfer' ? 'Transfer Required Before Removal'
                      : removeMemberStep === 'blocked'   ? 'Cannot Remove Member'
                      : removeMemberStep === 'checking'  ? 'Checking…'
                      : 'Remove Member?'}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => { if (!isRemovingMember && removeMemberStep !== 'checking') setConfirmRemoveMember(null); }} aria-label="Close" style={{ filter: 'invert(1)' }} />
                </div>
                <div className="modal-body" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif', padding: '24px' }}>
                  {(removeMemberStep === 'checking' || removeMemberStep === 'executing') && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b', fontSize: '14px' }}>
                      <span style={{ display: 'inline-block', width: 18, height: 18, border: '2px solid #e5e7eb', borderTopColor: '#dc2626', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span>
                      {removeMemberStep === 'checking' ? 'Checking member status…' : 'Removing member…'}
                    </div>
                  )}
                  {removeMemberStep === 'ready' && removeMemberCheckResult && (
                    <p style={{ margin: 0, fontSize: '14px', color: '#334155' }}>
                      {removeMemberCheckResult.delete_scope === 'team_scoped_delete_only'
                        ? 'This member has a paid or trial account, or belongs to other workspaces. Removing them will only remove them from this team — their account and other workspace data will not be deleted.'
                        : 'This member has no active task lists. Removing them will permanently remove their access and records from this workspace.'}
                    </p>
                  )}
                  {removeMemberStep === 'need_transfer' && (
                    <>
                      <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#334155' }}>
                        This member has active task lists. Transfer them to another team member before removing.
                      </p>
                      {removeMemberCheckResult && (
                        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', display: 'flex', gap: '20px' }}>
                          <span><span style={{ fontWeight: 600, color: '#92400e' }}>Active task lists: </span><span style={{ color: '#78350f' }}>{removeMemberCheckResult.active_task_list_count}</span></span>
                          {removeMemberCheckResult.affected_team_count != null && (
                            <span><span style={{ fontWeight: 600, color: '#92400e' }}>Teams affected: </span><span style={{ color: '#78350f' }}>{removeMemberCheckResult.affected_team_count}</span></span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {removeMemberStep === 'blocked' && (
                    <p style={{ margin: 0, fontSize: '14px', color: '#334155' }}>
                      {removeMemberError || 'This member cannot be removed at this time.'}
                    </p>
                  )}
                  {removeMemberError && removeMemberStep !== 'blocked' && (
                    <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '13px', color: '#dc2626' }}>{removeMemberError}</p>
                  )}
                </div>
                <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
                  {removeMemberStep !== 'checking' && removeMemberStep !== 'executing' && (
                    <Button3D
                      onClick={() => setConfirmRemoveMember(null)}
                      disabled={isRemovingMember}
                      style={{ padding: '8px 20px', background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)', border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', color: '#333', fontWeight: 600, borderRadius: '8px', opacity: isRemovingMember ? 0.6 : 1 }}
                    >{removeMemberStep === 'blocked' ? 'Close' : 'Cancel'}</Button3D>
                  )}
                  {removeMemberStep === 'ready' && (
                    <Button3D
                      onClick={handleRemoveMemberExecute}
                      disabled={isRemovingMember}
                      style={{ padding: '8px 20px', background: 'linear-gradient(135deg, #ef4444, #dc2626)', border: 'none', boxShadow: '0 3px 5px rgba(239,68,68,0.3)', color: 'white', fontWeight: 600, borderRadius: '8px', textShadow: '0 1px 2px rgba(0,0,0,0.3)', opacity: isRemovingMember ? 0.7 : 1 }}
                    >
                      {isRemovingMember ? (
                        <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span>
                      ) : 'Remove Member'}
                    </Button3D>
                  )}
                  {removeMemberStep === 'need_transfer' && (
                    <Button3D
                      onClick={() => openTransferMemberModal()}
                      style={{ padding: '8px 20px', background: 'linear-gradient(135deg, #f97316, #ea580c)', border: 'none', boxShadow: '0 3px 5px rgba(249,115,22,0.3)', color: 'white', fontWeight: 600, borderRadius: '8px', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                    >
                      Transfer &amp; Remove
                    </Button3D>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Transfer member modal */}
      {transferMemberModalOpen && (
        <>
          <div className="modal-backdrop show" style={{ display: 'block' }} />
          <div className="modal show" style={{ display: 'block' }}>
            <div className="modal-dialog modal-dialog-centered" style={{
              maxWidth: transferMemberStep === 'conflict-review'
                ? `min(${transferMemberConflicts.some(c => c.conflictingTasks?.length > 0) ? 900 : 660}px, 92vw)`
                : '560px',
            }}>
              <div className="modal-content">

                {/* Header */}
                <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
                  <h5 className="modal-title" style={{ color: 'white', fontWeight: 600 }}>
                    {transferMemberStep === 'select'              ? 'Transfer Tasks'
                      : transferMemberStep === 'checking'         ? 'Checking for Conflicts…'
                      : transferMemberStep === 'no-records'       ? 'Nothing to Transfer'
                      : transferMemberStep === 'confirm'          ? 'Confirm Transfer'
                      : transferMemberStep === 'conflict-warning' ? 'Review Conflicts Before Transfer'
                      : transferMemberStep === 'conflict-review'  ? 'Review Transfer Conflicts'
                      : 'Confirm Transfer'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => { if (!isTransferringMember && transferMemberStep !== 'checking') { setTransferMemberModalOpen(false); if (suspendMemberAfterTransfer) { setConfirmRemoveMember(null); setSuspendMemberAfterTransfer(false); } else if (transferMemberDirectMode) { setConfirmRemoveMember(null); setTransferMemberDirectMode(false); } } }}
                    aria-label="Close"
                    style={{ filter: 'invert(1)' }}
                    disabled={isTransferringMember || transferMemberStep === 'checking'}
                  />
                </div>

                {/* Body */}
                <div className="modal-body p-4" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif' }}>

                  {/* ── Step: select ── */}
                  {transferMemberStep === 'select' && confirmRemoveMember && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f8f9fa', borderRadius: 6, border: '1px solid #e9ecef', marginBottom: 14 }}>
                        <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, minWidth: 80 }}>Transfer from</div>
                        <MemberAvatar name={confirmRemoveMember.member?.name} lastName={confirmRemoveMember.member?.last_name} email={confirmRemoveMember.member?.email} size={30} />
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{confirmRemoveMember.memberName}</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>{confirmRemoveMember.member?.email}</div>
                        </div>
                      </div>

                      <p style={{ margin: '0 0 14px', fontSize: 14, color: '#374151' }}>
                        Choose who should receive this member's team task and calendar records.
                      </p>

                      {transferMemberTargetsLoading && (
                        <div style={{ textAlign: 'center', padding: '20px 0', color: '#6b7280', fontSize: 14 }}>
                          <div style={{ width: 22, height: 22, border: '2px solid #e5e7eb', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 8px' }}></div>
                          Loading eligible users…
                        </div>
                      )}

                      {transferMemberTargetsError && (
                        <div className="alert alert-danger" style={{ fontSize: 13 }}>{transferMemberTargetsError}</div>
                      )}

                      {!transferMemberTargetsLoading && !transferMemberTargetsError && transferMemberTargets.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '20px 0', color: '#6b7280', fontSize: 14 }}>
                          No eligible users found on shared teams.
                        </div>
                      )}

                      {!transferMemberTargetsLoading && transferMemberTargets.map(team => (
                        <div key={team.connectionId} style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, paddingBottom: 4, borderBottom: '1px solid #f3f4f6' }}>
                            {team.teamName}
                          </div>
                          {team.members.map(member => {
                            const isSelected = selectedMemberTransfer?.targetUser?.email === member.email
                              && selectedMemberTransfer?.connectionId === team.connectionId;
                            return (
                              <label
                                key={member.email}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, cursor: 'pointer', border: `1px solid ${isSelected ? '#c4b5fd' : '#e9ecef'}`, background: isSelected ? '#faf5ff' : '#fff', marginBottom: 6, transition: 'all 0.12s' }}
                              >
                                <input type="radio" name="transfer-member-target" checked={isSelected}
                                  onChange={() => setSelectedMemberTransfer({ targetUser: member, connectionId: team.connectionId, teamName: team.teamName })}
                                  style={{ accentColor: '#7c3aed', flexShrink: 0 }}
                                />
                                <MemberAvatar name={member.name} lastName={member.last_name} email={member.email} size={28} />
                                <div style={{ flex: 1, textAlign: 'left' }}>
                                  <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
                                    {[member.name, member.last_name].filter(Boolean).join(' ') || member.email}
                                  </span>
                                  <span style={{ color: '#6b7280', fontSize: 13 }}>{' — '}{member.role_label || 'Member'}</span>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      ))}

                      {transferMemberError && (
                        <div className="alert alert-danger mt-2 mb-0" style={{ fontSize: 13 }}>{transferMemberError}</div>
                      )}
                    </>
                  )}

                  {/* ── Step: checking ── */}
                  {transferMemberStep === 'checking' && (
                    <div style={{ textAlign: 'center', padding: '24px 0' }}>
                      <div style={{ width: 32, height: 32, border: '3px solid #ede9fe', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }}></div>
                      <p style={{ margin: 0, fontSize: 14, color: '#374151' }}>
                        Checking records for{' '}
                        <strong>{selectedMemberTransfer?.targetUser?.name || selectedMemberTransfer?.targetUser?.email}</strong>
                        {' '}on{' '}
                        <strong>{selectedMemberTransfer?.teamName}</strong>…
                      </p>
                    </div>
                  )}

                  {/* ── Step: no-records ── */}
                  {transferMemberStep === 'no-records' && (
                    <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
                      <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#f3f4f6', color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, margin: '0 auto 16px', fontWeight: 300 }}>&#8709;</div>
                      <p style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 600, color: '#111827' }}>Nothing to Transfer</p>
                      <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>
                        This user does not have any active task lists to transfer for this team.
                      </p>
                    </div>
                  )}

                  {/* ── Step: confirm (no conflicts) ── */}
                  {transferMemberStep === 'confirm' && (
                    <>
                      <div style={{ textAlign: 'center', marginBottom: 16 }}>
                        <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, margin: '0 auto' }}>&#10003;</div>
                      </div>
                      {claTransferPendingReturn ? (
                        <>
                          <p style={{ margin: '0 0 8px', fontSize: 14, color: '#374151' }}>
                            <strong>{confirmRemoveMember?.memberName}</strong>'s task lists will be transferred to{' '}
                            <strong>{selectedMemberTransfer?.targetUser?.name || selectedMemberTransfer?.targetUser?.email}</strong>,
                            the new Lead Agent, before the role change is completed.
                          </p>
                          <p style={{ margin: 0, fontSize: 13, color: '#16a34a' }}>No conflicts found. The transfer will proceed cleanly.</p>
                        </>
                      ) : (
                        <p style={{ margin: '0 0 8px', fontSize: 14, color: '#374151' }}>
                          This will transfer tasks from{' '}
                          <strong>{confirmRemoveMember?.memberName}</strong> to{' '}
                          <strong>{selectedMemberTransfer?.targetUser?.name || selectedMemberTransfer?.targetUser?.email}</strong>{' '}
                          for <strong>{selectedMemberTransfer?.teamName}</strong>.
                        </p>
                      )}
                      {transferMemberError && (
                        <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: 13 }}>{transferMemberError}</div>
                      )}
                    </>
                  )}

                  {/* ── Step: conflict-warning ── */}
                  {transferMemberStep === 'conflict-warning' && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', background: '#fff7ed', borderRadius: 8, border: '1px solid #fed7aa', marginBottom: 14 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#ea580c', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, flexShrink: 0, lineHeight: 1 }}>!</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                            <span style={{ fontWeight: 700, fontSize: 14, color: '#9a3412' }}>Conflicts Require Review</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 22, height: 22, padding: '0 7px', background: '#ea580c', color: '#fff', borderRadius: 11, fontSize: 12, fontWeight: 700, lineHeight: 1 }}>
                              {transferMemberConflicts.length}
                            </span>
                          </div>
                          <div style={{ fontSize: 13, color: '#7c2d12', lineHeight: 1.5 }}>
                            {transferMemberConflicts.some(c => c.conflictType === 'task_content_differs')
                              ? 'Both users have changes on the same task list. Please review which version to keep before completing the transfer.'
                              : `The selected user already has records or unpublished changes for ${transferMemberConflicts.length} matching task list${transferMemberConflicts.length !== 1 ? 's' : ''}. Please review how these records should be handled before completing the transfer.`}
                          </div>
                        </div>
                      </div>
                      <p style={{ margin: 0, fontSize: 13, color: '#374151' }}>
                        Click <strong>Review Conflicts</strong> to choose how to handle each overlapping record
                        {claTransferPendingReturn ? '.' : ', or go Back to select a different user.'}
                      </p>
                    </>
                  )}

                  {/* ── Step: conflict-review ── */}
                  {transferMemberStep === 'conflict-review' && (
                    <>
                      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6b7280' }}>
                        Review each conflict below. Defaults are set to the safest option.
                      </p>
                      <div style={{ maxHeight: '65vh', overflowY: 'auto', paddingRight: 2 }}>
                        {transferMemberConflicts.map((conflict, cidx) => (
                          <div key={conflict.conflictKey} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', background: '#fafafa', marginBottom: cidx < transferMemberConflicts.length - 1 ? 12 : 0 }}>
                            {/* Location */}
                            <div style={{ marginBottom: 10 }}>
                              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6d28d9', background: '#faf5ff', padding: '2px 8px', borderRadius: 4, border: '1px solid #ede9fe' }}>
                                {conflict.teamName}
                              </span>
                              {conflict.propertyAddress && (
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginTop: 6 }}>{conflict.propertyAddress}</div>
                              )}
                              {conflict.taskListTitle && (
                                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Task List: {conflict.taskListTitle}</div>
                              )}
                            </div>

                            {/* Conflict description */}
                            <div style={{ fontSize: 13, color: '#92400e', background: '#fffbeb', padding: '6px 10px', borderRadius: 6, border: '1px solid #fde68a', marginBottom: 12 }}>
                              {conflict.description}
                            </div>

                            {/* Task-level diff detail */}
                            {conflict.conflictingTasks?.length > 0 && (
                              <div style={{ marginBottom: 12 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                                  Conflicting Tasks ({conflict.conflictingTasks.length})
                                </div>
                                {conflict.conflictingTasks.map((task, ti) => {
                                  const isMerging = transferMemberConflictResolutions[conflict.conflictKey] === 'merge_tasks';
                                  const taskKey = task.taskId || `task_${ti}`;
                                  const taskChoice = (transferMemberTaskResolutions[conflict.conflictKey] || {})[taskKey];
                                  return (
                                    <div key={taskKey} style={{ border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', marginBottom: 6, overflow: 'hidden' }}>
                                      <div style={{ padding: '5px 10px', background: '#f3f4f6', fontSize: 12, fontWeight: 600, color: '#111827', borderBottom: '1px solid #e5e7eb' }}>
                                        {task.title || `Task ${ti + 1}`}
                                      </div>
                                      <div style={{ padding: '8px 10px' }}>
                                        {task.diffFields.map(field => (
                                          <div key={field} style={{ marginBottom: 6 }}>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                                              {MEMBER_TASK_FIELD_LABELS[field] || field}
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: '4px 8px', fontSize: 11 }}>
                                                <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: 2 }}>{confirmRemoveMember?.memberName}</div>
                                                <div style={{ color: '#374151' }}>{formatMemberTaskFieldValue(task.originalValues[field])}</div>
                                              </div>
                                              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4, padding: '4px 8px', fontSize: 11 }}>
                                                <div style={{ fontWeight: 600, color: '#166534', marginBottom: 2 }}>{selectedMemberTransfer?.targetUser?.name || selectedMemberTransfer?.targetUser?.email}</div>
                                                <div style={{ color: '#374151' }}>{formatMemberTaskFieldValue(task.targetValues[field])}</div>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                        {/* Per-task resolution controls — visible only when "Merge task-by-task" is selected */}
                                        {isMerging && (
                                          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
                                            <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Keep which version of this task?</div>
                                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                              {[
                                                { value: 'keep_original', label: `${confirmRemoveMember?.memberName}'s` },
                                                { value: 'keep_target', label: `${selectedMemberTransfer?.targetUser?.name || selectedMemberTransfer?.targetUser?.email}'s` },
                                                { value: undefined, label: 'Use newer' },
                                              ].map(opt => {
                                                const isTaskChecked = taskChoice === opt.value || (!taskChoice && opt.value === undefined);
                                                return (
                                                  <label key={opt.value ?? 'newer'} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer', padding: '3px 8px', borderRadius: 4, background: isTaskChecked ? '#ede9fe' : '#f3f4f6', border: `1px solid ${isTaskChecked ? '#c4b5fd' : '#e5e7eb'}`, color: isTaskChecked ? '#5b21b6' : '#374151', fontWeight: isTaskChecked ? 600 : 400 }}>
                                                    <input
                                                      type="radio"
                                                      name={`task_res_${conflict.conflictKey}_${taskKey}`}
                                                      checked={isTaskChecked}
                                                      onChange={() => setTransferMemberTaskResolutions(prev => ({ ...prev, [conflict.conflictKey]: { ...(prev[conflict.conflictKey] || {}), [taskKey]: opt.value } }))}
                                                      style={{ accentColor: '#7c3aed', flexShrink: 0 }}
                                                    />
                                                    {opt.label}
                                                  </label>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Resolution choices */}
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>How should this be handled?</div>
                            {[
                              { value: 'keep_target', label: `Keep ${selectedMemberTransfer?.targetUser?.name || selectedMemberTransfer?.targetUser?.email}'s version`, sub: "Target user's current work is preserved", recommended: conflict.requiresReview },
                              { value: 'keep_original', label: `Keep ${confirmRemoveMember?.memberName}'s version`, sub: "Overwrites the target user's current record" },
                              { value: 'keep_newer', label: 'Keep the newer version', sub: 'Based on last update timestamp' },
                              ...(conflict.conflictingTasks?.length > 0 ? [{ value: 'merge_tasks', label: 'Merge task-by-task', sub: 'Choose which version to keep for each conflicting task' }] : []),
                              { value: 'skip', label: 'Skip this item', sub: 'Leave both records unchanged' },
                            ].map(opt => {
                              const isChecked = transferMemberConflictResolutions[conflict.conflictKey] === opt.value;
                              return (
                                <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 8px', borderRadius: 5, cursor: 'pointer', background: isChecked ? '#faf5ff' : 'transparent', border: `1px solid ${isChecked ? '#c4b5fd' : 'transparent'}`, marginBottom: 4 }}>
                                  <input
                                    type="radio"
                                    name={`member_resolution_${conflict.conflictKey}`}
                                    checked={isChecked}
                                    onChange={() => setTransferMemberConflictResolutions(prev => ({ ...prev, [conflict.conflictKey]: opt.value }))}
                                    style={{ accentColor: '#7c3aed', marginTop: 3, flexShrink: 0 }}
                                  />
                                  <div>
                                    <div style={{ fontSize: 13, fontWeight: isChecked ? 600 : 400, color: '#111827' }}>
                                      {opt.label}
                                      {opt.recommended && (
                                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#7c3aed', background: '#ede9fe', padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Recommended</span>
                                      )}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{opt.sub}</div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* ── Step: final-confirm ── */}
                  {transferMemberStep === 'final-confirm' && (
                    <>
                      <div style={{ textAlign: 'center', marginBottom: 16 }}>
                        <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#ede9fe', color: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, margin: '0 auto' }}>&#8594;</div>
                      </div>
                      {claTransferPendingReturn ? (
                        <p style={{ margin: '0 0 8px', fontSize: 14, color: '#374151' }}>
                          <strong>{confirmRemoveMember?.memberName}</strong>'s task lists will be transferred to{' '}
                          <strong>{selectedMemberTransfer?.targetUser?.name || selectedMemberTransfer?.targetUser?.email}</strong>{' '}
                          before they become the new Lead Agent. Conflict choices will be applied as reviewed.
                        </p>
                      ) : (
                        <>
                          <p style={{ margin: '0 0 8px', fontSize: 14, color: '#374151' }}>
                            This will transfer selected team records from{' '}
                            <strong>{confirmRemoveMember?.memberName}</strong> to{' '}
                            <strong>{selectedMemberTransfer?.targetUser?.name || selectedMemberTransfer?.targetUser?.email}</strong>{' '}
                            for <strong>{selectedMemberTransfer?.teamName}</strong>.
                            Conflict choices will be applied as reviewed.
                          </p>
                          {!transferMemberDirectMode && (
                            <p style={{ margin: '0 0 8px', fontSize: 13, color: '#92400e', background: '#fffbeb', padding: '6px 10px', borderRadius: 6, border: '1px solid #fde68a' }}>
                              After transfer, <strong>{confirmRemoveMember?.memberName}</strong> will be {suspendMemberAfterTransfer ? 'suspended' : 'removed from this connection'}.
                            </p>
                          )}
                        </>
                      )}
                      {transferMemberConflicts.length > 0 && (
                        <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                          {transferMemberConflicts.length} conflict{transferMemberConflicts.length !== 1 ? 's' : ''} will be resolved using your selected options.
                        </p>
                      )}
                      {transferMemberError && (
                        <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: 13 }}>{transferMemberError}</div>
                      )}
                    </>
                  )}
                </div>

                {/* Footer */}
                <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
                  {transferMemberStep === 'select' && (
                    <>
                      <Button3D onClick={() => { setTransferMemberModalOpen(false); if (suspendMemberAfterTransfer) { setConfirmRemoveMember(null); setSuspendMemberAfterTransfer(false); } else if (transferMemberDirectMode) { setConfirmRemoveMember(null); setTransferMemberDirectMode(false); } }} style={{ padding: '8px 20px', background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)', border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', color: '#333', fontWeight: 600, borderRadius: '8px' }}>
                        Cancel
                      </Button3D>
                      <Button3D onClick={handleTransferMemberContinue} disabled={!selectedMemberTransfer || transferMemberTargetsLoading} style={{ padding: '8px 20px', background: '#7c3aed', border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', color: '#fff', fontWeight: 600, borderRadius: '8px', opacity: (!selectedMemberTransfer || transferMemberTargetsLoading) ? 0.5 : 1 }}>
                        Continue
                      </Button3D>
                    </>
                  )}
                  {transferMemberStep === 'checking' && (
                    <Button3D disabled style={{ padding: '8px 20px', background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)', border: 'none', color: '#333', fontWeight: 600, borderRadius: '8px', opacity: 0.6 }}>
                      Cancel
                    </Button3D>
                  )}
                  {transferMemberStep === 'no-records' && (
                    <Button3D onClick={() => { setTransferMemberModalOpen(false); if (suspendMemberAfterTransfer) { setConfirmRemoveMember(null); setSuspendMemberAfterTransfer(false); } else if (transferMemberDirectMode) { setConfirmRemoveMember(null); setTransferMemberDirectMode(false); } }} style={{ padding: '8px 20px', background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)', border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', color: '#333', fontWeight: 600, borderRadius: '8px' }}>
                      Close
                    </Button3D>
                  )}
                  {transferMemberStep === 'confirm' && (
                    <>
                      {!claTransferPendingReturn && (
                        <Button3D onClick={() => setTransferMemberStep('select')} disabled={isTransferringMember} style={{ padding: '8px 20px', background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)', border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', color: '#333', fontWeight: 600, borderRadius: '8px', opacity: isTransferringMember ? 0.6 : 1 }}>
                          Back
                        </Button3D>
                      )}
                      <Button3D onClick={claTransferPendingReturn && claMode === 'new' ? handleClaNewUserTransferExecute : handleTransferMemberExecute} disabled={isTransferringMember} style={{ padding: '8px 20px', background: '#7c3aed', border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', color: '#fff', fontWeight: 600, borderRadius: '8px', opacity: isTransferringMember ? 0.7 : 1 }}>
                        {isTransferringMember ? 'Transferring…' : 'Confirm Transfer'}
                      </Button3D>
                    </>
                  )}
                  {transferMemberStep === 'conflict-warning' && (
                    <>
                      {!claTransferPendingReturn && (
                        <Button3D onClick={() => setTransferMemberStep('select')} style={{ padding: '8px 20px', background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)', border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', color: '#333', fontWeight: 600, borderRadius: '8px' }}>
                          Back
                        </Button3D>
                      )}
                      <Button3D onClick={() => setTransferMemberStep('conflict-review')} style={{ padding: '8px 20px', background: '#d97706', border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', color: '#fff', fontWeight: 600, borderRadius: '8px' }}>
                        Review Conflicts
                      </Button3D>
                    </>
                  )}
                  {transferMemberStep === 'conflict-review' && (
                    <>
                      <Button3D onClick={() => setTransferMemberStep('conflict-warning')} style={{ padding: '8px 20px', background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)', border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', color: '#333', fontWeight: 600, borderRadius: '8px' }}>
                        Back
                      </Button3D>
                      <Button3D onClick={() => setTransferMemberStep('final-confirm')} style={{ padding: '8px 20px', background: '#7c3aed', border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', color: '#fff', fontWeight: 600, borderRadius: '8px' }}>
                        Continue
                      </Button3D>
                    </>
                  )}
                  {transferMemberStep === 'final-confirm' && (
                    <>
                      <Button3D onClick={() => setTransferMemberStep(transferMemberConflicts.length > 0 ? 'conflict-review' : 'confirm')} disabled={isTransferringMember} style={{ padding: '8px 20px', background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)', border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', color: '#333', fontWeight: 600, borderRadius: '8px', opacity: isTransferringMember ? 0.6 : 1 }}>
                        Back
                      </Button3D>
                      <Button3D onClick={handleTransferMemberExecute} disabled={isTransferringMember} style={{ padding: '8px 20px', background: '#7c3aed', border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', color: '#fff', fontWeight: 600, borderRadius: '8px', opacity: isTransferringMember ? 0.7 : 1 }}>
                        {isTransferringMember ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span>
                            Transferring…
                          </span>
                        ) : transferMemberDirectMode ? 'Transfer' : 'Transfer & Remove'}
                      </Button3D>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Suspend member modal */}
      {confirmSuspendMember && !transferMemberModalOpen && (
        <>
          <div className="modal-backdrop show" style={{ display: 'block' }} />
          <div className="modal show" style={{ display: 'block' }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '440px', width: '90%' }}>
              <div className="modal-content">
                <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
                  <h5 className="modal-title" style={{ color: 'white', fontWeight: 600 }}>
                    {suspendMemberStep === 'need_transfer' ? 'Transfer Required Before Suspension'
                      : suspendMemberStep === 'blocked' ? 'Cannot Suspend Member'
                      : suspendMemberStep === 'checking' ? 'Checking…'
                      : 'Suspend Member?'}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => { if (!isSuspendingMember && suspendMemberStep !== 'checking') setConfirmSuspendMember(null); }} aria-label="Close" style={{ filter: 'invert(1)' }} />
                </div>
                <div className="modal-body" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif', padding: '24px' }}>
                  {(suspendMemberStep === 'checking' || suspendMemberStep === 'executing') && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b', fontSize: '14px' }}>
                      <span style={{ display: 'inline-block', width: 18, height: 18, border: '2px solid #e5e7eb', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span>
                      {suspendMemberStep === 'checking' ? 'Checking active task lists…' : 'Suspending member…'}
                    </div>
                  )}
                  {suspendMemberStep === 'ready' && (
                    <p style={{ margin: 0, fontSize: '14px', color: '#334155' }}>
                      This member has no active task lists. Suspending them will pause their team access, notifications, reminders, and calendar sync. They can be reactivated later.
                    </p>
                  )}
                  {suspendMemberStep === 'need_transfer' && (
                    <>
                      <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#334155' }}>
                        This member has active task lists. To avoid leaving work assigned to a suspended member, transfer their task lists to another team member before suspension.
                      </p>
                      {suspendMemberCheckResult && (
                        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', display: 'flex', gap: '20px' }}>
                          <span><span style={{ fontWeight: 600, color: '#92400e' }}>Active task lists: </span><span style={{ color: '#78350f' }}>{suspendMemberCheckResult.active_task_list_count}</span></span>
                          {suspendMemberCheckResult.affected_team_count != null && (
                            <span><span style={{ fontWeight: 600, color: '#92400e' }}>Teams affected: </span><span style={{ color: '#78350f' }}>{suspendMemberCheckResult.affected_team_count}</span></span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {suspendMemberStep === 'blocked' && (
                    <>
                      {suspendMemberCheckResult?.lead_agent_teams?.length > 0 ? (
                        <p style={{ margin: 0, fontSize: '14px', color: '#334155' }}>
                          <strong>{confirmSuspendMember.memberName}</strong> is the only Lead Agent on: <strong>{suspendMemberCheckResult.lead_agent_teams.join(', ')}</strong>. Assign another Lead Agent before suspending.
                        </p>
                      ) : (
                        <p style={{ margin: 0, fontSize: '14px', color: '#334155' }}>
                          {suspendMemberError || 'This member cannot be suspended at this time.'}
                        </p>
                      )}
                    </>
                  )}
                  {suspendMemberError && suspendMemberStep !== 'blocked' && (
                    <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '13px', color: '#dc2626' }}>{suspendMemberError}</p>
                  )}
                </div>
                <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
                  {suspendMemberStep !== 'checking' && suspendMemberStep !== 'executing' && (
                    <Button3D
                      onClick={() => setConfirmSuspendMember(null)}
                      disabled={isSuspendingMember}
                      style={{ padding: '8px 20px', background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)', border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', color: '#333', fontWeight: 600, borderRadius: '8px', opacity: isSuspendingMember ? 0.6 : 1 }}
                    >{suspendMemberStep === 'blocked' ? 'Close' : 'Cancel'}</Button3D>
                  )}
                  {suspendMemberStep === 'ready' && (
                    <Button3D
                      onClick={handleSuspendMemberExecute}
                      disabled={isSuspendingMember}
                      style={{ padding: '8px 20px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', boxShadow: '0 3px 5px rgba(245,158,11,0.3)', color: 'white', fontWeight: 600, borderRadius: '8px', textShadow: '0 1px 2px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <FaPause style={{ fontSize: '11px' }} /> Suspend Member
                    </Button3D>
                  )}
                  {suspendMemberStep === 'need_transfer' && (
                    <Button3D
                      onClick={() => { setSuspendMemberAfterTransfer(true); openTransferMemberModal(confirmSuspendMember); }}
                      style={{ padding: '8px 20px', background: 'linear-gradient(135deg, #f97316, #ea580c)', border: 'none', boxShadow: '0 3px 5px rgba(249,115,22,0.3)', color: 'white', fontWeight: 600, borderRadius: '8px', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                    >
                      Transfer &amp; Suspend
                    </Button3D>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Change Lead Agent Modal ── */}
      {claModalOpen && !transferMemberModalOpen && (
        <>
          <div className="modal-backdrop show" style={{ display: 'block' }} />
          <div className="modal show" style={{ display: 'block' }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '520px', width: '92%' }}>
              <div className="modal-content">

                {/* Header */}
                <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
                  <h5 className="modal-title" style={{ color: 'white', fontWeight: 600 }}>
                    {claStep === 'executing' ? 'Applying Changes…' : 'Change Lead Agent'}
                  </h5>
                  <button type="button" className="btn-close" onClick={closeChangeLeadAgentModal} disabled={claExecuting} aria-label="Close" style={{ filter: 'invert(1)' }} />
                </div>

                {/* Step indicator */}
                {claStep !== 'executing' && (
                  <div style={{ display: 'flex', alignItems: 'center', padding: '10px 20px', background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                    {['choose', 'old_action', 'preview'].map((s, i) => {
                      const labels = ['New Lead Agent', 'Current Lead Agent', 'Review & Confirm'];
                      const stepIdx = ['choose', 'old_action', 'preview'].indexOf(claStep);
                      const isDone = i < stepIdx;
                      const isActive = i === stepIdx;
                      return (
                        <React.Fragment key={s}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <div style={{ width: 22, height: 22, borderRadius: '50%', background: isDone ? '#16a34a' : isActive ? '#059669' : '#d1d5db', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                              {isDone ? '✓' : i + 1}
                            </div>
                            <span style={{ fontSize: 12, fontWeight: isActive ? 600 : 400, color: isActive ? '#065f46' : isDone ? '#16a34a' : '#9ca3af', whiteSpace: 'nowrap' }}>{labels[i]}</span>
                          </div>
                          {i < 2 && <div style={{ flex: 1, height: 1, background: isDone ? '#bbf7d0' : '#e5e7eb', margin: '0 8px' }} />}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}

                <div className="modal-body" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif', padding: '24px' }}>

                  {/* Executing spinner */}
                  {claStep === 'executing' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#374151', fontSize: 14 }}>
                      <span style={{ display: 'inline-block', width: 20, height: 20, border: '3px solid #e9ecef', borderTopColor: '#059669', borderRadius: '50%', animation: 'spin 0.9s linear infinite', flexShrink: 0 }} />
                      Applying Lead Agent change…
                    </div>
                  )}

                  {/* Step 1: choose */}
                  {claStep === 'choose' && (
                    <>
                      <p style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: '#111827', textAlign: 'left' }}>Who should be the new Lead Agent?</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div onClick={() => { setClaMode('existing'); setClaError(null); setClaSelectedMember(null); }} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px', border: `1.5px solid ${claMode === 'existing' ? '#059669' : '#e5e7eb'}`, borderRadius: 8, cursor: 'pointer', background: claMode === 'existing' ? '#f0fdf4' : '#fff', transition: 'all 0.15s', textAlign: 'left', userSelect: 'none' }}>
                          <input type="radio" name="cla-mode" value="existing" checked={claMode === 'existing'} onChange={() => { setClaMode('existing'); setClaError(null); setClaSelectedMember(null); }} onClick={e => e.stopPropagation()} style={{ marginTop: 2, accentColor: '#059669', flexShrink: 0 }} />
                          <div style={{ textAlign: 'left' }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>Choose an existing team member</div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Promote a current active member to Lead Agent</div>
                          </div>
                        </div>
                        <div onClick={() => { setClaMode('new'); setClaError(null); setClaSelectedMember(null); }} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px', border: `1.5px solid ${claMode === 'new' ? '#059669' : '#e5e7eb'}`, borderRadius: 8, cursor: 'pointer', background: claMode === 'new' ? '#f0fdf4' : '#fff', transition: 'all 0.15s', textAlign: 'left', userSelect: 'none' }}>
                          <input type="radio" name="cla-mode" value="new" checked={claMode === 'new'} onChange={() => { setClaMode('new'); setClaError(null); setClaSelectedMember(null); }} onClick={e => e.stopPropagation()} style={{ marginTop: 2, accentColor: '#059669', flexShrink: 0 }} />
                          <div style={{ textAlign: 'left' }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>Add a new Lead Agent</div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Invite someone new to join as Lead Agent</div>
                          </div>
                        </div>
                      </div>

                      {claMode === 'existing' && (
                        <div style={{ marginTop: 16, textAlign: 'left' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Select a team member</div>
                          {props.members.slice(1).filter(m => !m.suspended && !m.isNew).length === 0 ? (
                            <div style={{ fontSize: 13, color: '#9ca3af', padding: '12px 0' }}>No eligible active members on this team.</div>
                          ) : (
                            props.members.slice(1).filter(m => !m.suspended && !m.isNew).map(m => {
                              const name = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
                              const isSel = claSelectedMember?.email === m.email;
                              return (
                                <label key={m.email} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: `1px solid ${isSel ? '#6ee7b7' : '#e9ecef'}`, borderRadius: 6, cursor: 'pointer', background: isSel ? '#f0fdf4' : '#fff', marginBottom: 6, transition: 'all 0.12s', textAlign: 'left' }}>
                                  <input type="radio" name="cla-member" checked={isSel} onChange={() => { setClaSelectedMember(m); setClaError(null); }} style={{ accentColor: '#059669', flexShrink: 0 }} />
                                  <MemberAvatar name={m.first_name} lastName={m.last_name} email={m.email} size={28} />
                                  <div style={{ textAlign: 'left' }}>
                                    <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{name}</div>
                                    <div style={{ fontSize: 12, color: '#6b7280' }}>{m.email}</div>
                                  </div>
                                </label>
                              );
                            })
                          )}
                        </div>
                      )}

                      {claMode === 'new' && (
                        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <div style={{ display: 'flex', gap: 10 }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>First Name *</label>
                              <input type="text" placeholder="First name" value={claNewUser.firstName}
                                onChange={e => { setClaNewUser(p => ({ ...p, firstName: e.target.value })); setClaNewUserErrors(p => ({ ...p, firstName: '' })); }}
                                style={{ width: '100%', padding: '7px 10px', fontSize: 13, border: `1px solid ${claNewUserErrors.firstName ? '#fca5a5' : '#d1d5db'}`, borderRadius: 6, outline: 'none', boxSizing: 'border-box' }} />
                              {claNewUserErrors.firstName && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 3 }}>{claNewUserErrors.firstName}</div>}
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Last Name *</label>
                              <input type="text" placeholder="Last name" value={claNewUser.lastName}
                                onChange={e => { setClaNewUser(p => ({ ...p, lastName: e.target.value })); setClaNewUserErrors(p => ({ ...p, lastName: '' })); }}
                                style={{ width: '100%', padding: '7px 10px', fontSize: 13, border: `1px solid ${claNewUserErrors.lastName ? '#fca5a5' : '#d1d5db'}`, borderRadius: 6, outline: 'none', boxSizing: 'border-box' }} />
                              {claNewUserErrors.lastName && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 3 }}>{claNewUserErrors.lastName}</div>}
                            </div>
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Email Address *</label>
                            <input type="email" placeholder="email@example.com" value={claNewUser.email}
                              onChange={e => { setClaNewUser(p => ({ ...p, email: e.target.value })); setClaNewUserErrors(p => ({ ...p, email: '' })); }}
                              style={{ width: '100%', padding: '7px 10px', fontSize: 13, border: `1px solid ${claNewUserErrors.email ? '#fca5a5' : '#d1d5db'}`, borderRadius: 6, outline: 'none', boxSizing: 'border-box' }} />
                            {claNewUserErrors.email && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 3 }}>{claNewUserErrors.email}</div>}
                          </div>
                        </div>
                      )}

                      {claError && <div style={{ marginTop: 12, fontSize: 13, color: '#dc2626' }}>{claError}</div>}
                    </>
                  )}

                  {/* Step 2: old_action */}
                  {claStep === 'old_action' && (() => {
                    const oldName = [props.members[0]?.first_name, props.members[0]?.last_name].filter(Boolean).join(' ') || props.members[0]?.email || 'the current Lead Agent';
                    return (
                      <>
                        <p style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: '#111827', textAlign: 'left' }}>What should happen to {oldName}?</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px', border: `1.5px solid ${claOldAction === 'keep' ? '#059669' : '#e5e7eb'}`, borderRadius: 8, cursor: 'pointer', background: claOldAction === 'keep' ? '#f0fdf4' : '#fff', transition: 'all 0.15s', textAlign: 'left' }}>
                            <input type="radio" name="cla-old-action" checked={claOldAction === 'keep'} onChange={() => setClaOldAction('keep')} style={{ marginTop: 2, accentColor: '#059669', flexShrink: 0 }} />
                            <div style={{ textAlign: 'left' }}>
                              <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>Keep as Member</div>
                              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>They stay on the team with standard member access and permissions</div>
                            </div>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px', border: `1.5px solid ${claOldAction === 'remove' ? '#fca5a5' : '#e5e7eb'}`, borderRadius: 8, cursor: 'pointer', background: claOldAction === 'remove' ? '#fff5f5' : '#fff', transition: 'all 0.15s', textAlign: 'left' }}>
                            <input type="radio" name="cla-old-action" checked={claOldAction === 'remove'} onChange={() => setClaOldAction('remove')} style={{ marginTop: 2, accentColor: '#dc2626', flexShrink: 0 }} />
                            <div style={{ textAlign: 'left' }}>
                              <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>Remove from this team</div>
                              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>They are removed from the team. Active task lists must be transferred first if any exist.</div>
                            </div>
                          </label>
                        </div>
                        {claError && <div style={{ marginTop: 12, fontSize: 13, color: '#dc2626' }}>{claError}</div>}
                      </>
                    );
                  })()}

                  {/* Step 3: preview / confirm */}
                  {claStep === 'preview' && (
                    <>
                      {claPreviewLoading ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#6b7280', fontSize: 14 }}>
                          <span style={{ display: 'inline-block', width: 18, height: 18, border: '2px solid #e5e7eb', borderTopColor: '#059669', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                          Loading preview…
                        </div>
                      ) : claPreview ? (
                        <>
                          {/* Summary card */}
                          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', marginBottom: 14 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Change Summary</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                              <div style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                                <span style={{ color: '#6b7280', minWidth: 140 }}>Current Lead Agent:</span>
                                <span style={{ fontWeight: 600, color: '#374151' }}>{claPreview.old_lead_name}</span>
                              </div>
                              <div style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                                <span style={{ color: '#6b7280', minWidth: 140 }}>New Lead Agent:</span>
                                <span style={{ fontWeight: 600, color: '#374151' }}>
                                  {claMode === 'existing'
                                    ? [claSelectedMember?.first_name, claSelectedMember?.last_name].filter(Boolean).join(' ') || claSelectedMember?.email
                                    : `${claNewUser.firstName} ${claNewUser.lastName}`.trim() || claNewUser.email}
                                </span>
                              </div>
                              <div style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                                <span style={{ color: '#6b7280', minWidth: 140 }}>After change:</span>
                                <span style={{ fontWeight: 600, color: claOldAction === 'remove' ? '#b91c1c' : '#374151' }}>
                                  {claOldAction === 'keep' ? `${claPreview.old_lead_name} stays as Member` : `${claPreview.old_lead_name} removed from team`}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Task list impact */}
                          {claTransferRequired && !claTransferDone ? (
                            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '14px 16px', marginBottom: 14 }}>
                              <div style={{ fontWeight: 600, fontSize: 13, color: '#92400e', marginBottom: 6 }}>Transfer Required Before Change</div>
                              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#78350f' }}>
                                {claPreview.old_lead_name} has <strong>{claPreview.active_task_list_count}</strong> active task {claPreview.active_task_list_count === 1 ? 'list' : 'lists'}. Transfer these to the new Lead Agent before completing the change.
                              </p>
                              <button
                                onClick={openClaTransferModal}
                                style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 13, color: '#fff', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #d97706, #b45309)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #f59e0b, #d97706)'; }}
                              >
                                <FaArrowRight style={{ fontSize: 11 }} /> Transfer & Continue
                              </button>
                            </div>
                          ) : (
                            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', marginBottom: 14, fontSize: 13, color: '#166534' }}>
                              {claTransferDone
                                ? '✓ Task lists transferred successfully.'
                                : 'No active task lists — no transfer needed.'}
                            </div>
                          )}

                          {/* New user invite notice */}
                          {claMode === 'new' && (
                            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '12px 16px', marginBottom: 14, fontSize: 13, color: '#1d4ed8' }}>
                              <strong>{`${claNewUser.firstName} ${claNewUser.lastName}`.trim() || claNewUser.email}</strong> will receive an invitation to set up their account as Lead Agent.
                            </div>
                          )}

                          {claError && <div style={{ fontSize: 13, color: '#dc2626' }}>{claError}</div>}
                        </>
                      ) : (
                        <div style={{ fontSize: 13, color: '#dc2626' }}>{claError || 'Failed to load preview. Please go back and try again.'}</div>
                      )}
                    </>
                  )}

                </div>

                {/* Footer */}
                <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  {claStep !== 'executing' && (
                    <Button3D
                      onClick={claStep === 'choose' ? closeChangeLeadAgentModal : () => setClaStep(claStep === 'old_action' ? 'choose' : 'old_action')}
                      disabled={claExecuting || claPreviewLoading}
                      style={{ padding: '8px 20px', background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)', border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', color: '#333', fontWeight: 600, borderRadius: '8px', opacity: (claExecuting || claPreviewLoading) ? 0.6 : 1 }}
                    >
                      {claStep === 'choose' ? 'Cancel' : 'Back'}
                    </Button3D>
                  )}
                  {claStep === 'choose' && (
                    <Button3D
                      onClick={handleClaContinueFromChoose}
                      disabled={!claMode}
                      style={{ padding: '8px 20px', background: 'linear-gradient(135deg, #059669, #047857)', border: 'none', boxShadow: '0 3px 5px rgba(16,185,129,0.3)', color: '#fff', fontWeight: 600, borderRadius: '8px', opacity: !claMode ? 0.55 : 1 }}
                    >
                      Next
                    </Button3D>
                  )}
                  {claStep === 'old_action' && (
                    <Button3D
                      onClick={handleClaContinueFromOldAction}
                      disabled={claPreviewLoading}
                      style={{ padding: '8px 20px', background: 'linear-gradient(135deg, #059669, #047857)', border: 'none', boxShadow: '0 3px 5px rgba(16,185,129,0.3)', color: '#fff', fontWeight: 600, borderRadius: '8px', opacity: claPreviewLoading ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                      {claPreviewLoading
                        ? <><span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 1s linear infinite' }} /> Loading…</>
                        : 'Next'}
                    </Button3D>
                  )}
                  {claStep === 'preview' && claPreview && !(claTransferRequired && !claTransferDone) && (
                    <Button3D
                      onClick={handleClaExecute}
                      disabled={claExecuting}
                      style={{ padding: '8px 20px', background: 'linear-gradient(135deg, #059669, #047857)', border: 'none', boxShadow: '0 3px 5px rgba(16,185,129,0.3)', color: '#fff', fontWeight: 600, borderRadius: '8px', opacity: claExecuting ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                      {claExecuting
                        ? <><span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 1s linear infinite' }} /> Applying…</>
                        : 'Confirm Change'}
                    </Button3D>
                  )}
                </div>

              </div>
            </div>
          </div>
        </>
      )}

      {/* Reactivate member modal */}
      {confirmReactivateMember && (
        <>
          <div className="modal-backdrop show" style={{ display: 'block' }} />
          <div className="modal show" style={{ display: 'block' }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '440px', width: '90%' }}>
              <div className="modal-content">
                <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
                  <h5 className="modal-title" style={{ color: 'white', fontWeight: 600 }}>Reactivate Member</h5>
                  <button type="button" className="btn-close" onClick={() => { if (!isReactivatingMember) setConfirmReactivateMember(null); }} aria-label="Close" style={{ filter: 'invert(1)' }} />
                </div>
                <div className="modal-body" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif', padding: '24px' }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#334155' }}>
                    Reactivate <strong>{confirmReactivateMember.memberName}</strong>?
                  </p>
                  <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '13px', color: '#064e3b', background: '#ecfdf5', borderRadius: '6px', padding: '8px 12px', border: '1px solid #6ee7b7' }}>
                    Their account will be restored and they will regain access to all connections on this team.
                  </p>
                  {reactivateMemberError && (
                    <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '13px', color: '#dc2626' }}>{reactivateMemberError}</p>
                  )}
                </div>
                <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
                  <Button3D
                    onClick={() => setConfirmReactivateMember(null)}
                    disabled={isReactivatingMember}
                    style={{ padding: '8px 20px', background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)', border: 'none', boxShadow: '0 3px 5px rgba(0,0,0,0.2)', color: '#333', fontWeight: 600, borderRadius: '8px', opacity: isReactivatingMember ? 0.6 : 1 }}
                  >Cancel</Button3D>
                  <Button3D
                    onClick={handleReactivateMemberExecute}
                    disabled={isReactivatingMember}
                    style={{ padding: '8px 20px', background: 'linear-gradient(135deg, #22c55e, #16a34a)', border: 'none', boxShadow: '0 3px 5px rgba(34,197,94,0.3)', color: 'white', fontWeight: 600, borderRadius: '8px', textShadow: '0 1px 2px rgba(0,0,0,0.3)', opacity: isReactivatingMember ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    {isReactivatingMember ? (
                      <><span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span> Reactivating…</>
                    ) : (
                      <><FaPlay style={{ fontSize: '11px' }} /> Reactivate</>
                    )}
                  </Button3D>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
});

export default Auth;
